import type React from 'react';
import Immutable from 'immutable';
import { debounce } from 'lodash';
import isEqual from 'lodash/isEqual';
import type {
    AspectRatio,
    Asset,
    AssetGroup,
    AudioSourceData,
    MediaSourceData,
    OpaqueVideoData,
    SceneCategory,
    SceneGroup,
    Slide,
    TemplateColor,
    TimeAwareMediaAsset,
    WatermarkPositions,
} from '@lumen5/luminary';
import Luminary, { SceneAnnotation, TextAlignment, VideoData } from '@lumen5/luminary';
import { context, trace } from '@opentelemetry/api';
import { $dfs } from '@lexical/utils';
import type { SerializedEditorState } from 'lexical';

import { AgentTaskPipeline } from 'js/src/agent/AgentPipeline';
import { Model } from 'js/src/agent/types';
import { generateVideoDescriptionFromVideoData } from 'js/src/agent/video-state';
import type {
    AssetTranscript,
    AudioAssetModel,
    BoundingBox,
    Capabilities,
    FaceDetectionResponse,
    GenerateRecommendedVideoRequest,
    GenerateRecommendedVideoResponse,
    MediaCandidates,
    MediaOptions,
    QueuedTaskResult,
    SceneAttributesResponse,
    SceneDesign,
    SceneRecommendation,
    SlideBackgroundModel,
    TranscriptWord,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AssetTypes,
    LanguageEnum,
    NarrativeThreadEnum,
    SlideBackgroundModelTypeEnum,
    SourceEnum,
    TaskResultMessageTypeEnum,
    TranscriptAssetType,
} from 'js/src/client';
import type { TimingMetadata } from 'js/src/components/app/OverlaySelector';
import { SharedLeftControlHandlers } from 'js/src/components/app/slide-list/SharedLeftControlHandlers';
import { TrimmerSource } from 'js/src/components/common/asset-trimmer/common';
import { Toast } from 'js/src/components/common/Toast';
import {
    advancedMediaControlsAssetId,
    advancedMediaControlsSceneId,
    getSelectedBlueprintId,
    getVideoId,
    getVideoIdOrThrow,
    getVideoLanguage,
    isSceneGroupLoadingSkipped,
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    SCENE_GROUP_LOADING_TYPES,
    setIsStoryboardGenerating,
    setRecommendationsCompleted,
    setSkipMediaOptimization,
    setTextOnMediaGenerating,
    setVideoFeedbackFormState,
    shouldSkipAgentPipeline,
    uploadVideoPosterBlob,
} from 'js/src/data/app';
import { setMultiVideos } from 'js/src/data/app/multi-videos-slice';
import { startAudioEnhancementOperation } from 'js/src/data/audio-enhancement';
import {
    blueprintIdIfItMatchesNarrativeThread,
    getBlueprint,
    getOrFetchBlueprint,
    setCaptionsFromBlueprintOrCookiesOperation,
} from 'js/src/data/blueprints';
import { getColorGradingFilterFromUrl } from 'js/src/data/color-grading/color-grading-selectors';
import { MONO_FILTER_TEXTURE_URL } from 'js/src/data/color-grading/color-grading-slice';
import {
    getSceneColorShuffleData,
    getSceneColorShuffleIndex,
    getTemplateColorShuffleIndex,
    getTemplateColorShufflePalettes,
    setColorShuffleDataForSlide,
    setColorShuffleIndexForSlide,
    setTemplateColorShuffleIndex,
    setTemplateColorShufflePalettes,
} from 'js/src/data/color-shuffle';
import type { FlattenedAsset, ModifiedAssetGenerationData } from 'js/src/data/media';
import {
    convertBackgroundAssetToFlattenedAsset,
    fetchRecommendedSearchTerms,
    getActiveSearchContextForCurrentTab,
    getActiveSearchTerm,
    getGenerationFromMediaUuid,
    getIsSimilarMediaResult,
    getSimilarSearchOriginalAssetUuid,
    isAudioSourceData,
    isFlattenedAssetMediaSourceDataCompatible,
    isImmutableAudioAsset,
    refreshRecommendedAssetsAfterLoadingRecommendedVideo,
    regenerateTranscriptForAsset,
    saveAIMedia,
    talkingHeadPlaybackRegionRequiresTrimming,
} from 'js/src/data/media';
import {
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isMediaSourceData,
    RECORDING_FOLDER_NAME,
    SCREEN_RECORDING_FOLDER_NAME,
} from 'js/src/data/media/media-types';
import { selectMusicRecommendationMode } from 'js/src/data/media-recommendation-preferences';
import type { RecordYourScreenModalContext } from 'js/src/data/modals';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { updatePremiumMediaUsage } from 'js/src/data/premium-media';
import { setRecommendedVideoResponse } from 'js/src/data/recommendations';
import {
    clearTextAssetTrackedSlide,
    getFocusedSubsceneIndex,
    setFocusedSceneAndScroll,
    setFocusedSceneInfo,
    trackChangedTextAssetSlide,
    updateFocusedSceneInfoOnDelete,
} from 'js/src/data/scenes';
import type { OverlayBounds } from 'js/src/data/scenes/scenes-types';
import type { SceneDesignId } from 'js/src/data/templates';
import {
    getTemplateBundle,
    getTemplateColorPalettes,
    getTemplateDefaultCaptionSceneDesign,
    getTemplateDefaultOutroSceneDesign,
    getTemplateDefaultSceneDesign,
    getTemplateDefaultStoryboardSceneDesign,
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
    getTemplateSceneDesignsByCategory,
    getTemplateSceneIds,
    isTemplateBundleLoading,
    storeRecentSelectedSceneDesign,
} from 'js/src/data/templates';
import type { InterestPointTime } from 'js/src/data/transcript';
import {
    $getTimeIndicatedBy,
    calculateInterestPointTimesForEntireSceneGroupOperation,
    calculateInterestPointTimesOperation,
    checkpointVideoAndTranscriptOperation,
    convertTranscriptWordsToMillis,
    deleteTranscript,
    duplicateTranscript,
    getEditorFromSceneGroupId,
    getIsOriginalOrMissingTranscript,
    getTranscriptById,
    getTranscriptIds,
    loadNewTranscriptOperation,
    reapplyInterestPointsOperation,
    showTranscriptLanguageWarning,
    theseAreOverlapping,
    updateTranscriptCurrentTimeAndPreview,
} from 'js/src/data/transcript';
import { $isSilenceNode } from 'js/src/data/transcript/nodes/SilenceNode';
import type { TranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import {
    $getFineOverlayBoundaryTimeCloseTo,
    $getFirstTranscriptWordShowing,
    $getNextTranscriptWordNode,
    $getNextWordAtTime,
    $getPreviousTranscriptWordNode,
    $getPreviousWordAtTime,
    $getSilenceNodesAfter,
    $getSilenceNodesBefore,
    $getWordNodesBetweenTimes,
    $isTrimmedOut,
    defragPlaybackRegions,
    getTranscriptWords,
} from 'js/src/data/transcript/transcript-utils';
import { getLanguageReadingSpeed, getSupportedLanguages } from 'js/src/data/translations';
import { getUploadedAssetByUuid, UploadingContext } from 'js/src/data/uploads';
import {
    copyUserOverridesForDuplicatedSceneGroup,
    getAssetsWithTimesOverridden,
    isBackgroundColorUserOverridden,
    isBrightnessUserOverridden,
    isCropUserOverridden,
    isKenburnUserOverridden,
    isSceneDurationUserOverridden,
    isTextAlignmentUserOverridden,
    isTextPositionUserOverridden,
    setBrightnessUserOverride,
    setCropUserOverride,
    setKenburnUserOverride,
    setSceneDurationUserOverride,
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides';
import {
    clearAssetTimingOverrides,
    setAssetTimingOverride,
    setSceneOverrides,
} from 'js/src/data/user-overrides/user-overrides-actions';
import { getSlideOverrides } from 'js/src/data/user-overrides/user-overrides-selectors';
import { checkpointVideo, getVideoData } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import {
    generateBestColorPalettePermutationsFromLuminary,
    getColorPermutations,
    MAX_COLOR_SHUFFLES,
} from 'js/src/libs/color-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    AudioVolume,
    AUTO_CROP,
    COOKIE_KEYS,
    DEFAULT_BLUR,
    DEFAULT_BRIGHTNESS,
    DEFAULT_CONTRAST,
    DEFAULT_ENABLE_GREYSCALE,
    DEFAULT_HUE,
    DEFAULT_LIGHTNESS,
    DEFAULT_NOISE,
    DEFAULT_NOISE_SIZE,
    DEFAULT_PLAYBACK_RATE,
    DEFAULT_SATURATION,
    DEFAULT_VIGNETTING,
    DEFAULT_VIGNETTING_ALPHA,
    DEFAULT_VIGNETTING_BLUR,
    getEnumKeyByEnumValue,
    ImmutableFromJS,
    ImmutableToJS,
    isNotNull,
    LOADING_SCENE_MSG,
    MEDIA_ZOOM,
    SceneDesignSpecialCase,
    WATERMARK_ASSET_NAME,
} from 'js/src/libs/consts';
import { focusAssetElement } from 'js/src/libs/custom-utils';
import { downloadStringAsFile } from 'js/src/libs/download-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import FocusAndScroll from 'js/src/libs/focus-and-scroll';
import PollAsyncTask from 'js/src/libs/poll_async_task';
import type { AppDispatch } from 'js/src/libs/RootState';
import SceneAttributesCache, { SceneAttributesCacheData } from 'js/src/libs/scene-attributes-cache';
import SceneSwapper from 'js/src/libs/scene-swapping/SceneSwapper';
import type { slideAndSceneDesign } from 'js/src/libs/scene-utils';
import {
    AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA,
    buildScenePresentationMaps,
    getLinkedMediaAssets,
    resetCaptions,
    TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA,
    VOICEOVER_PLACEHOLDER_ASSET_SOURCE,
} from 'js/src/libs/scene-utils';
import type { StartEditingNonTextAssetPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import { calculateSceneDuration } from 'js/src/libs/timing-utils';
import uuid from 'js/src/libs/uuid';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    canVoiceDrivenBaseAssetBeOverwritten,
    getBackgroundColorAssetProperties,
    getBaseSceneDesignId,
    getEditableSubslideText,
    getFirstVoiceDrivenSceneGroup,
    getInvisibleAssetNames,
    getNumNonOutroNonBrandSceneGroups,
    getOverlayScenesInSceneGroup,
    getSceneGroupCategory,
    getSceneWithBaseMediaAsset,
    getVoiceDrivenAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    getVoiceDrivenBaseAssetStartTime,
    isAIVoiceoverSceneGroup,
    isOrdinarySceneGroup,
    isSceneAnOverlay,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
    sceneContainsIcon,
    translateTranscriptTimeToSceneGroupTime,
    translateTranscriptTimeToVideoTime,
    translateVideoTimeToSceneGroupTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import {
    calculateWatermarkContrastsForAllOptions,
    calculateWatermarkContrastsForAllOptionsWithLuminary,
    selectBestWatermarkIndexByContrast,
} from 'js/src/luminary/text-contrast-utils';

// Simple module-level agent pipeline tracking for cancellation
let agentPipeline: AgentTaskPipeline | null = null;

export function cancelAgentPipeline(): void {
    if (agentPipeline) {
        agentPipeline.cancel();
        agentPipeline = null;
    }
}

export function setAgentPipeline(pipeline: AgentTaskPipeline | null): void {
    agentPipeline = pipeline;
}
import { createContainCropState, cropToBoundingBox } from 'js/src/libs/crop-utils';
import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { videoAssetIsNotTranscoded } from 'js/src/luminary/background-transcoding-operations';
import {
    equalWithinEpsilon,
    greaterThanOrEqualWithinEpsilon,
    greaterThanWithinEpsilon,
    lessThanOrEqualWithinEpsilon,
    lessThanWithinEpsilon,
} from 'js/src/luminary/comparison-utils';
import {
    addStickerAssetToSceneInternalOperation,
    adjustAssetCropByResolution,
    applyGlobalCaptionStylesToSceneGroupOperation,
    clearSourceData,
    CreateOverlayError,
    createTextOnMediaPlaceholderScenes,
    deleteAssetOperation,
    fetchAndSetRecommendMusic,
    getAdvancedMediaControlsAsset,
    getBackgroundMediaAssetSourceData,
    getLargestMediaAsset,
    getMediaToUpdate,
    getOrCreateStickerCompatibleSceneOperation,
    getVideoMusicVolume,
    isAIAvatarSceneGroup,
    isMediaAssetFullBleed,
    recommendedMediaSourceData,
    RemoveOverlayError,
    saveRawLuminary,
    setMediaCrop,
    setMediaKenburn,
    setMediaTrim,
    setSceneDuration,
    SlideDeleteError,
    TranslateOverlayError,
    updateLuminaryAspectRatio,
    updatePositionAndAnchor,
    waitUntilTemplateBundleLoaded,
} from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import type {
    AddSceneGroupInfo,
    ConfigurableElementProperties,
} from 'js/src/luminary/luminary-types';
import {
    appendSceneRecordingSuffix,
    clearScenes,
    clearScenesExceptCategories,
    findScreenRecordingMediaAssetsInScene,
    floatingPointComparison,
    floatingPointSubtraction,
    getElementPropertiesToSave,
    getTargetIndex,
    isScreenRecordingScene,
    isScreenRecordingSceneGroup,
    isScreenRecordingSourceData,
    isVoiceDrivenCompatibleSceneCategory,
    muteOutroMusicIfHasMediaWithVolume,
    resizeTextToFitOnScene,
    resizeTextToFitOnSceneForEntireVideo,
} from 'js/src/luminary/luminary-utils';
import type { SetAndApplyVoiceoverParams } from 'js/src/luminary/recommendation-utils';
import {
    addScenesFromRecommendations,
    populateSceneFromCreationRecommendations,
    populateSourceSegmentForRephrasedScene,
    setAndApplyVoiceoverAssetForSceneGroup,
    setRecommendedVoiceDrivenVideo,
} from 'js/src/luminary/recommendation-utils';
import {
    getMultipleSpeakersSceneDesignIdsOperation,
    setUpMultipleSpeakersSceneOperation,
} from 'js/src/luminary/swap-scene-operations';
import { updateSceneGroupDurationToEqualVoiceDrivenAssetDuration } from 'js/src/luminary/voicedriven-scenegroup-utils';
import ActivityProfiler from 'js/src/profiling/ActivityProfiler';
import { METRIC_CHANGE_ASPECT_RATIO, METRIC_INITIAL_VIDEO_LOAD } from 'js/src/profiling/metrics';
import { isFiniteNumber } from 'js/src/types';
import Cookies from 'js-cookie';

/**
 * When the Video Creator loads, we get video_data and we supply that to luminary.
 * Luminary loads the video.
 */
export const initializeLuminaryOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState, call }) => {
        // track how long it takes for all the scene design previews to be updated
        // when first loading the video
        const activityId = uuid();
        ActivityProfiler.start(activityId);
        const videoData = getVideoData(getState());
        if (!videoData) {
            throw new Error('Video data is missing when initializing the VideoCreator');
        }
        if (luminary.destroyed) {
            return;
        }
        luminary.load(videoData);
        if (!isTemplateBundleLoading(getState(), luminary.template.id)) {
            luminary.setTemplate(
                ImmutableToJS(getTemplateBundle(getState(), luminary.template.id)),
            );
        }

        // Make sure that all voicedriven scenegroups are correctly configured
        luminary.sceneGroups.forEach((sceneGroup) => {
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
            }
        });

        // mark the tracking as complete when all scene images have finished rendering
        await luminary.onAllSlideImagesRendered();

        const metricLabels = METRIC_INITIAL_VIDEO_LOAD.getLabels(luminary.scenes.length);
        ActivityProfiler.end(activityId, METRIC_INITIAL_VIDEO_LOAD.name, metricLabels);
    },
);

export const calculateAndSetRecommendedSceneDurationOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneId: string,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        if (isSceneDurationUserOverridden(getState(), sceneId)) {
            return;
        }

        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        // currently we don't support recommended scene durations for voice-driven scenes
        if (isVoiceDrivenSceneGroup(scene.sceneGroup)) {
            return;
        }

        const sceneTexts = scene.subscenes.map(getEditableSubslideText);
        const videoLanguage = getState().app ? getVideoLanguage(getState()) : LanguageEnum.XX; // when using outside the app use unknown language code
        const sceneCategory = getSceneGroupCategory(scene.sceneGroup);
        const paceMultiplier = luminary.paceMultiplier;
        const readingSpeed = getLanguageReadingSpeed(getState(), videoLanguage);
        const sceneDuration = calculateSceneDuration(
            sceneTexts,
            sceneCategory,
            paceMultiplier,
            readingSpeed,
        );
        if (sceneDuration !== scene.duration) {
            setSceneDuration(luminary, sceneId, sceneDuration);
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

/**
 * sets recommended scene attributes on the scene
 */

const setRecommendedSceneAttributesOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState },
        {
            scene,
            scene_attributes,
            systemTriggered = true,
            parentActionGroup = uuid(),
            autoScale = false,
            isolatedLuminary,
        }: {
            scene: Slide;
            scene_attributes: ImmutableMap<SceneAttributesResponse>;
            systemTriggered?: boolean;
            parentActionGroup?: string;
            autoScale?: boolean;
            /** Optional pre-created isolated Luminary for contrast calculations. When provided,
             * avoids creating new WebGL contexts for each scene. Caller is responsible for cleanup. */
            isolatedLuminary?: Luminary;
        },
    ) => {
        // Apply text position first, then calculate contrast with the new position
        luminary.batchRun(() => {
            // update text position
            if (!isTextPositionUserOverridden(getState(), scene.id)) {
                // only apply the text position if it's different to avoid overwriting alignment if set
                const alignmentOverwritten =
                    scene.getActiveTextPreset() === null &&
                    isTextAlignmentUserOverridden(getState(), scene.id);
                if (!alignmentOverwritten) {
                    scene.applyTextPreset(scene_attributes.get('text_pos'));
                }
            }
        });

        // Calculate watermark contrast and select best watermark option
        // Only runs if template has exactly 2 watermarks
        // TODO: Add watermark override check when override tracking system is implemented
        try {
            const watermarkContrastResults = isolatedLuminary
                ? await calculateWatermarkContrastsForAllOptionsWithLuminary(
                      isolatedLuminary,
                      scene.id,
                  )
                : await calculateWatermarkContrastsForAllOptions(luminary, scene.id);

            if (watermarkContrastResults.length > 0) {
                const bestWatermarkIndex =
                    selectBestWatermarkIndexByContrast(watermarkContrastResults);

                if (bestWatermarkIndex !== null) {
                    // Apply the watermark with best contrast to this scene
                    scene.watermark.sourceData =
                        VideoData.templateWatermarkSourcesByIndex(bestWatermarkIndex);
                }
            }
        } catch (error) {
            ErrorReporter.capture(error, {
                extra: {
                    context: 'setRecommendedSceneAttributes_watermarkContrast',
                    sceneId: scene.id,
                },
            });
        }

        luminary.batchRun(() => {
            // update media asset attributes
            scene_attributes.get('media_attributes').forEach((attrs) => {
                // get media asset (if it exists)
                const mediaAsset = scene.getAssetByName(attrs.get('asset_name'));
                if (!mediaAsset || !VideoData.isMediaAsset(mediaAsset)) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `cannot find child: ${attrs.get('asset_name')} so cannot apply recommended attributes`,
                    );
                    return;
                }

                // Check if the fillmode is something other than cover. If so we shouldn't set any of the
                // media attributes for it since they aren't applicable for non-cover'ed assets.
                // TODO: ideally this would be better to do further upstream to avoid duplicate logic here
                if (mediaAsset.fillMode !== VideoData.FILL_COVER) {
                    return;
                }

                const shouldScale = (() => {
                    if (mediaAsset.linkToSceneGroup) {
                        return false;
                    }

                    if (!autoScale) {
                        return false;
                    }

                    if (!isMediaAssetFullBleed(luminary, mediaAsset)) {
                        return false;
                    }

                    return mediaAsset.sourceAspectRatio !== mediaAsset.targetAspectRatio;
                })();
                if (shouldScale && !isCropUserOverridden(getState(), scene.id)) {
                    mediaAsset.setScaleToContainToVisibleArea();
                }

                // update crop
                const x_offset = attrs.get('x_offset');
                const y_offset = attrs.get('y_offset');
                if (
                    !shouldScale &&
                    x_offset !== null &&
                    y_offset !== null &&
                    !isCropUserOverridden(getState(), scene.id)
                ) {
                    setMediaCrop(luminary, scene.id, mediaAsset.id, x_offset, y_offset);
                }

                // update kenburn animation
                if (!isKenburnUserOverridden(getState(), scene.id)) {
                    setMediaKenburn(
                        luminary,
                        scene.id,
                        mediaAsset.id,
                        attrs.get('background_animation'),
                    );
                }

                // update brightness using backend recommendation
                if (!isBrightnessUserOverridden(getState(), scene.id)) {
                    mediaAsset.brightness = attrs.get('brightness');
                }
            });
        });
    },
);

export const fetchAndSetRecommendedSceneAttributesOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneId,
            autoCrop = true,
            recommendBrightness = true,
            systemTriggered = true,
            parentActionGroup = uuid(),
            isGlobalVideo = true,
            autoScale = false,
            isolatedLuminary,
        }: {
            sceneId: string;
            autoCrop?: boolean;
            recommendBrightness?: boolean;
            systemTriggered?: boolean;
            parentActionGroup?: string;
            isGlobalVideo?: boolean;
            autoScale?: boolean;
            /** Optional pre-created isolated Luminary for contrast calculations. */
            isolatedLuminary?: Luminary;
        },
    ) => {
        // get scene
        const scene = luminary.getSceneById(sceneId);
        if (scene === undefined) {
            return;
        }

        // don't recommend any scene attributes if the scene is a screen recording scene
        if (isScreenRecordingSceneGroup(scene.sceneGroup)) {
            return;
        }
        if (isScreenRecordingScene(scene)) {
            return;
        }

        // don't recommend any scene attributes if the scene isn't an "ordinary" scene (eg. scene is branding scene)
        const sceneAttributesAreAllowed =
            isOrdinarySceneGroup(scene.sceneGroup) || isVoiceDrivenSceneGroup(scene.sceneGroup);
        if (!sceneAttributesAreAllowed) {
            return;
        }
        if (isBrightnessUserOverridden(getState(), sceneId)) {
            recommendBrightness = false;
        }

        // fetch & apply recommended scene attributes
        const cacheData = SceneAttributesCacheData.from(
            scene,
            luminary,
            autoCrop,
            recommendBrightness,
        );
        try {
            const sceneAttributesList =
                await SceneAttributesCache.getInstance().getSceneAttributes(cacheData);

            // ensure the scene still has the same asset (it hasn't been changed while we were fetching the recommendations)
            const scene = luminary.getSceneById(sceneId);
            if (scene === undefined) {
                return;
            }

            const currentCacheKey = SceneAttributesCacheData.from(
                scene,
                luminary,
                autoCrop,
                recommendBrightness,
            ).toCacheKey();
            if (cacheData.toCacheKey() !== currentCacheKey) {
                return;
            }

            // get the correct scene_attributes from the list
            // the correct ones have the same scene_design as the scene we are working on.
            const scene_attributes = sceneAttributesList.find((scene_attributes) => {
                return scene_attributes.get('scene_design') === scene.sceneDesign;
            });
            if (!scene_attributes) {
                // there was no matching scene_design
                return;
            }

            call(setRecommendedSceneAttributesOperation, {
                scene,
                scene_attributes,
                systemTriggered,
                parentActionGroup,
                autoScale,
                isolatedLuminary,
            });

            if (!isVoiceDrivenSceneGroup(scene.sceneGroup)) {
                // Set recommended scene duration
                call(
                    calculateAndSetRecommendedSceneDurationOperation,
                    scene.id,
                    systemTriggered,
                    parentActionGroup,
                );
            }

            if (isGlobalVideo) {
                dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(luminary),
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
            }
        } catch (e) {
            ErrorReporter.capture(e);
        }
    },
);

/**
 * Fetches and sets recommended scene attributes for multiple scenes using a single isolated Luminary instance.
 * This is more efficient than calling fetchAndSetRecommendedSceneAttributesOperation for each scene,
 * as it avoids creating/destroying WebGL contexts repeatedly.
 *
 * Use this when processing multiple scenes at once (e.g., after media agent completes).
 */
export const fetchAndSetRecommendedSceneAttributesForMultipleScenesOperation =
    createLuminaryOperation(
        async (
            { luminary, dispatch, getState, call },
            {
                sceneIds,
                autoCrop = true,
                recommendBrightness = true,
                systemTriggered = true,
                parentActionGroup = uuid(),
                autoScale = false,
            }: {
                sceneIds: string[];
                autoCrop?: boolean;
                recommendBrightness?: boolean;
                systemTriggered?: boolean;
                parentActionGroup?: string;
                autoScale?: boolean;
            },
        ) => {
            if (sceneIds.length === 0) {
                return;
            }

            // Process each scene using the existing single-scene operation
            for (const sceneId of sceneIds) {
                await call(fetchAndSetRecommendedSceneAttributesOperation, {
                    sceneId,
                    autoCrop,
                    recommendBrightness,
                    systemTriggered,
                    parentActionGroup,
                    isGlobalVideo: false, // Don't checkpoint per scene
                    autoScale,
                });
            }

            // Checkpoint once at the end
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        },
    );

/*
 * The applyUser* operations are overridable operations triggered by a user's direct actions.
 * Most of these operations have an equivalent operation for changes initiated by the system.
 * These operations coordinate handling the change and also apply any user
 * specific updates (like setting user overrides flag and analytics tracking)
 */

/**
 * @param {string} sceneId
 * @param {number} duration - duration in milliseconds,
 * @param {boolean} [systemTriggered]
 * @param {uuid} [parentActionGroup]
 */
export const applyUserSceneDurationOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneId: string,
        duration: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const assetsWithUserOverridenTimes = getAssetsWithTimesOverridden(getState(), sceneId);
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const previousOutroTimes = new Map<string, number>();
        assetsWithUserOverridenTimes.forEach((assetId) => {
            const asset = luminary.getAssetById(assetId);
            if (!asset) {
                return;
            }
            previousOutroTimes.set(
                assetId,
                asset.container.duration - asset.appliedOutroAnimationOffset,
            );
        });
        luminary.batchRun(() => {
            setSceneDuration(luminary, sceneId, duration);
            assetsWithUserOverridenTimes.forEach((assetId) => {
                const asset = luminary.getAssetById(assetId);
                if (!asset) {
                    return;
                }
                let introTimeOffset = asset.appliedIntroAnimationOffset;
                let outroTimeOffset = asset.appliedOutroAnimationOffset;
                let changed = false;

                const previousOutroTime = previousOutroTimes.get(assetId);
                if (typeof previousOutroTime === 'number' && outroTimeOffset !== 0) {
                    // to the user, it looks like they are setting an exit time in the UI, not an
                    // exit time offset. If the duration of the scene changes, we want to keep
                    // the exit time the same as the previous exit time as much as possible.
                    // But if the offset is set to 0, we interpret that as the user saying "exit at the end of the container",
                    // rather than "exit at exactly time X" - in that case we should just keep the offset at 0.
                    outroTimeOffset = asset.container.duration - previousOutroTime;
                    changed = true;
                }
                if (introTimeOffset >= asset.container.duration) {
                    // if the asset is starting after the container, bump the start time to
                    // start the asset within 500ms of the end of the container
                    const assetMargin =
                        asset.container.duration > 500 ? 500 : asset.container.duration;
                    introTimeOffset = asset.container.duration - assetMargin;
                    changed = true;
                }
                if (outroTimeOffset < 0) {
                    // the asset is exiting after the end of the container. Bump it to
                    // exit at the end of the container
                    outroTimeOffset = 0;
                    changed = true;
                }
                if (asset.container.duration - outroTimeOffset <= introTimeOffset) {
                    // the asset is exiting before it's own start time
                    // Bump the exit time to the end of the container
                    outroTimeOffset = 0;
                    changed = true;
                }
                // assert that we have valid times:
                if (introTimeOffset > asset.container.duration) {
                    throw new Error('Intro time offset is greater than container duration');
                }
                if (introTimeOffset < 0) {
                    throw new Error('Intro time offset is less than 0');
                }
                if (outroTimeOffset < 0) {
                    throw new Error('Outro time offset is less than 0');
                }
                if (outroTimeOffset > asset.container.duration) {
                    throw new Error('Outro time offset is greater than container duration');
                }
                if (asset.container.duration - outroTimeOffset < introTimeOffset) {
                    throw new Error('Outro time cannot be before intro time');
                }
                if (changed) {
                    call(setAssetIntroExitTimesOperation, {
                        timingSpecifications: [
                            {
                                sceneId,
                                assetId,
                                introTime: introTimeOffset,
                                exitTime: asset.container.duration - outroTimeOffset,
                            },
                        ],
                        operatingOnMainLuminary: true,
                        systemTriggered,
                        parentActionGroup,
                    });
                }
            });
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // mark the scene duration as user overridden
        dispatch(
            setSceneDurationUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * @param {string} sceneId
 * @param {string} assetId
 * @param {number} startTime
 * @param {number} stopTime
 * @param {boolean} [systemTriggered]
 * @param {uuid} [parentActionGroup]
 */
export const applyUserMediaTrimOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetName: string,
        startTime: number,
        stopTime: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        setMediaTrim(luminary, sceneId, assetName, startTime, stopTime);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // mark the scene duration as user overridden
        dispatch(
            setSceneDurationUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const applyUserMediaKenburnOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        animation: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const previousBackgroundAnimation = setMediaKenburn(luminary, sceneId, assetId, animation);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // mark the background animation as user overridden
        dispatch(
            setKenburnUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );

        // analytics
        analytics.track('background animation changed', {
            new_animation: animation,
            old_animation: previousBackgroundAnimation,
            sceneId: sceneId,
        });
    },
);

export const applyDesignToSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        targetSceneId: string,
        selectedScene: Slide,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneToOverride = luminary.getSceneById(targetSceneId);
        if (!sceneToOverride) {
            return;
        }
        const sceneDesignProps = selectedScene.save();
        // get serialized version of selected scene design and apply it to new scene
        sceneToOverride.clear();
        sceneToOverride.load(sceneDesignProps);
        sceneToOverride.generateNewAssetIds();

        if (
            sceneToOverride.sceneCategory !== VideoData.CATEGORY_TALKING_HEAD &&
            sceneToOverride.sceneCategory !== VideoData.CATEGORY_VOICEOVER &&
            sceneToOverride.sceneCategory !== VideoData.CATEGORY_OUTRO
        ) {
            // we do not want voiceover and talking head to show up in the recents section of the new scene picker
            dispatch(storeRecentSelectedSceneDesign({ sceneDesignId: selectedScene.sceneDesign }));
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * The applySystem* operations are operations triggered by the system and, if applicable,
 * need to respect user overrides.
 * Each of these operations have an equivalent operation for changes initiated by the user.
 */

export const applyMediaCropOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneGroupId: string,
        croppedSceneId: string,
        croppedAssetId: string,
        baseSceneDesignId: string | null,
        offsetX: number,
        offsetY: number,
        scale: number,
        rotation: number,
        flipHorizontally: boolean,
        flipVertically: boolean,
        transformChanged: boolean,
        scaleChanged: boolean,
        rotationChanged: boolean,
        flipChanged: boolean,
        parentActionGroup = uuid(),
    ) => {
        luminary.batchRun(() => {
            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
            const croppedScene = luminary.getSceneById(croppedSceneId);
            if (!sceneGroup || !croppedScene)
                throw new Error('SceneGroup or CroppedScene not found');
            const croppedAsset = croppedScene.getAssetById(croppedAssetId) as
                | TimeAwareMediaAsset
                | undefined;
            if (!croppedAsset) throw new Error('CroppedAsset not found');
            const isLinkedAsset = croppedAsset.linkToSceneGroup;
            if (croppedScene.sceneDesign === baseSceneDesignId && isLinkedAsset) {
                sceneGroup.scenes.forEach((scene) => {
                    const isBaseSceneDesign = scene.sceneDesign === baseSceneDesignId;
                    const hasBeenOverridden = isCropUserOverridden(getState(), scene.id);
                    if (!isBaseSceneDesign && hasBeenOverridden) {
                        // for non-base scenes that have had their crop set, don't update the crop
                        return;
                    }
                    getLinkedMediaAssets(scene).forEach((assetToUpdate) => {
                        const allowedDelta = 5; // Allow 5 px width/height discrepancy when deducing equality
                        const isEqualResolution =
                            Math.abs(
                                croppedAsset.mediaTargetResolution.width -
                                    assetToUpdate.mediaTargetResolution.width,
                            ) < allowedDelta &&
                            Math.abs(
                                croppedAsset.mediaTargetResolution.height -
                                    assetToUpdate.mediaTargetResolution.height,
                            ) < allowedDelta;
                        if (isEqualResolution) {
                            setMediaCrop(
                                luminary,
                                scene.id,
                                assetToUpdate.id,
                                offsetX,
                                offsetY,
                                scale,
                                rotation,
                                flipHorizontally,
                                flipVertically,
                            );
                        }
                    });
                });
                analytics.track('Talking Head cropped', {
                    sceneGroupId,
                    transformChanged,
                    scaleChanged,
                    rotationChanged,
                    flipChanged,
                    scale,
                });
            } else {
                setMediaCrop(
                    luminary,
                    croppedScene.id,
                    croppedAsset.id,
                    offsetX,
                    offsetY,
                    scale,
                    rotation,
                    flipHorizontally,
                    flipVertically,
                );
                dispatch(
                    setCropUserOverride({
                        sceneId: croppedScene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                analytics.track('Crop set by user', {
                    croppedSceneId,
                    transformChanged,
                    scaleChanged,
                    rotationChanged,
                    flipChanged,
                    scale,
                });
            }
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const fitScreenRecordingInSceneOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        {
            sceneGroupId,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) throw new Error('SceneGroup not found');

        const scene = sceneGroup.scenes[0];
        const sceneId = scene.id;

        const mediaAsset = scene
            .getAssets()
            .filter(VideoData.isMediaAsset)
            .find((a) => isScreenRecordingSourceData(a.sourceData));
        if (!mediaAsset) {
            throw new Error('Media asset not found or is not a valid media asset');
        }
        const assetId = mediaAsset.id;

        const assetSourceWidth = mediaAsset.sourceData.width;
        const assetSourceHeight = mediaAsset.sourceData.height;
        const mediaTargetWidth = mediaAsset.mediaTargetResolution.width;
        const mediaTargetHeight = mediaAsset.mediaTargetResolution.height;

        const cropDimensions = {
            assetWidth: assetSourceWidth,
            assetHeight: assetSourceHeight,
            viewportWidth: mediaTargetWidth,
            viewportHeight: mediaTargetHeight,
        };
        const containState = createContainCropState(cropDimensions);
        const zoomValue = Math.max(containState.zoom, MEDIA_ZOOM.MIN_VALUE);
        const xTranslatePercent = containState.xTranslatePercent;
        const yTranslatePercent = containState.yTranslatePercent;

        const baseSceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(
            getState(),
            luminary.template.id,
        );

        const translateXPercent = mediaAsset.xTranslation;
        const translateYPercent = mediaAsset.yTranslation;
        const assetScale = mediaAsset.scaleX;

        const rotationValue = 0;
        const flipHorizontally = false;
        const flipVertically = false;
        const rotationChanged = false;
        const flipChanged = false;
        const translateChanged =
            xTranslatePercent !== translateXPercent || yTranslatePercent !== translateYPercent;
        const zoomChanged = zoomValue !== assetScale;

        call(
            applyMediaCropOperation,
            sceneGroupId,
            sceneId,
            assetId,
            baseSceneDesignId,
            xTranslatePercent,
            yTranslatePercent,
            zoomValue,
            rotationValue,
            flipHorizontally,
            flipVertically,
            translateChanged,
            zoomChanged,
            rotationChanged,
            flipChanged,
            parentActionGroup,
        );
    },
);

export const updateSceneDurationToEqualScreenRecordingOperation = createLuminaryOperation(
    (
        { luminary },
        {
            sceneGroupId,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) throw new Error('SceneGroup not found');

        const scene = sceneGroup.scenes[0];
        const mediaAssets = findScreenRecordingMediaAssetsInScene(scene);
        if (mediaAssets.length === 0) {
            throw new Error('No screen recording media assets found in scene');
        }
        if (mediaAssets.length > 1) {
            throw new Error('Multiple screen recording media assets found in scene');
        }
        const mediaAsset = mediaAssets[0];
        const duration = mediaAsset.duration;
        setSceneDuration(luminary, scene.id, duration);
    },
);

export const initiateScreenRecordingOperation = createLuminaryOperation(
    (
        { call, dispatch },
        sceneGroupIndex = 0,
        deleteOtherScenes = false,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (sceneGroupIndex < 0) {
            throw new Error('Invalid scene group index');
        }
        const { sceneGroupId, sceneId } = call(
            createNewVoicedrivenSceneGroupOperation,
            VideoData.CATEGORY_TALKING_HEAD,
            sceneGroupIndex,
            {
                isScreenRecording: true,
                deleteOtherScenes,
            },
        );
        dispatch(setFocusedSceneAndScroll(sceneId, sceneGroupId));
        dispatch(
            showModal({
                modal_id: MODAL_IDS.RECORD_YOUR_SCREEN,
                modal_context: ImmutableFromJS<RecordYourScreenModalContext>({
                    uploadConfirmedCallback: (uploadingPromise: Promise<string[]>) => {
                        call(applyUploadingMediaToSceneGroupOperation, {
                            uploadingPromise,
                            uploadingContext: UploadingContext.ScreenRecording,
                            sceneGroupId,
                            recommendOverlays: false,
                            systemTriggered,
                            parentActionGroup,
                        });
                    },
                }),
            }),
        );
    },
);

/*
 * The remaining operations are not overrideable
 */

/**
 * Creates a blank scene and inserts it into the specified position
 *
 * @param {number} targetIndex
 * @param {bool} systemTriggered
 * @param {uuid} parentActionGroup
 * @param {bool} saveCheckpoint
 * @return {Object} Record<sceneId: string, sceneGroup: SceneGroup>
 */
export const addBlankSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        targetIndex: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
        saveCheckpoint = true,
    ) => {
        const templateId = luminary.template.id;
        const sceneDesignId = getTemplateDefaultSceneDesign(getState(), templateId);
        if (!sceneDesignId) throw new Error('Default scene design not found');
        const { sceneGroup, scene } = luminary.addSceneGroup({ sceneDesignId });
        luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex);
        saveCheckpoint &&
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );

        dispatch(trackChangedTextAssetSlide({ sceneId: scene.id }));

        return { sceneId: scene.id, sceneGroup };
    },
);

/**
 * Creates a blank default scene and inserts it into the specified position
 *
 * @param {number} targetIndex
 * @param {bool} systemTriggered
 * @param {uuid} parentActionGroup
 * @param {bool} saveCheckpoint
 * @return {Object} Record<sceneId: string, sceneGroup: SceneGroup>
 */
export const addDefaultSceneOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }, targetIndex: number, deleteOtherScenes = false) => {
        // Get the template and create a new scene group with the default scene design
        const templateId = luminary.template.id;
        const sceneDesignId = getTemplateDefaultStoryboardSceneDesign(getState(), templateId);
        if (!sceneDesignId) throw new Error('Default scene design not found');
        const { sceneGroup, scene } = luminary.addSceneGroup({ sceneDesignId });
        luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex);

        // delete all other scenes if requested
        if (deleteOtherScenes) {
            // preserve any brand scenes (since we'd just re-create them)
            const sceneCategoriesToPreserve: SceneCategory[] = [VideoData.CATEGORY_BRAND];
            const sceneGroupIdsToKeep = [sceneGroup.id];
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (sceneCategoriesToPreserve.includes(getSceneGroupCategory(sceneGroup))) {
                    sceneGroupIdsToKeep.push(sceneGroup.id);
                }
            });
            luminary.music = null;
            clearScenes(luminary, sceneGroupIdsToKeep);
        }

        return { sceneId: scene.id, sceneGroup };
    },
);

/**
 * Add a new scene at the specified index with an optional scene design
 * @param targetIndex - The index where the scene should be inserted
 * @param sceneDesignId - Optional scene design ID. If not provided, uses the template's default storyboard scene design
 * @return {Object} Record<sceneId: string, sceneGroup: SceneGroup, sceneDesignId: string>
 */
export const addSceneWithDesignOperation = createLuminaryOperation(
    ({ luminary, getState }, targetIndex: number, sceneDesignId?: string) => {
        // If no sceneDesignId provided, get the template's default scene design
        let finalSceneDesignId: string;
        if (sceneDesignId) {
            finalSceneDesignId = sceneDesignId;
        } else {
            const templateId = luminary.template.id;
            const defaultDesign = getTemplateDefaultStoryboardSceneDesign(getState(), templateId);
            if (!defaultDesign) throw new Error('Default scene design not found');
            finalSceneDesignId = defaultDesign;
        }

        const { sceneGroup, scene } = luminary.addSceneGroup({ sceneDesignId: finalSceneDesignId });
        luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex);

        return { sceneId: scene.id, sceneGroup, sceneDesignId: finalSceneDesignId };
    },
);

export const addVoiceDrivenAssetsToSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, getState },
        sceneCategory: SceneCategory,
        sceneGroupId: string,
        { setPlaceholderAsset = true, hasAvatar = false, isScreenRecording = false } = {},
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        if (!isVoiceDrivenCompatibleSceneCategory(sceneCategory)) {
            return;
        }

        luminary.batchRun(() => {
            let didAssignSourceData = false;
            if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
                sceneGroup.addMediaAsset();
                if (setPlaceholderAsset) {
                    const sourceData = { ...TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA };
                    if (isScreenRecording) {
                        sourceData.source_id = appendSceneRecordingSuffix(sourceData.source_id);
                    }
                    sceneGroup.mediaAsset.sourceData = sourceData;
                    sceneGroup.scenes[0].duration =
                        TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA.duration as number;
                    didAssignSourceData = true;
                }
            } else if (sceneCategory === VideoData.CATEGORY_VOICEOVER) {
                if (hasAvatar) {
                    sceneGroup.avatar.addForeground();
                    if (setPlaceholderAsset && sceneGroup.avatar.foreground) {
                        sceneGroup.avatar.foreground.sourceData =
                            TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA;
                        sceneGroup.scenes[0].duration =
                            TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA.duration as number;
                        didAssignSourceData = true;
                    }
                    sceneGroup.avatar.addBackground();
                    if (sceneGroup.avatar.background) {
                        sceneGroup.avatar.background.sourceData =
                            AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA;
                    }
                }
                sceneGroup.addVoiceoverAsset();
                if (setPlaceholderAsset) {
                    sceneGroup.voiceoverAsset.sourceData = VOICEOVER_PLACEHOLDER_ASSET_SOURCE;
                    sceneGroup.scenes[0].duration =
                        VOICEOVER_PLACEHOLDER_ASSET_SOURCE.duration * 1000;
                    didAssignSourceData = true;
                }
            }

            // Set placeholder caption text
            const defaultCaptionsSceneDesignId = getTemplateDefaultCaptionSceneDesign(
                getState(),
                luminary.template.id,
            );
            if (!defaultCaptionsSceneDesignId) throw new Error('Caption scene design not found');
            resetCaptions(sceneGroup, defaultCaptionsSceneDesignId);

            if (didAssignSourceData) {
                void updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
            }
        });
    },
);

/**
 * Generates talking head SceneGroup from a media asset and it's captions.
 * @param sceneGroupId The id of the target scene group
 * @param captionBuckets A list of captions returned by the `asset_transcript` api
 * @param mediaSourceData The source data of the media asset. If null, the source data isn't set
 */
export const buildTalkingHeadSceneOperation = createLuminaryOperation(
    async (
        { luminary, call },
        {
            sceneGroupId,
            mediaSourceData,
            blueprintId = null,
        }: {
            sceneGroupId: string;
            mediaSourceData: MediaSourceData;
            blueprintId?: number | null;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const sceneGroupMediaAsset = sceneGroup.mediaAsset || sceneGroup.addMediaAsset();
        sceneGroupMediaAsset.sourceData = mediaSourceData;
        sceneGroupMediaAsset.volume = AudioVolume.MAX;

        // adjust scenes after base asset is set
        await call(buildCaptionSceneOperation, { sceneGroupId, blueprintId });
        await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });

        if (isScreenRecordingSourceData(mediaSourceData)) {
            call(fitScreenRecordingInSceneOperation, { sceneGroupId });
        }
    },
);

/**
 * Generate Scene Group with captions from video by default
 * @returns true when slide has Talking Head type
 */
export const applyTalkingHeadMediaOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            media = null, // media asset to apply (null = use existing)
            language_code = null, // specified language code (null = auto)
            forceCaptionGeneration = false, // set to true if you want the captions to always be
            enhanceAudio = true,
            recommendOverlays = true,
            blueprintId = null,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            media?: ImmutableMap<SlideBackgroundModel> | null;
            language_code?: string | null;
            forceCaptionGeneration?: boolean;
            enhanceAudio?: boolean;
            recommendOverlays?: boolean;
            blueprintId?: number | null;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return false;
        }
        const scene = sceneGroup.scenes[0];
        if (!scene) {
            return false;
        }
        const sceneId = scene.id;
        if (scene.sceneCategory !== VideoData.CATEGORY_TALKING_HEAD) {
            return false;
        }

        const startTime = performance.now();
        let mediaId, hasVoiceover, sourceData: MediaSourceData;

        if (!media) {
            const defaultMediaAsset = sceneGroup.mediaAsset;
            if (defaultMediaAsset) {
                mediaId = defaultMediaAsset.sourceData.uuid;
                hasVoiceover = defaultMediaAsset.hasVoiceover;
                sourceData = defaultMediaAsset.sourceData;
            } else {
                throw new Error('Media asset not found');
            }
        } else {
            if (media.get('type') !== SlideBackgroundModelTypeEnum.VIDEO) {
                return false;
            }
            const video = media.get(SlideBackgroundModelTypeEnum.VIDEO);
            if (!video) throw new Error('Video data not found');
            if (video.get('has_audio') === false) {
                Toast.error(
                    'The video clip you selected does not appear to have any audio. Please choose another clip that contains audio of a person speaking.',
                );
                analytics.track('No audio detected in talking head scene', {
                    sceneId: scene.id,
                    sceneGroupId: sceneGroup.id,
                    sceneDesignId: scene.sceneDesign,
                    templateId: luminary.template.id,
                });
                return false;
            }
            mediaId = video.get('uuid');
            hasVoiceover = video.get('has_voiceover');
            sourceData = {
                ...ImmutableToJS(video),
                type: SlideBackgroundModelTypeEnum.VIDEO,
            } as unknown as MediaSourceData;
        }

        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId: sceneGroup.id,
                type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                context: { loadingMsg: LOADING_SCENE_MSG.GEN_CAPTION },
            }),
        );
        const showError = () => {
            Toast.error(
                'We had a problem generating captions for your chosen video clip. Please make sure the video contains clear audio speech and try again.',
            );
        };

        try {
            // mediaId must be defined
            if (hasVoiceover === null || forceCaptionGeneration) {
                if (!mediaId) throw new Error('Media ID not found');
                hasVoiceover = await regenerateTranscriptForAsset(
                    mediaId,
                    TranscriptAssetType.VIDEO,
                    language_code ?? undefined,
                );
            }
            if (hasVoiceover) {
                if (!mediaId) throw new Error('Media ID not found');
                if (!sourceData) throw new Error('Source data not found');
                const response = await ApiService.assetTranscriptRetrieve({
                    path: { asset_id: mediaId },
                    query: { asset_type: TranscriptAssetType.VIDEO },
                });
                const transcriptWords = convertTranscriptWordsToMillis(
                    response.data.transcriptWords,
                );

                dispatch(showTranscriptLanguageWarning(response.data.language));
                if (media) {
                    await call(buildTalkingHeadSceneOperation, {
                        sceneGroupId: sceneGroup.id,
                        mediaSourceData: sourceData,
                        blueprintId,
                    });
                }

                // for long talking head videos, we prompt the user to trim before calling this function again
                if (talkingHeadPlaybackRegionRequiresTrimming(sceneGroup.mediaAsset)) {
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.INSERT_TRIM,
                            modal_context: ImmutableFromJS({
                                sceneGroupId: sceneGroup.id,
                                trimmerSource: TrimmerSource.SWAP,
                            }),
                        }),
                    );
                    // since the video requires trimming, we don't want to start recommendations until the video is trimmed
                    recommendOverlays = false;
                    // we don't return because we need the transcript to be fetched and set
                    // however, we want to show the modal asap to the user to prompt trimming
                }
                // Create and load transcript editor instance to global context
                await call(
                    loadNewTranscriptOperation,
                    transcriptWords,
                    response.data.language,
                    sceneGroupId,
                    sceneId,
                    true,
                    false,
                    startTime,
                    false,
                    systemTriggered,
                    parentActionGroup,
                );

                // Copy caption styles (scene design ID and granular overrides) from existing
                // voice-driven scene groups to maintain caption style consistency
                call(applyGlobalCaptionStylesToSceneGroupOperation, sceneGroup);

                // Do not recommend overlays for screen recordings
                const isScreenRecording = isScreenRecordingSourceData(sourceData);
                if (isScreenRecording) {
                    recommendOverlays = false;
                }
                if (recommendOverlays && !SERVER.END_TO_END_TESTING_MODE) {
                    await call(
                        fetchAndSetRecommendedVoiceDrivenVideoOperation,
                        transcriptWords,
                        response.data.language,
                        sceneGroupId,
                        NarrativeThreadEnum.TALKING_HEAD,
                        getSelectedBlueprintId(getState()),
                        true, // isGlobalVideo
                        systemTriggered,
                        parentActionGroup,
                    );
                }
                dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(luminary),
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
                dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            } else {
                showError();
                dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            }
        } catch (e) {
            showError();
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            ErrorReporter.capture(e);
        }
        // Check if talking head is transcoded and user is on paid plan
        if (
            enhanceAudio &&
            !videoAssetIsNotTranscoded(sceneGroup.mediaAsset) &&
            SERVER.USER.plan_price > 0
        ) {
            void call(startAudioEnhancementOperation, sceneGroup.id);
        }
        return true;
    },
);

/**
 * When we first apply a media asset to a scene (or create a new scene with a media asset), we need
 * to set the volume of the mediaAsset and SceneGroup to good defaults.
 */
export const setInitialVolumeForMediaAsset = (
    luminary: Luminary,
    mediaAsset: TimeAwareMediaAsset,
    scene: Slide,
    sceneIsOverlay: boolean,
) => {
    const sceneGroup = scene.sceneGroup;
    const videoMusicVolume = getVideoMusicVolume(luminary);
    // unset music volume override if media is silent or not a video
    if (!mediaAsset.isVideo || !mediaAsset.hasAudio) {
        sceneGroup.musicVolumeOverride = null;
    }
    if (!mediaAsset.isVideo) {
        return;
    }
    // turn on audio by default for user uploads and mute otherwise
    if (sceneIsOverlay) {
        mediaAsset.volume = 0;
    } else {
        mediaAsset.volume =
            mediaAsset.mediaSource === SourceEnum.SOURCE_USER ? AudioVolume.MAX : AudioVolume.MIN;
        // we should only be ducking user uploaded media, not stock media
        if (
            mediaAsset.mediaSource === SourceEnum.SOURCE_USER &&
            videoMusicVolume <= (AudioVolume.DUCK as number)
        ) {
            sceneGroup.musicVolumeOverride = AudioVolume.DUCK;
        }
    }
};

/**
 * Replaces the current sceneGroup with the same scene design and assigns the mediaAsset
 * to the background media component on the new scene
 */
const replaceTalkingHeadSceneGroupWithNewMediaOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroup: SceneGroup,
        mediaAsset: ImmutableMap<SlideBackgroundModel>,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!sceneGroup) {
            return;
        }
        const sceneDesignId = sceneGroup.scenes[0].sceneDesign;
        const newSceneGroup = luminary.addSceneGroup({ sceneDesignId }).sceneGroup;
        newSceneGroup.musicVolumeOverride = sceneGroup.musicVolumeOverride;
        const captionsLanguage: string | undefined = undefined;
        luminary.moveSceneGroupToIndex(newSceneGroup.id, sceneGroup.index);
        const forceCaptionGeneration = false;
        const enhanceAudio = false;
        const recommendOverlays = true;
        await call(applyTalkingHeadMediaOperation, {
            sceneGroupId: newSceneGroup.id,
            media: mediaAsset,
            language_code: captionsLanguage,
            forceCaptionGeneration,
            enhanceAudio,
            recommendOverlays,
            systemTriggered,
            parentActionGroup,
        });
        luminary.removeSceneGroup(sceneGroup.id);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Slide Replaced with Default Scene Design', { action: 'drag-media' });
    },
);

/**
 * Uploads and saves a given AI image to the database and replaces usages of the corresponding temporary AI image asset with the saved asset.
 */
const uploadAndUpdateAIImageOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        generation: ModifiedAssetGenerationData,
        targetAssetId: string,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!generation) {
            throw new Error('Insufficient data to upload AI image');
        }
        const url = generation?.asset?.background?.image?.preview_url;
        const newAsset = await saveAIMedia(generation);
        // If the scene has been destroyed by the time the upload completes, there's no need to continue
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        // Replace temporary AI image asset with saved asset
        const asset = luminary.getAssetById(targetAssetId) as TimeAwareMediaAsset | undefined;
        if (!asset) {
            throw new Error('Asset not found');
        }
        const newImageAssetModel = newAsset
            .get(AssetContainerTypes.BACKGROUND)
            ?.get('image')
            ?.toJS();
        if (!newImageAssetModel || !newImageAssetModel.uuid) {
            throw new Error('No image source data for background asset');
        }
        if (url !== asset.sourceData.preview_url) {
            // The user has changed the media while the upload was in progress so we don't want to replace the asset
            return;
        }
        // Replace existing source data properties with newly uploaded source data
        asset.setSourceDataWithoutResettingProperties({
            ...asset.sourceData,
            uuid: newImageAssetModel.uuid,
            source: newImageAssetModel.source,
            license: newImageAssetModel.license,
            title: newImageAssetModel.title,
            thumbnail_url: newImageAssetModel.thumbnail_url,
            preview_url: newImageAssetModel.preview_url,
            render_url: newImageAssetModel.render_url,
            width: newImageAssetModel.width,
            height: newImageAssetModel.height,
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Supports setMediaAssetForSceneOperation by handling the actual media asset replacement,
 * without any scene group swapping.
 */
const setMediaAssetForSceneWithoutReplacingSceneGroupOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneId,
            mediaSourceData,
            targetAssetId = null,
            method = SetMediaAssetForSceneOperationMethod.CLICK,
            isRecommendedMedia = false,
            autoScale = false,
            isSticker = false,
            targetPosition = null,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneId: string;
            mediaSourceData: ImmutableMap<SlideBackgroundModel>;
            targetAssetId?: string | null;
            method: SetMediaAssetForSceneOperationMethod;
            isRecommendedMedia: boolean;
            autoScale?: boolean;
            isSticker?: boolean;
            targetPosition?: { x: number; y: number } | null;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        const scene = isSticker
            ? call(
                  getOrCreateStickerCompatibleSceneOperation,
                  sceneId,
                  systemTriggered,
                  parentActionGroup,
              )
            : luminary.getSceneById(sceneId);
        if (!scene) {
            Toast.error('Unable to add media to scene', 'Error');
            return;
        }

        // convert mediaSourceData to sourceData
        const rawSourceData = mediaSourceData.toJS();
        // media assets are nested by their type, so we want to flatten the object
        // ie. { type: 'image', image: { uuid: 'abc', ...} } - we just want { type: 'image', uuid: 'abc' ...}
        const sourceData: FlattenedAsset = convertBackgroundAssetToFlattenedAsset(rawSourceData);

        const sceneGroup = scene.sceneGroup;

        const tryGetOrAddMediaAsset = (scene: Slide): TimeAwareMediaAsset | null => {
            if (!isSticker) {
                return getMediaToUpdate(scene, targetAssetId);
            }

            let targetAsset = null;
            if (targetAssetId) {
                const foundAsset = scene.getAssetById(targetAssetId);
                if (
                    foundAsset &&
                    (VideoData.isStickerAsset(foundAsset) || VideoData.isIconAsset(foundAsset))
                ) {
                    targetAsset = foundAsset;
                }
            }

            let targetDimensions: ConfigurableElementProperties['dimensions'] = {
                width: sourceData.width,
                height: sourceData.height,
            };
            let introOutroTimes = null;
            let dynamicStyles = null;
            let groupIndex = null;
            let container: Slide | AssetGroup = scene;
            if (targetAsset) {
                if (VideoData.isMediaAsset(targetAsset)) {
                    return targetAsset;
                }

                const assetGroup = targetAsset.getParentByType(VideoData.GROUP_ASSET_TYPE);
                if (assetGroup) {
                    container = assetGroup;
                }
                ({
                    position: targetPosition,
                    dimensions: targetDimensions,
                    introOutroTimes,
                    dynamicStyles,
                    groupIndex,
                } = getElementPropertiesToSave(targetAsset, scene.duration));
                call(
                    deleteAssetOperation,
                    scene.id,
                    targetAsset.id,
                    systemTriggered,
                    parentActionGroup,
                );
            }

            const mediaAsset = call(
                addStickerAssetToSceneInternalOperation,
                container,
                VideoData.MEDIA_ASSET_TYPE,
                {
                    position: targetPosition,
                    dimensions: targetDimensions,
                    introOutroTimes,
                    dynamicStyles,
                    groupIndex,
                },
                systemTriggered,
                parentActionGroup,
            );

            return mediaAsset;
        };

        const mediaAsset = tryGetOrAddMediaAsset(scene);

        // below this, we know media is set
        const isTargetTalkingHeadBaseAsset =
            (isTalkingHeadSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) &&
            (!mediaAsset || !!mediaAsset?.linkToSceneGroup);
        const isReplacingVoiceDrivenBaseAsset = isTargetTalkingHeadBaseAsset;
        if (isReplacingVoiceDrivenBaseAsset) {
            const isOriginalTranscript = getIsOriginalOrMissingTranscript(
                getState(),
                sceneGroup.id,
            );
            const canOverwriteBaseAsset = canVoiceDrivenBaseAssetBeOverwritten(
                sceneGroup,
                isOriginalTranscript,
                sourceData,
            );
            if (!canOverwriteBaseAsset.result) {
                if (canOverwriteBaseAsset.reason) {
                    Toast.error(canOverwriteBaseAsset.reason, canOverwriteBaseAsset.title || '');
                }
                return;
            }
        }

        const isTalkingHeadPlaceholderAsset =
            isTargetTalkingHeadBaseAsset && !scene.sceneGroup.mediaAsset?.hasAudio;
        const media = mediaAsset?.linkToSceneGroup
            ? sceneGroup.mediaAsset || sceneGroup.addMediaAsset()
            : mediaAsset;
        if (!media) throw new Error('Media not found');

        // assign sourceData
        if (isFlattenedAssetMediaSourceDataCompatible(sourceData)) {
            media.sourceData = sourceData;
        } else {
            throw Error('sourceData is not compatible with media.sourceData');
        }

        // set the attribution
        scene.attribution = sourceData['attribution'];

        // set the volume
        setInitialVolumeForMediaAsset(luminary, media, scene, isSceneAnOverlay(getState(), scene));

        // update the number of premium media credits used in this draft
        dispatch(updatePremiumMediaUsage());

        const isSimilarMediaResult = getIsSimilarMediaResult(mediaSourceData)(dispatch, getState);
        const similarSearchOriginalAssetUuid = getSimilarSearchOriginalAssetUuid(getState());
        let model_name = null;
        let promptDetails = null;
        let generation = null;
        let generationIndex = null;
        if ((media.sourceData.source as SourceEnum) === SourceEnum.SOURCE_AI_GENERATED) {
            // Note that generation may not exist in redux for saved ai images e.g ai images in the recents tab.
            generation = getGenerationFromMediaUuid(
                getState(),
                media.sourceData.uuid,
                SlideBackgroundModelTypeEnum.IMAGE,
            );
            if (generation) {
                model_name = generation.model_name;
                promptDetails = {
                    prompt: generation.prompt,
                    style: generation.properties?.style,
                    tone: generation.properties?.tone,
                };
                generationIndex = generation.index;
            }
        }
        // analytics
        const mediaSource = getActiveSearchContextForCurrentTab(getState());
        const query = mediaSource ? getActiveSearchTerm(getState(), mediaSource) : null;
        analytics.track('Media Step: Media Added To Slide', {
            sceneId,
            sceneCategory: scene.sceneCategory,
            source: media.sourceData.source,
            media_source: mediaSource,
            query: query,
            current_user: mediaSourceData
                .get('history_index_user_ids')
                ?.some((user_id: number) => SERVER.USER.id === user_id),
            model_name,
            promptDetails,
            isRecommendedMedia,
            isSimilarMediaResult,
            similarSearchOriginalAssetUuid,
            method,
            assetType: mediaSourceData.get('type'),
            generationIndex,
        });
        if (!systemTriggered && mediaAsset) {
            const subsceneIndex = getFocusedSubsceneIndex(getState());
            SlideEventEmitter.emit(SLIDE_EVENTS.START_EDITING_NON_TEXT_ASSET, {
                sceneGroupId: sceneGroup.id,
                sceneId,
                subsceneIndex,
                assetId: mediaAsset.id,
                assetType: VideoData.MEDIA_ASSET_TYPE,
            } as StartEditingNonTextAssetPayload);
            focusAssetElement(mediaAsset.id, sceneId, subsceneIndex ?? 0);
        }

        /**
         * If it is a screen recording media, but not a screen recording scene
         * group, we need to manually fit the screen recording in the scene and
         * update the scene duration to match the screen recording duration.
         * This can happen when the user replaces a non-screen recording media
         * with a screen recording media.
         *
         * In case it is a screen recording scene group, the scene category
         * equals talking head if-block below will handle the fitting and
         * duration update.
         */
        if (isScreenRecordingSourceData(sourceData) && !isScreenRecordingSceneGroup(sceneGroup)) {
            /**
             * Media asset needs to be loaded before we can fit the media
             * because Luminary fills the media according to the fill mode
             * property on media load. This action overrides our properties.
             * By waiting for the media asset to be loaded, we ensure that
             * our properties are applied after Luminary's.
             */
            await luminary.onCanPlay();
            call(fitScreenRecordingInSceneOperation, {
                sceneGroupId: sceneGroup.id,
                systemTriggered,
                parentActionGroup,
            });
            call(updateSceneDurationToEqualScreenRecordingOperation, {
                sceneGroupId: sceneGroup.id,
                systemTriggered,
                parentActionGroup,
            });
        }

        // if we potentially just updated talking head media, trigger caption regeneration
        if (scene.sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
            if (!isTalkingHeadPlaceholderAsset) {
                Toast.info(
                    'Hang tight - we are updating your scene media, transcript and captions.',
                    '',
                );
                call(
                    replaceTalkingHeadSceneGroupWithNewMediaOperation,
                    sceneGroup,
                    mediaSourceData,
                    systemTriggered,
                    parentActionGroup,
                );
                return;
            }
            await call(applyTalkingHeadMediaOperation, {
                sceneGroupId: sceneGroup.id,
                media: mediaSourceData,
                language_code: null, // unknown language
                forceCaptionGeneration: false,
                enhanceAudio: false,
                recommendOverlays: true,
                systemTriggered,
                parentActionGroup,
            });
        }

        // For AI charts: apply fit-to-scene and disable animations
        const isChart = (media.sourceData.source as SourceEnum) === SourceEnum.SOURCE_AI_CHART;
        if (isChart) {
            media.setScaleToContainToVisibleArea();
            media.kenburn = 'none';
        }

        if (!isSticker && !isChart) {
            // fetch recommendations (skip for charts - we apply our own settings above)
            call(fetchAndSetRecommendedSceneAttributesOperation, {
                sceneId,
                systemTriggered,
                parentActionGroup,
                autoScale,
            });
        }

        // If media is newly ai generated, get as file and upload to bucket.
        if (generation) {
            // Run async
            call(uploadAndUpdateAIImageOperation, generation, media.id, sceneId);
        } else {
            // checkpoint video
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

/**
 * Replaces the current scene with the default scene design and assigns the mediaAsset
 * to the background media component on the new scene - useful if `setMediaAssetForScene` fails
 */
const replaceSceneGroupWithDefaultAndAssignMediaOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        mediaAsset: ImmutableMap<SlideBackgroundModel> | null,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('SceneGroup not found');
        }

        const scene = sceneGroup.scenes[0];
        const templateId = luminary.template.id;
        const sceneDesignId = getTemplateDefaultSceneDesign(getState(), templateId);
        if (!sceneDesignId) throw new Error('default scene design not found');
        const defaultSceneGroup = luminary.addSceneGroup({ sceneDesignId }).sceneGroup;

        luminary.batchRun(() => {
            // copy content over to the new scene
            const sceneSwapper = new SceneSwapper(scene);
            sceneSwapper.copyContentToScene(defaultSceneGroup.scenes[0], {
                copyVoicedrivenContent: false,
            });

            luminary.moveSceneGroupToIndex(defaultSceneGroup.id, sceneGroup.index);
            luminary.removeSceneGroup(sceneGroup.id);
            dispatch(clearTextAssetTrackedSlide({ sceneId: scene.id }));

            if (mediaAsset) {
                call(setMediaAssetForSceneWithoutReplacingSceneGroupOperation, {
                    sceneId: defaultSceneGroup.scenes[0].id,
                    mediaSourceData: mediaAsset,
                    targetAssetId: null,
                    method: SetMediaAssetForSceneOperationMethod.CLICK,
                    isRecommendedMedia: false,
                    systemTriggered,
                    parentActionGroup,
                });
            }
        });

        // resize so all text fits on the scene
        resizeTextToFitOnScene(defaultSceneGroup.scenes[0]);

        analytics.track('Slide Replaced with Default Scene Design', { action: 'drag-media' });

        return defaultSceneGroup;
    },
);

export enum SetMediaAssetForSceneOperationMethod {
    CLICK = 'click',
    DRAG = 'drag',
    PASTE = 'paste',
}

/**
 * Assigns the provided mediaSourceData object to the specified scene.
 * If no targetAssetId is specified we'll assign the media to the 'default' (aka largest editable) media asset component
 * on the scene. If none exists we'll switch to a scene design that does support media.
 *
 * It's important to use this method instead of directly setting `media.sourceData` since there are a lot of things we
 * check, validate, and set in this method: talking head specific logic, premium media usage, etc.
 */
export const setMediaAssetForSceneOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        {
            sceneId,
            mediaSourceData,
            targetAssetId = null,
            method = SetMediaAssetForSceneOperationMethod.CLICK,
            isRecommendedMedia = false,
            autoScale = false,
            isSticker = false,
            targetPosition,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneId: string;
            mediaSourceData: ImmutableMap<SlideBackgroundModel>;
            targetAssetId?: string | null;
            method?: SetMediaAssetForSceneOperationMethod;
            isRecommendedMedia?: boolean;
            autoScale?: boolean;
            isSticker?: boolean;
            targetPosition?: { x: number; y: number };
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        // validate arguments
        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }

        const sceneGroup = scene.sceneGroup;
        const sceneGroupId = sceneGroup.id;
        const mediaAsset = getMediaToUpdate(scene, targetAssetId);

        // check if we can actually update the asset
        const assetDoesNotExist = !mediaAsset && !isSticker;

        if (assetDoesNotExist && scene.sceneCategory === VideoData.CATEGORY_OUTRO) {
            // Return early since we don't support swapping outro scenes to another scene design.
            return;
        }

        if (assetDoesNotExist) {
            // scene doesn't support media assets
            // if it's an outro scene, we don't want to switch the scene category BUT we don't have a known/guaranteed
            // outro scene with media (default_outro_scene doesn't have that guarantee). So just tell the user.
            if (scene.sceneCategory === VideoData.CATEGORY_OUTRO) {
                Toast.info('This scene does not support media.');
                return;
            }

            if (!isVoiceDrivenSceneGroup(sceneGroup)) {
                // otherwise, swap to default scene design which is guaranteed to support media.
                Toast.info('We swapped your scene design to a design that supports media.', '');
                call(
                    replaceSceneGroupWithDefaultAndAssignMediaOperation,
                    sceneGroupId,
                    mediaSourceData,
                    systemTriggered,
                    parentActionGroup,
                );
                return;
            }

            if (isVoiceoverSceneGroup(sceneGroup)) {
                // this is a voiceover or AI avatar SceneGroup, but the scene has no "mediaAsset hole" in which to place the mediaAsset.
                if (isAIAvatarSceneGroup(sceneGroup)) {
                    Toast.error('Cannot apply media: please switch to a non-AI avatar scene.', '');
                } else {
                    Toast.error(
                        'Voiceover scenes require audio to generate a transcript.',
                        'This media is not an audio with spoken words',
                    );
                }
                return;
            }
        }

        try {
            call(setMediaAssetForSceneWithoutReplacingSceneGroupOperation, {
                sceneId,
                mediaSourceData,
                targetAssetId,
                method,
                isRecommendedMedia,
                isSticker,
                targetPosition,
                systemTriggered,
                parentActionGroup,
                autoScale,
            });
        } catch (e) {
            ErrorReporter.capture(e);
        }
    },
);
export const selectOverlayOperation = createLuminaryOperation(
    (
        {
            luminary,
            dispatch,
            getState,
        }: { luminary: Luminary; dispatch: AppDispatch; getState: GetRootState },
        sceneGroupId: string,
        sceneId: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        if (scene.sceneGroup !== sceneGroup) {
            return;
        }

        // update all the spots in redux that need to know about the selected overlay
        dispatch(setFocusedSceneAndScroll(sceneId, sceneGroupId));
        dispatch(
            updateTranscriptCurrentTimeAndPreview({
                sceneGroupId,
                sceneId,
                renderPreviewAtTime: translateVideoTimeToSceneGroupTime(
                    (scene.startTime + scene.endTime) / 2,
                    sceneGroup.startTime,
                ),
                playPreviewFromTime: translateVideoTimeToSceneGroupTime(
                    scene.startTime,
                    sceneGroup.startTime,
                ),
            }),
        );
    },
);

export const setFocusedSceneInfoToSceneAtTimeOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: { luminary: Luminary; dispatch: AppDispatch },
        videoTimeMillis: number,
    ) => {
        const scenes = luminary.getActiveScenesAt(videoTimeMillis);
        if (scenes.length > 0) {
            const scene = scenes[0]; // just use the first scene
            dispatch(setFocusedSceneInfo({ sceneGroupId: scene.sceneGroup.id, sceneId: scene.id }));
            return { sceneGroupId: scene.sceneGroup.id, sceneId: scene.id };
        }
    },
);

export const moveOtherOverlaysAndCreateOverlayOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        sceneGroupId: string,
        startTimeMillis: number, // in transcript time
        endTimeMillis: number, // in transcript time
        sceneDesignId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
        isMultiSpeakerSelected: boolean = false, // whether the overlay is for a multi-speaker scene
        faceDetections: BoundingBox[] = [], // optional face detection data for the new overlay
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new CreateOverlayError('Scene Group missing');
        }

        return luminary.batchRun(() => {
            call(
                moveOverlaysAwayFromTimesOperation,
                sceneGroupId,
                startTimeMillis,
                endTimeMillis,
                [],
                systemTriggered,
                parentActionGroup,
            );
            const newOverlayId = call(
                createOverlayOperation,
                sceneGroupId,
                startTimeMillis,
                endTimeMillis,
                sceneDesignId,
                systemTriggered,
                parentActionGroup,
            );
            if (isMultiSpeakerSelected) {
                call(setUpMultipleSpeakersSceneOperation, newOverlayId, faceDetections);
                const scene = luminary.getSceneById(newOverlayId);
                if (!scene) {
                    throw new CreateOverlayError('New overlay scene not found');
                }
                // TODO: https://lumen5.atlassian.net/browse/LU-3836
                scene.sceneGroup.mediaAsset.requiresRedraw = true;
            }
            return newOverlayId;
        });
    },
);

export const moveOverlaysAwayFromTimesOperation = createLuminaryOperation(
    (
        { luminary, getState, call },
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        ignoreSceneIds: Array<string> = [],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) throw new Error('Base scene design id not found');

        const voicedrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voicedrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const startTimeRelativeToVideo = translateTranscriptTimeToVideoTime(
            startTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const endTimeRelativeToVideo = translateTranscriptTimeToVideoTime(
            endTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        for (let i = sceneGroup.scenes.length - 1; i >= 0; i--) {
            const scene = sceneGroup.scenes[i];
            if (ignoreSceneIds.includes(scene.id)) {
                continue;
            }
            if (!isSceneAnOverlay(getState(), scene)) {
                continue;
            }
            if (
                lessThanWithinEpsilon(scene.startTime, startTimeRelativeToVideo) &&
                greaterThanWithinEpsilon(scene.endTime, endTimeRelativeToVideo)
            ) {
                // the scene is wrapping the entire region of the new overlay. We need to split it into two scenes
                // and then create the overlay in between them.
                const initialEndTime = translateVideoTimeToTranscriptTime(
                    scene.endTime,
                    voicedrivenAssetPlaybackRegions,
                    voicedrivenAssetSourceDuration,
                    sceneGroup.startTime,
                    { includeTrailingCut: false },
                );
                call(
                    translateOverlayEndOperation,
                    scene.id,
                    sceneGroupId,
                    baseSceneDesignId,
                    startTimeMillis,
                    systemTriggered,
                    parentActionGroup,
                );
                const duplicatedSceneId = call(
                    createOverlayOperation,
                    sceneGroupId,
                    endTimeMillis,
                    initialEndTime,
                    scene.sceneDesign,
                    systemTriggered,
                    parentActionGroup,
                );
                const duplicatedSceneData = scene.save();
                const duplicatedScene = luminary.getSceneById(duplicatedSceneId);
                if (!duplicatedScene) {
                    throw new Error(
                        'while splitting an overlay, could not find the duplicated overlay!',
                    );
                }
                const originalDuration = duplicatedScene.duration;
                duplicatedScene.load(duplicatedSceneData);
                duplicatedScene.generateNewAssetIds();
                duplicatedScene.duration = originalDuration; // .load()'ing will change the duration of the scene, we want the duration specified in the call to createOverlayOperation
            } else if (
                lessThanWithinEpsilon(scene.startTime, startTimeRelativeToVideo) &&
                greaterThanWithinEpsilon(scene.endTime, startTimeRelativeToVideo)
            ) {
                // the scene is overlapping the start of the new overlay, but ends before the new overlay would.
                // We need to translate the end of the scene left.
                call(
                    translateOverlayEndOperation,
                    scene.id,
                    sceneGroupId,
                    baseSceneDesignId,
                    startTimeMillis,
                    systemTriggered,
                    parentActionGroup,
                );
            } else if (
                lessThanWithinEpsilon(scene.startTime, endTimeRelativeToVideo) &&
                greaterThanWithinEpsilon(scene.endTime, endTimeRelativeToVideo)
            ) {
                // the scene is overlapping the end of the new overlay, but starts after the new overlay would.
                // We need to translate the start of the scene right.
                call(
                    translateOverlayStartOperation,
                    scene.id,
                    sceneGroupId,
                    baseSceneDesignId,
                    endTimeMillis,
                    systemTriggered,
                    parentActionGroup,
                );
            } else if (
                greaterThanWithinEpsilon(scene.startTime, startTimeRelativeToVideo) &&
                lessThanWithinEpsilon(scene.endTime, endTimeRelativeToVideo)
            ) {
                // the scene is totally within the new overlay. We need to delete it.
                call(
                    deleteLuminaryOverlayOperation,
                    scene.id,
                    sceneGroupId,
                    systemTriggered,
                    parentActionGroup,
                );
            } else {
                // the scene is not overlapping the new overlay at all. We don't need to do anything.
                continue;
            }
        }
    },
);

export const createOverlaySelectorPreviewsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sourceSceneId: string,
        sourceSceneGroupId: string,
        sceneDesigns: Immutable.List<ImmutableMap<SceneDesign>>,
        overlayBounds: ImmutableMap<OverlayBounds>,
        localLuminary: Luminary,
        multipleSpeakersSceneToSlideIdMap: Record<string, string>,
    ) => {
        let faceDetectionsResult: BoundingBox[];
        try {
            faceDetectionsResult = await call(getFaceDetectionsOperation, sourceSceneId);
            // If there's 0 or 1 face detected, we don't consider it a valid detection for multiple speakers
            if (faceDetectionsResult.length <= 1) {
                faceDetectionsResult = [];
            }
        } catch (error) {
            faceDetectionsResult = [];
        }

        const sceneDesignIds = sceneDesigns.map((sceneDesign) => sceneDesign.get('id'));
        const multipleSpeakersSceneDesignIds = call(
            getMultipleSpeakersSceneDesignIdsOperation,
            sceneDesignIds,
            faceDetectionsResult,
        );

        let slidesAndSceneDesigns = Immutable.List<slideAndSceneDesign>([]);

        slidesAndSceneDesigns = localLuminary.batchRun(() => {
            return sceneDesigns.flatMap((sceneDesign) => {
                const result = [];
                const existingScenes = localLuminary.scenes.filter(
                    (scene) => scene.sceneDesign === sceneDesign.get('id'),
                );
                const isMultipleSpeakerSlide = multipleSpeakersSceneDesignIds?.includes(
                    sceneDesign.get('id'),
                );
                let multiSpeakerScene = null;
                if (existingScenes.length === 0) {
                    // Creating all the scenes
                    const scene = localLuminary.addSceneGroup({
                        sceneDesignId: sceneDesign.get('id'),
                    }).scene;

                    result.push({
                        sceneDesign,
                        slide: scene,
                        specialCase: SceneDesignSpecialCase.NONE,
                    });
                    if (isMultipleSpeakerSlide) {
                        multiSpeakerScene = localLuminary.addSceneGroup({
                            sceneDesignId: sceneDesign.get('id'),
                        }).scene;
                        result.push({
                            sceneDesign,
                            slide: multiSpeakerScene,
                            specialCase: SceneDesignSpecialCase.MultipleSpeakers,
                        });

                        multipleSpeakersSceneToSlideIdMap[sceneDesign.get('id')] =
                            multiSpeakerScene.id;
                    }
                } else {
                    // Get all the normal scenes for this design
                    const normalScenes = existingScenes.filter(
                        (scene) =>
                            !Object.values(multipleSpeakersSceneToSlideIdMap).includes(scene.id),
                    );

                    normalScenes.forEach((scene) => {
                        result.push({
                            sceneDesign,
                            slide: scene,
                            specialCase: SceneDesignSpecialCase.NONE,
                        });
                    });

                    // Get all the multiple speakers scenes for this design
                    if (isMultipleSpeakerSlide) {
                        const multiSpeakerScenes = existingScenes.filter(
                            (scene) =>
                                multipleSpeakersSceneToSlideIdMap[sceneDesign.get('id')] ===
                                scene.id,
                        );

                        multiSpeakerScenes.forEach((scene) => {
                            result.push({
                                sceneDesign,
                                slide: scene,
                                specialCase: SceneDesignSpecialCase.MultipleSpeakers,
                            });
                        });
                    }

                    if (
                        isMultipleSpeakerSlide &&
                        multipleSpeakersSceneToSlideIdMap[sceneDesign.get('id')] === undefined
                    ) {
                        // If the slide does not exists for the multiple speakers version of this design, create it
                        multiSpeakerScene = localLuminary.addSceneGroup({
                            sceneDesignId: sceneDesign.get('id'),
                        }).scene;
                        result.push({
                            sceneDesign,
                            slide: multiSpeakerScene,
                            specialCase: SceneDesignSpecialCase.MultipleSpeakers,
                        });
                        multipleSpeakersSceneToSlideIdMap[sceneDesign.get('id')] =
                            multiSpeakerScene.id;
                    }
                }
                return result;
            });
        });

        const { scenesToShow, sceneToSlideMap, sceneDesignDisplayNameMap } =
            buildScenePresentationMaps(slidesAndSceneDesigns);

        // All slides that we need to show
        const allPreviewSlides = [
            ...Object.values(sceneToSlideMap),
            ...Object.values(multipleSpeakersSceneToSlideIdMap),
        ];

        localLuminary.batchRun(() => {
            call(
                setOverlaySelectorPreviewsVoicedrivenMediaAssetOperation,
                sourceSceneId,
                allPreviewSlides,
                localLuminary,
            );
            call(
                setOverlaySelectorPreviewsFancyCaptionOperation,
                sourceSceneGroupId,
                overlayBounds,
                allPreviewSlides,
                localLuminary,
            );
            call(
                setOverlaySelectorPreviewsStandardizeTalkingHeadMediaAssetOperation,
                sourceSceneId,
                multipleSpeakersSceneToSlideIdMap,
                faceDetectionsResult,
                localLuminary,
                false,
            );
        });

        return {
            scenesToShow,
            sceneToSlideMap,
            sceneDesignDisplayNameMap,
            faceDetectionsResult,
        };
    },
);

const setOverlaySelectorPreviewsVoicedrivenMediaAssetOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sourceSceneId: string,
        allPreviewSlides: string[],
        localLuminary: Luminary,
    ) => {
        const sourceScene = luminary.getSceneById(sourceSceneId);
        if (!sourceScene) return;

        const voicedrivenSourceData = getVoiceDrivenAssetSourceData(sourceScene.sceneGroup);
        const isTalkingHead = isTalkingHeadSceneGroup(sourceScene.sceneGroup);
        const isVoiceover = isVoiceoverSceneGroup(sourceScene.sceneGroup);
        const isAIAvatar = isAIAvatarSceneGroup(sourceScene.sceneGroup);

        localLuminary.batchRun(() => {
            allPreviewSlides.forEach((sceneId) => {
                const scene = localLuminary.getSceneById(sceneId);
                if (scene && voicedrivenSourceData) {
                    if (isVoiceover) {
                        if (scene.sceneGroup.mediaAsset) {
                            scene.sceneGroup.removeMediaAsset();
                        }
                        if (!scene.sceneGroup.voiceoverAsset) {
                            scene.sceneGroup.addVoiceoverAsset();
                        }
                        if (!isAIAvatar && scene.sceneGroup.avatar.foreground) {
                            scene.sceneGroup.avatar.removeForeground();
                        }
                        if (!isAIAvatar && scene.sceneGroup.avatar.background) {
                            scene.sceneGroup.avatar.removeBackground();
                        }
                        if (!isAudioSourceData(voicedrivenSourceData)) {
                            throw new Error('voicedrivenSourceData is not an AudioSourceData');
                        }
                        scene.sceneGroup.voiceoverAsset.sourceData = voicedrivenSourceData;
                    }
                    if (isTalkingHead) {
                        if (scene.sceneGroup.voiceoverAsset) {
                            scene.sceneGroup.removeVoiceoverAsset();
                        }
                        if (scene.sceneGroup.avatar.foreground) {
                            scene.sceneGroup.avatar.removeForeground();
                        }
                        if (scene.sceneGroup.avatar.background) {
                            scene.sceneGroup.avatar.removeBackground();
                        }

                        if (!scene.sceneGroup.mediaAsset) {
                            scene.sceneGroup.addMediaAsset();
                        }
                        if (scene.sceneGroup.mediaAsset) {
                            if (!isMediaSourceData(voicedrivenSourceData)) {
                                throw new Error('voicedrivenSourceData is not a MediaSourceData');
                            }
                            scene.sceneGroup.mediaAsset.sourceData = voicedrivenSourceData;
                        }
                    }
                    if (isAIAvatar) {
                        if (scene.sceneGroup.mediaAsset) {
                            scene.sceneGroup.removeMediaAsset();
                        }

                        if (sourceScene.sceneGroup.avatar.foreground) {
                            if (!scene.sceneGroup.avatar.foreground) {
                                scene.sceneGroup.avatar.addForeground();
                            }
                            if (scene.sceneGroup.avatar.foreground) {
                                scene.sceneGroup.avatar.foreground.sourceData =
                                    sourceScene.sceneGroup.avatar.foreground.sourceData;
                            }
                        }
                        if (sourceScene.sceneGroup.avatar.background) {
                            if (!scene.sceneGroup.avatar.background) {
                                scene.sceneGroup.avatar.addBackground();
                            }
                            if (scene.sceneGroup.avatar.background) {
                                scene.sceneGroup.avatar.background.sourceData =
                                    sourceScene.sceneGroup.avatar.background.sourceData;
                            }
                        }
                    }
                }
            });
        });
    },
);

const setOverlaySelectorPreviewsFancyCaptionOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sourceSceneGroupId: string,
        overlayBounds: ImmutableMap<OverlayBounds>,
        allPreviewSlides: string[],
        localLuminary: Luminary,
    ) => {
        const sourceSceneGroup = luminary.getSceneGroupById(sourceSceneGroupId);
        const sourceTranscript = sourceSceneGroup?.originalTranscript;

        if (!sourceTranscript || sourceTranscript.empty) {
            return;
        }

        const startTime = overlayBounds.get('startTime');
        const endTime = overlayBounds.get('endTime');
        if (!isFiniteNumber(startTime) || !isFiniteNumber(endTime)) {
            throw new Error('Overlay bounds are not set');
        }

        localLuminary.batchRun(() => {
            allPreviewSlides.forEach((sceneId) => {
                const scene = localLuminary.getSceneById(sceneId);
                if (!scene) {
                    return;
                }
                const voicedrivenAsset = getVoiceDrivenAsset(scene.sceneGroup);
                if (!voicedrivenAsset) {
                    return;
                }

                scene.duration = endTime - startTime;
                const sourceVoiceDrivenAssetPlaybackRegions =
                    getVoiceDrivenAssetPlaybackRegions(sourceSceneGroup);
                const sourceVoiceDrivenAssetSourceDuration =
                    getVoiceDrivenBaseAssetSourceDuration(sourceSceneGroup);
                const sourceTranscriptStartTime = translateVideoTimeToTranscriptTime(
                    startTime,
                    sourceVoiceDrivenAssetPlaybackRegions,
                    sourceVoiceDrivenAssetSourceDuration,
                    sourceSceneGroup.startTime,
                );
                const sourceTranscriptEndTime = translateVideoTimeToTranscriptTime(
                    endTime,
                    sourceVoiceDrivenAssetPlaybackRegions,
                    sourceVoiceDrivenAssetSourceDuration,
                    sourceSceneGroup.startTime,
                    {
                        includeTrailingCut: false,
                    },
                );
                scene.sceneGroup.originalTranscript.transcriptData =
                    sourceTranscript.getDataForRange(
                        sourceTranscriptStartTime,
                        sourceTranscriptEndTime,
                    );

                const targetVoiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(
                    scene.sceneGroup,
                );
                const targetPlaybackRegions = sourceVoiceDrivenAssetPlaybackRegions
                    .map((region): [number, number] | null => {
                        // only select regions that overlap with the part of the transcript that we are creating an
                        // overlay for, and don't exceed the duration of the target voice driven asset
                        let regionStart = region[0];
                        let regionEnd = region[1];
                        if (
                            theseAreOverlapping(
                                regionStart,
                                regionEnd,
                                sourceTranscriptStartTime,
                                sourceTranscriptEndTime,
                            )
                        ) {
                            regionStart = Math.max(regionStart, sourceTranscriptStartTime);
                            regionEnd = Math.min(
                                regionEnd,
                                sourceTranscriptEndTime,
                                targetVoiceDrivenAssetSourceDuration,
                            );
                            return [regionStart, regionEnd];
                        }
                        return null;
                    })
                    .filter(isNotNull);

                const cutRegions = VideoData.getCutRegions(
                    targetPlaybackRegions,
                    targetVoiceDrivenAssetSourceDuration,
                );

                // uncut everything to reset the scene
                const preExistingCutRegions = getVoiceDrivenBaseAssetCutRegions(scene.sceneGroup);
                preExistingCutRegions.forEach((region) => {
                    voicedrivenAsset.uncut(region[0], region[1]);
                });

                // then cut the new regions
                cutRegions.forEach((region) => {
                    voicedrivenAsset.cut(region[0], region[1]);
                });
            });
        });
    },
);

const setOverlaySelectorPreviewsStandardizeTalkingHeadMediaAssetOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sourceSceneId: string,
        multipleSpeakersSceneToSlideIdMap: Record<string, string>,
        faceDetections: BoundingBox[],
        localLuminary: Luminary,
        copyZoom = false,
    ) => {
        const sourceScene = luminary.getSceneById(sourceSceneId);
        if (!sourceScene) return;

        const allMultiSpeakersSlideIds = Object.values(multipleSpeakersSceneToSlideIdMap);

        localLuminary.scenes.forEach((scene) => {
            const sceneId = scene.id;
            standardizeTalkingHeadMediaAssetProperties(scene, sourceScene, copyZoom);
            if (allMultiSpeakersSlideIds.includes(sceneId)) {
                call(setUpMultipleSpeakersSceneOperation, sceneId, faceDetections, {
                    luminary: localLuminary,
                });
            }
        });
    },
);

/**
 * Splits a Scene in a SceneGroup into multiple Scenes. This is accomplished by:
 *
 * - Creating two new Scenes by cloning the original Scene twice
 * - Applying the Overlay sceneDesign to the first new Scene
 * - Trimming the Scenes so that they are have the same aggregate duration as the original Scene
 * - Deleting any Scene whose duration would be reduced to zero (for example, adding an Overlay at 0ms)
 *
 * In this process, the Overlay scene is like the cheese in the sandwhich and the original scene
 * is the bread. Depending on the length of the Overlay, the sandwhich is either a normal sandwich,
 * an open faced sandwich, or just a piece of cheese with no bread.
 *
 * Example(s):
 *
 * It: Can make a normal sandwich
 * Let: [ Scene{duration=10000} ]
 * Act: Create Overlay @ {2000, 5000}
 * Res: [ Scene{duration=2000}, Scene{duration=3000, Overlay=True}, Scene{duration=5000} ]
 *
 * It: Can make an open faced sandwich
 * Let: [ Scene{duration=10000} ]
 * Act: Create Overlay @ {0, 5000}
 * Res: [ Scene{duration=5000, Overlay=True}, Scene{duration=5000} ]
 *
 * It: Can make an inverted open faced sandwich
 * Let: [ Scene{duration=10000} ]
 * Act: Create Overlay @ {5000, 10000}
 * Res: [ Scene{duration=5000}, Scene{duration=5000, Overlay=True} ]
 *
 * It: Can make cheese
 * Let: [ Scene{duration=10000} ]
 * Act: Create Overlay @ {0, 10000}
 * Res: [ Scene{duration=10000, Overlay=True} ]
 *
 * This operation makes the assumption that when it is called, every Scene in the SceneGroup has
 * a media asset named `mediaAssetName` and that the (startTime, endTime) ranges are contiguous
 * and non-overlapping.
 *
 * As long as this operation is only called once a Talking Head scene has been created, this invariant
 * should hold true.
 *
 * This operation should only be called on a SceneGroup created by adding a Talking Head clip to
 * the storyboard and attempts to use it with SceneGroups of unrelated scenes will result in
 * undefined behaviour.
 *
 * @param sceneGroupId - the container scene group
 * @param startTimeMillis - the start time of the first word from transcript (in the transcript "timezone")
 * @param endTimeMillis - the end time of the last word from the transcript (in the transcript "timezone")
 * @param sceneDesignId - the scene design of the overlay
 * @param systemTriggered
 * @param parentActionGroup
 * @returns operation to create an overlay in-place of the base scene
 * (either create a new overlay scene, bookend-wrapping base scenes + overlay scene, or just the overlay scene and discarding the original base scene)
 */
export const createOverlayOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call, getState },
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        sceneDesignId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): string => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new CreateOverlayError('Scene Group missing');
        }

        if (luminary.isPlaying()) {
            luminary.stop();
        }

        // if a scene was programmatically created, it might still be waiting for the media asset to be loaded
        // let's wait for that media to load first because subsequent trimming can't proceed until original media
        // is loaded and duration is calculated
        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new CreateOverlayError('Voice Driven asset missing');
        }

        const voicedrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voicedrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        const startTimeVideoTime = translateTranscriptTimeToVideoTime(
            startTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const endTimeVideoTime = translateTranscriptTimeToVideoTime(
            endTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const midpoint = (startTimeVideoTime + endTimeVideoTime) / 2;
        const activeScenes = luminary.getActiveScenesAt(midpoint);
        if (activeScenes.length > 1) {
            throw new CreateOverlayError(
                'Overlapping scenes in the middle of the area we are creating a new overlay',
            );
        } else if (activeScenes.length === 0) {
            ErrorReporter.setContext('overlay creation data', {
                sceneGroupStartEndTimes: luminary.sceneGroups.map((sg) => ({
                    start: sg.startTime,
                    end: sg.endTime,
                })),
                sceneStartEndTimes: luminary.scenes.map((s) => ({
                    start: s.startTime,
                    end: s.endTime,
                })),
                startTimeMillis,
                endTimeMillis,
                sceneDesignId,
                startTimeVideoTime,
                endTimeVideoTime,
                midpoint,
            });
            throw new CreateOverlayError(
                'No scenes in the middle of the area we are creating a new overlay',
            );
        }

        const originalBaseScene = activeScenes[0];
        const sceneIndex = originalBaseScene.localIndex;
        if (!originalBaseScene) {
            throw new CreateOverlayError('Scene missing');
        }
        if (originalBaseScene.sceneGroup !== sceneGroup) {
            throw new CreateOverlayError('Scene does not belong to Scene Group');
        }

        // only voice driven compatible scene categories are valid base scenes
        if (!isVoiceDrivenCompatibleSceneCategory(originalBaseScene.sceneCategory)) {
            throw new CreateOverlayError('Cannot create overlay: scene is not a base scene');
        }

        // we'll be modifying the base scene and setting durations in-place, so let's save the original state for calculations later
        const originalBaseSceneStartTime = originalBaseScene.startTime - sceneGroup.startTime;
        const originalBaseSceneDuration = originalBaseScene.duration;

        // Disallow Overlays to be out of bounds
        const transcriptStartTimeRelativeToSceneGroup = translateTranscriptTimeToSceneGroupTime(
            startTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
        );
        const transcriptEndTimeRelativeToSceneGroup = translateTranscriptTimeToSceneGroupTime(
            endTimeMillis,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
        );

        // derive overlay times
        const overlayDuration = floatingPointSubtraction(
            transcriptEndTimeRelativeToSceneGroup,
            transcriptStartTimeRelativeToSceneGroup,
        );
        // transcript start time is relative to scene group, but we also need to account for previous overlays in the scene group
        const overlayStartTimeRelativeToBaseScene = floatingPointSubtraction(
            transcriptStartTimeRelativeToSceneGroup,
            originalBaseSceneStartTime,
        );
        // adjust endTimeMillis with asset's trim offset so we get the end time relative to the "in-place" base scene window
        const overlayEndTimeRelativeToBaseScene = floatingPointSubtraction(
            transcriptEndTimeRelativeToSceneGroup,
            originalBaseSceneStartTime,
        );

        // if newOverlay's endTime is equal or exceeds the baseScene's endTime, we will discard the right bun
        let timesAreEqual = floatingPointComparison(
            overlayEndTimeRelativeToBaseScene,
            originalBaseScene.duration,
        );
        const discardRightBaseScene =
            timesAreEqual || overlayEndTimeRelativeToBaseScene >= originalBaseScene.duration;

        // determines if we don't need the left base scene, so we're making an open-faced sandwich or just cheese
        timesAreEqual = floatingPointComparison(
            overlayStartTimeRelativeToBaseScene,
            originalBaseSceneStartTime,
        );
        const discardLeftBaseScene = timesAreEqual || overlayStartTimeRelativeToBaseScene <= 0;

        if (overlayStartTimeRelativeToBaseScene === overlayEndTimeRelativeToBaseScene) {
            throw new CreateOverlayError('Overlay must have non-zero duration');
        }

        let newOverlayScene: Slide | undefined;
        const sceneWithBaseMediaAsset = getSceneWithBaseMediaAsset(sceneGroup);

        // make sure the operations inside each createOverlay() is await-ed/completed before the next createOverlay() is called
        luminary.batchRun(() => {
            // Create the first new scene and apply the Overlay design to it
            newOverlayScene = sceneGroup.addScene({ sceneDesignId });
            makeVoicedrivenCompatibleScene(newOverlayScene);
            newOverlayScene.duration = originalBaseScene.duration; // make the new overlay the same duration as the original scene. Later, we'll trim it to the correct duration
            sceneGroup.moveSceneToIndex(newOverlayScene.id, sceneIndex + 1);
            standardizeTalkingHeadMediaAssetProperties(
                newOverlayScene,
                sceneWithBaseMediaAsset,
                false,
            );

            // Create the second new scene as a clone of the original scene
            const rightBaseScene = sceneGroup.addScene({
                sceneDesignId: originalBaseScene.sceneDesign,
            });
            sceneGroup.moveSceneToIndex(rightBaseScene.id, sceneIndex + 2);
            standardizeTalkingHeadMediaAssetProperties(
                rightBaseScene,
                sceneWithBaseMediaAsset,
                false,
            );

            call(
                replaceSlideOperation,
                rightBaseScene.id,
                originalBaseScene,
                [],
                systemTriggered,
                parentActionGroup,
                false,
            );

            // Trim the end off of the original scene (the bread)
            if (discardLeftBaseScene) {
                sceneGroup.removeScene(originalBaseScene.id);
            } else {
                originalBaseScene.duration = overlayStartTimeRelativeToBaseScene;
            }

            // Trim the beginning and end off of the second scene (the Overlay scene / cheese)
            newOverlayScene.duration = overlayDuration;

            // Trim the beginning off of the third scene (the bread)
            if (discardRightBaseScene) {
                sceneGroup.removeScene(rightBaseScene.id);
            } else {
                rightBaseScene.duration =
                    originalBaseSceneDuration - overlayEndTimeRelativeToBaseScene;
            }

            _validateCreateOverlayDurations(
                discardLeftBaseScene,
                discardRightBaseScene,
                originalBaseSceneDuration,
                originalBaseScene,
                newOverlayScene,
                rightBaseScene,
            );
        });

        // newOverlayScene must be set
        if (!newOverlayScene) {
            throw new CreateOverlayError('Overlay scene missing');
        }

        if (isTalkingHeadSceneGroup(sceneGroup)) {
            // update brightness of every media asset in the new scene if the exact same asset has been used in any other scene
            newOverlayScene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).forEach((child) => {
                // do not apply brightness to watermark asset
                if (child.name !== WATERMARK_ASSET_NAME) {
                    const sceneWithMatchingMediaAssetSourceId = sceneGroup.scenes.find((scene) => {
                        const existingSceneMediaAsset = scene.getAssetByName(child.name, {
                            deep: false,
                        }) as TimeAwareMediaAsset;
                        return (
                            existingSceneMediaAsset &&
                            existingSceneMediaAsset.sourceData &&
                            child.sourceData &&
                            child.sourceData.source_id ===
                                existingSceneMediaAsset.sourceData.source_id
                        );
                    });
                    if (sceneWithMatchingMediaAssetSourceId) {
                        const asset = sceneWithMatchingMediaAssetSourceId.getAssetByName(
                            child.name,
                            { deep: false },
                        ) as TimeAwareMediaAsset;
                        if (asset) {
                            child.brightness = asset.brightness;
                        }
                    }
                }
            });
        }

        // if in the app, select the overlay and checkpoint the video
        if (getState().app) {
            call(selectOverlayOperation, sceneGroup.id, newOverlayScene.id);
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
        return newOverlayScene.id;
    },
);

export const addOverlaysFromRecommendationsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        recommendations: GenerateRecommendedVideoResponse,
        sceneGroupId: string,
        isGlobalVideo: boolean,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        // Check if overlay generation was skipped by user
        if (getState().app && isSceneGroupLoadingSkipped(getState(), sceneGroupId)) {
            return;
        }

        await luminary.onCanPlay();

        ErrorReporter.addBreadcrumb({
            category: 'recommendation',
            message: 'Adding overlays from recommendations',
            level: 'info',
            data: {
                recommendations,
                isGlobalVideo,
            },
        });

        const voicedrivenAssetStartTime = getVoiceDrivenBaseAssetStartTime(sceneGroup);
        const numExistingOverlays = getOverlayScenesInSceneGroup(getState(), sceneGroup).length;

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('Editor not found for scene group');
        }
        const transcriptWords: TranscriptWord[] = getTranscriptWords(editor);

        editor.getEditorState().read(() => {
            luminary.batchRun(() => {
                let hitError = false;

                let currentWord: TranscriptWordNode | null =
                    $getFirstTranscriptWordShowing(voicedrivenAssetStartTime);
                if ($isSilenceNode(currentWord)) {
                    currentWord = $getNextTranscriptWordNode(currentWord, {
                        includeSilence: false,
                        includeSpaces: false,
                        includeTrimmedWords: false,
                    });
                }
                recommendations.scenes.forEach((data, sceneIndex) => {
                    if (!data) {
                        /*
                    Imagine the backend knows that it is going to recommend 4 overlays.
                    A partial overlay recommendation could be in the form:

                    [
                        {recommendation for overlay 1},
                        {recommendation for overlay 2},
                        null,  // this overlay's recommendations aren't ready yet!
                        null,  // neither are this one's
                    ]

                    So here, we aren't ready to create the overlay yet (until we have the recommendation).
                    */
                        return;
                    }
                    if (hitError) {
                        return;
                    }

                    // find the transcript words that match the recommended text
                    // the server GUARANTEES that the recommended scene's "text_words" will exactly match the
                    // transcript, so if we run into a mismatch send an error report and abort (don't try to fix)
                    // Do comparison ignoring whitespace since tokenization might be different.
                    let matchingTranscriptWords: TranscriptWordNode[] = [];
                    const matchingTextWords: string[] = [];
                    const lowerCaseRecommendationTextWords = data.text_words.map((w) =>
                        w.toLowerCase(),
                    );
                    while (!isEqual(matchingTextWords, lowerCaseRecommendationTextWords)) {
                        if (matchingTextWords.length > data.text_words.length) {
                            // uh oh, we've found a case where the text content isn't identical – abort!
                            const logObject = {
                                matchingTextWords,
                                lowerCaseRecommendationTextWords,
                                'data.text_words': data.text_words,
                                transcriptWords,
                                'recommendation text_words': recommendations.scenes.map((s) =>
                                    s === null ? null : s.text_words,
                                ),
                            };
                            ErrorReporter.capture(
                                'overlay recommendation text does not match transcript',
                                { extra: logObject },
                            );
                            hitError = true;
                            return;
                        }

                        // add word

                        if (!currentWord) {
                            const logObject = { recommendations, transcriptWords };
                            ErrorReporter.capture('ran out of transcript words', {
                                extra: logObject,
                            });
                            hitError = true;
                            return;
                        }

                        matchingTextWords.push(currentWord.getTextContent().toLowerCase());
                        matchingTranscriptWords.push(currentWord);

                        currentWord = $getNextTranscriptWordNode(currentWord, {
                            includeSilence: false,
                            includeSpaces: false,
                            includeTrimmedWords: false,
                        });
                    }

                    // if there are some silences before the first word, we want to include them in the overlay
                    const firstWord = matchingTranscriptWords[0];
                    const precedingSilences = $getSilenceNodesBefore(firstWord);
                    const isFirstScene = sceneIndex === 0;
                    // if we are at the beginning of the transcript, we want to include ALL
                    // the silences at the beginning of the transcript. Otherwise (somewhere in the middle
                    // if the transcript), we want half of them
                    const precedingSilencesToInclude = isFirstScene
                        ? precedingSilences
                        : precedingSilences.slice(-1 * Math.ceil(precedingSilences.length / 2));

                    // same for silences right after the last word
                    const lastWord = matchingTranscriptWords.at(-1)!;
                    const followingSilences = $getSilenceNodesAfter(lastWord);
                    const isLastScene = sceneIndex === recommendations.scenes.length - 1;
                    // same as above - if we are at the end of the transcript, grab all the silences.
                    // otherwise, grab half of them.
                    const followingSilencesToInclude = isLastScene
                        ? followingSilences
                        : followingSilences.slice(0, Math.floor(followingSilences.length / 2));

                    // add the silences to the matching transcript words
                    matchingTranscriptWords = [
                        ...precedingSilencesToInclude,
                        ...matchingTranscriptWords,
                        ...followingSilencesToInclude,
                    ];

                    if (sceneIndex < numExistingOverlays) {
                        // we already have an overlay created for this recommended scene, so skip it
                        currentWord = $getNextTranscriptWordNode(matchingTranscriptWords.at(-1)!, {
                            includeSilence: false,
                            includeSpaces: false,
                            includeTrimmedWords: false,
                        });
                        return;
                    }

                    const { startTime, endTime } = call(
                        $getTimeIndicatedBy,
                        matchingTranscriptWords[0],
                        matchingTranscriptWords.at(-1)!,
                        sceneGroupId,
                    );

                    // add overlay
                    const sceneDesignId = data.scene_data.scene_design;
                    const overlaySceneId = call(
                        createOverlayOperation,
                        sceneGroupId,
                        startTime,
                        endTime,
                        sceneDesignId,
                        systemTriggered,
                        parentActionGroup,
                    );
                    if (isGlobalVideo) {
                        call(selectOverlayOperation, sceneGroupId, overlaySceneId);
                    }

                    const isOverlay = true;
                    populateSceneFromCreationRecommendations({
                        luminary,
                        sceneId: overlaySceneId,
                        strategy: ImmutableFromJS(data.scene_data),
                        isOverlay,
                        isGlobalVideo,
                        dispatch,
                        call,
                    });

                    call(fetchAndSetRecommendedSceneAttributesOperation, {
                        sceneId: overlaySceneId,
                        systemTriggered,
                        parentActionGroup,
                    });

                    if (isGlobalVideo) {
                        populateSourceSegmentForRephrasedScene(overlaySceneId, data)(dispatch);

                        // must call analytics AFTER populating the recommendations for everything to be accurate
                        const reccMediaData = recommendedMediaSourceData(data.scene_data);
                        const analyticsPayload: TimingMetadata = {
                            method: 'recommended',
                            hasMedia: reccMediaData !== null,
                            sceneId: overlaySceneId,
                            sceneGroupId: sceneGroup.id,
                            sceneDesignId: sceneDesignId,
                            duration: endTime - startTime,
                            templateId: luminary.template.id,
                            totalScenesInSceneGroup: sceneGroup.scenes.length,
                            sceneGroupType: isVoiceoverSceneGroup(sceneGroup)
                                ? 'voiceover'
                                : 'talking-head',
                            creationDisplayGroupName:
                                data.scene_data.display_group_name ?? 'unknown',
                            mediaType: reccMediaData?.type ?? 'none',
                            recommendedMediaSource: reccMediaData.sourceData
                                ? getEnumKeyByEnumValue(SourceEnum, reccMediaData.sourceData.source)
                                : null,
                            isVirtualDesign:
                                data.scene_data.modifications &&
                                Object.keys(data.scene_data.modifications).length > 0,
                        };
                        analytics.track('Overlay added', analyticsPayload);

                        // every time we make an overlay, make sure the undo-redo stack is updated as well.
                        // The user could be doing other things while the overlays are being generated, so we want to make sure
                        // that the undo-redo stack is constantly updated.
                        // Here, we also don't want to wait for luminary to finish loading before we update the undo-redo stack,
                        // because we know that there won't be any conflicts between luminary and the transcript state.
                        call(
                            checkpointVideoAndTranscriptOperation,
                            sceneGroupId,
                            { isOriginalTranscript: true, waitForLuminary: false },
                            systemTriggered,
                            parentActionGroup,
                        );
                    }

                    currentWord = $getNextTranscriptWordNode(matchingTranscriptWords.at(-1)!, {
                        includeSilence: false,
                        includeSpaces: false,
                        includeTrimmedWords: false,
                    });
                });
            });
        });

        // the overlay code above has rounding in it, which can cause durations to get slightly messed up.
        // We can fix that by updating the sceneGroup's duration to match the voice driven asset's duration:
        await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });

        // can't resize text in `batchRun()` since it relies on the asset values being updated
        resizeTextToFitOnSceneForEntireVideo(luminary);
    },
);

/**
 * This method is used as part of creating a recommended "voiceover video".
 * It fetches our recommendation endpoint for a scene
 * group containing a voiceover asset/transcript.
 * NOTE: the endpoint is async and requires polling so this operation can take up to a minute.
 */
export const fetchRecommendedVoiceDrivenVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState },
        {
            words,
            language,
            narrative_thread,
            selectedBlueprintId,
            mediaCandidates,
            mediaOptions,
        }: {
            words: string[];
            language: LanguageEnum;
            narrative_thread: NarrativeThreadEnum;
            selectedBlueprintId: number | null;
            mediaCandidates?: MediaCandidates;
            mediaOptions?: MediaOptions;
        },
    ): Promise<{ data: QueuedTaskResult; request: Request; response: Response }> => {
        const blueprintId = await dispatch(
            blueprintIdIfItMatchesNarrativeThread(selectedBlueprintId, narrative_thread),
        );
        const videoId = getVideoIdOrThrow(getState());
        const body: GenerateRecommendedVideoRequest = {
            video_id: videoId,
            template_id: luminary.template.id,
            language: language,
            narrative_thread,
            transcript_words: words,
            script_segments: null, // not used for voiceover recommendations
            aspect_ratio: luminary.aspectRatio,
            blueprint_id: blueprintId !== null ? blueprintId : undefined,
            media_candidates: mediaCandidates,
            media_options: mediaOptions,
            reference_material: null, // only necessary when creating multiple videos in same session
        };
        if (blueprintId) {
            analytics.setUsingBlueprintsContext(true);
        }

        const result = await ApiService.generateVideoRecommendationCreate({ body });
        return result;
    },
);

/**
 * This method is used to create a recommended "voiceover video". It fetches our recommendation endpoint for a scene
 * group containing a voiceover asset/transcript. It then creates overlays for each segment of the transcript using
 * the server-recommended designs, asset contents, and attributes.
 * NOTE: the endpoint is async and requires polling so this operation can take up to a minute.
 */
export const fetchAndSetRecommendedVoiceDrivenVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        transcriptWords: Array<TranscriptWord>,
        language: LanguageEnum,
        sceneGroupId: string,
        narrative_thread: NarrativeThreadEnum,
        selectedBlueprintId: number | null,
        isGlobalVideo: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const words = transcriptWords.map((word) => word.word);
        const tasksResultResponse = await call(fetchRecommendedVoiceDrivenVideoOperation, {
            words,
            language,
            narrative_thread,
            selectedBlueprintId,
        });
        const optimizing = Flags.canUseAgentFamily() && !shouldSkipAgentPipeline(getState());
        await setRecommendedVoiceDrivenVideo(
            sceneGroupId,
            narrative_thread,
            selectedBlueprintId,
            tasksResultResponse,
            null,
            isGlobalVideo,
            luminary,
            dispatch,
            call,
            optimizing,
            getState,
            systemTriggered,
            parentActionGroup,
        );
        setSkipMediaOptimization({ skip: false });
    },
);

/**
 * This method is the same as fetchAndSetRecommendedVoiceDrivenVideo, but is for a scene group which already
 * has a transcript (and uses that data for the transcriptWords/language)
 */
export const refetchAndSetRecommendedVoiceDrivenVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        transcriptWords: TranscriptWord[],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        if (transcriptWords.length <= 0) {
            return;
        }
        const language = sceneGroup.captions.asset.transcript.language as LanguageEnum;
        const narrativeThread = isTalkingHeadSceneGroup(sceneGroup)
            ? NarrativeThreadEnum.TALKING_HEAD
            : NarrativeThreadEnum.VOICEOVER;
        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
            }),
        );

        // Reset skip flag at the start of voiceover overlay generation
        dispatch(setSkipMediaOptimization({ skip: false }));

        await call(
            fetchAndSetRecommendedVoiceDrivenVideoOperation,
            transcriptWords,
            language,
            sceneGroupId,
            narrativeThread,
            getSelectedBlueprintId(getState()),
            true, // isGlobalVideo
            systemTriggered,
            parentActionGroup,
        );
    },
);

/**
 * Builds captions for a scene group and sets a voiceover asset to the scene group.
 * @returns void : this is an operation that builds a voiceover scene
 */
export const buildVoiceoverSceneOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            audioAssetData,
            avatarAssetData,
            avatarBackgroundAssetData = null,
            blueprintId = null,
        }: {
            sceneGroupId: string;
            audioAssetData: ImmutableMap<AudioAssetModel> | ImmutableMap<AudioSourceData>;
            avatarAssetData: ImmutableMap<SlideBackgroundModel> | null;
            avatarBackgroundAssetData?: MediaSourceData | null;
            blueprintId?: number | null;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        luminary.batchRun(() => {
            // load the voiceoverAsset into luminary
            sceneGroup.voiceoverAsset.sourceData = audioAssetData.toJS() as AudioSourceData;

            // load the avatarAsset into luminary
            if (avatarAssetData) {
                // convert AIAvatar into MediaSourceData
                const video = avatarAssetData.get(SlideBackgroundModelTypeEnum.VIDEO);
                if (!video) throw new Error('Avatar asset is not a video');
                const sourceData = {
                    ...ImmutableToJS(video),
                    type: SlideBackgroundModelTypeEnum.VIDEO,
                } as unknown as MediaSourceData;

                if (sceneGroup.avatar) {
                    if (sceneGroup.avatar.foreground) {
                        sceneGroup.avatar.foreground.sourceData = sourceData;
                    }
                    if (sceneGroup.avatar.background && avatarBackgroundAssetData) {
                        sceneGroup.avatar.background.sourceData = avatarBackgroundAssetData;
                    }
                }
                dispatch(
                    setVideoFeedbackFormState({ isVisible: true, source: 'Creator (AI Avatar)' }),
                );
            }

            // adjust scenes after base asset is set
            void call(buildCaptionSceneOperation, { sceneGroupId, blueprintId });
        });
        await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
    },
);

/**
 * Operation to set and apply a voiceover asset to a voiceover scene group. The scene group must already have
 * added a voiceover asset. This function will not run if the scene group isn't a voiceover scene group.
 * @param sceneGroupId - voiceover scene group id
 * @param audioAssetData - Immutable Map of voiceover audio asset data. Leave undefined, if you want to use the existing asset
 * @param captionsLanguage - specified captions language (undefined = auto)
 * @param forceCaptionGeneration - set to true if you want the captions to always be generated
 * @param systemTriggered boolean
 * @param parentActionGroup uuid
 * @returns void
 */
export const setAndApplyVoiceoverAssetForSceneGroupOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            audioAssetData = null,
            avatarAssetData = null,
            avatarBackgroundAssetData = null,
            captionsLanguage = null, // specified captions language (null = auto)
            forceCaptionGeneration = false, // set to true if you want the captions to always be generated
            selectedBlueprintId = null,
            generateRecommendedVideoResponse = null,
            sceneRecommendations = null,
            isGlobalVideo = true,
            transcript = null,
            recommendOverlays = true,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: SetAndApplyVoiceoverParams,
    ) => {
        try {
            return await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData,
                avatarAssetData,
                avatarBackgroundAssetData,
                captionsLanguage,
                forceCaptionGeneration,
                selectedBlueprintId,
                generateRecommendedVideoResponse,
                sceneRecommendations,
                isGlobalVideo,
                transcript,
                recommendOverlays,
                luminary,
                dispatch,
                getState,
                call,
                systemTriggered,
                parentActionGroup,
            });
        } catch (e) {
            ErrorReporter.capture(e);
            return false;
        }
    },
);

/**
 * Operation to add a new voiceover scene group given a voiceover asset.
 * @param targetIndex - index position of where the scene group should be added to
 * @param audioAssetData - Immutable Map of voiceover asset data
 * @param systemTriggered - false
 * @param parentActionGroup - uuid
 */
export const addSceneGroupFromVoiceoverAssetOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            targetIndex = 0,
            audioAssetData = undefined,
            avatarAssetData = null,
            avatarBackgroundAssetData = null,
            deleteOtherScenes = false,
            analyticsMetadata = {},
            selectedBlueprintId = null,
            aspectRatioOverride = null,
            generateRecommendedVideoResponse = null,
            sceneRecommendations = null,
            isGlobalVideo = true,
            transcript = null,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            targetIndex?: number;
            audioAssetData?: ImmutableMap<AudioAssetModel>;
            avatarAssetData?: null | ImmutableMap<SlideBackgroundModel>;
            avatarBackgroundAssetData?: MediaSourceData | null;
            deleteOtherScenes?: boolean;
            analyticsMetadata?: Record<string, string | number | boolean>;
            selectedBlueprintId?: number | null;
            aspectRatioOverride?: AspectRatio | null;
            generateRecommendedVideoResponse?: {
                data: QueuedTaskResult;
                request: Request;
                response: Response;
            } | null;
            sceneRecommendations?: SceneRecommendation[] | null;
            isGlobalVideo: boolean;
            transcript?: AssetTranscript | null;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): Promise<AddSceneGroupInfo | undefined> => {
        updateLuminaryAspectRatio(luminary, aspectRatioOverride);
        const numNonOutroNonBrandSceneGroups = getNumNonOutroNonBrandSceneGroups(luminary);
        const { sceneGroupId } = call(
            createNewVoicedrivenSceneGroupOperation,
            VideoData.CATEGORY_VOICEOVER,
            targetIndex,
            { deleteOtherScenes, setPlaceholderAsset: false, hasAvatar: !!avatarAssetData },
        );

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene Group not found');
        }

        const hasAssetBeenApplied = await call(setAndApplyVoiceoverAssetForSceneGroupOperation, {
            sceneGroupId: sceneGroup.id,
            audioAssetData: audioAssetData || null,
            avatarAssetData,
            avatarBackgroundAssetData,
            captionsLanguage: null,
            forceCaptionGeneration: false,
            selectedBlueprintId,
            generateRecommendedVideoResponse,
            sceneRecommendations,
            recommendOverlays: true,
            isGlobalVideo,
            transcript,
            systemTriggered,
            parentActionGroup,
        });
        if (!hasAssetBeenApplied) {
            call(deleteSceneGroupOperation, sceneGroupId, systemTriggered, parentActionGroup);
            return;
        }

        if (isGlobalVideo) {
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }

        if (isGlobalVideo && !sceneRecommendations) {
            const scene = sceneGroup.scenes[0];
            if (scene) {
                analytics.track('New Scene Added', {
                    method: 'Media Drag And Drop',
                    mediaSource: 'Voice',
                    sceneId: scene.id,
                    sceneGroupId,
                    sceneDesignId: scene.sceneDesign,
                    sceneCategory: scene.sceneCategory,
                    templateId: luminary.template.id,
                    hasIcon: sceneContainsIcon(sceneGroup.scenes[0]),
                    numNonOutroNonBrandSceneGroups,
                    ...analyticsMetadata,
                });
            }
        }

        return { sceneGroupId, sceneId: sceneGroup.scenes[0].id };
    },
);

export const applyDraggedVoiceoverToSceneOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        targetSceneId: string,
        assetData: ImmutableMap<AudioAssetModel>,
        systemTriggered: boolean,
        parentActionGroupId: string,
    ) => {
        const scene = luminary.getSceneById(targetSceneId);
        if (!scene) return;
        await call(setAndApplyVoiceoverAssetForSceneGroupOperation, {
            sceneGroupId: scene.sceneGroup.id,
            audioAssetData: assetData,
            avatarAssetData: null,
            captionsLanguage: null,
            forceCaptionGeneration: false,
            selectedBlueprintId: null,
            generateRecommendedVideoResponse: null,
            sceneRecommendations: null,
            recommendOverlays: true,
            isGlobalVideo: true,
            systemTriggered,
            parentActionGroup: parentActionGroupId,
        });
    },
);

/**
 * Creates a blank scene and assigns the media data to the largest MediaAsset
 */
export const addSceneFromMediaOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        targetIndex: number,
        mediaData: ImmutableMap<SlideBackgroundModel>,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): Promise<AddSceneGroupInfo> => {
        const templateId = luminary.template.id;
        let talkingHeadDesigns, sceneGroup, scene;
        const videoMusicVolume = getVideoMusicVolume(luminary);
        const numNonOutroNonBrandSceneGroups = getNumNonOutroNonBrandSceneGroups(luminary);

        // Recommend a talking head scene if media is a video with voiceover
        if (
            mediaData.get('type') === SlideBackgroundModelTypeEnum.VIDEO &&
            mediaData.get(SlideBackgroundModelTypeEnum.VIDEO)!.get('has_voiceover')
        ) {
            talkingHeadDesigns = getTemplateSceneDesignsByCategory(
                getState(),
                templateId,
                VideoData.CATEGORY_TALKING_HEAD,
            );
        }
        if (talkingHeadDesigns && talkingHeadDesigns.size > 0) {
            const { sceneGroupId } = call(
                createNewVoicedrivenSceneGroupOperation,
                VideoData.CATEGORY_TALKING_HEAD,
                targetIndex,
                { deleteOtherScenes: false, setPlaceholderAsset: false },
                parentActionGroup,
                systemTriggered,
            );
            sceneGroup = luminary.getSceneGroupById(sceneGroupId);

            // we should only be ducking user uploaded media, not stock media
            if (
                mediaData.get(SlideBackgroundModelTypeEnum.VIDEO)?.get('source') ===
                    SourceEnum.SOURCE_USER &&
                videoMusicVolume >= (AudioVolume.DUCK as number)
            ) {
                sceneGroup.musicVolumeOverride = AudioVolume.DUCK;
            }
            scene = sceneGroup.scenes[0];

            await call(applyTalkingHeadMediaOperation, {
                sceneGroupId: sceneGroup.id,
                media: mediaData,
                language_code: undefined,
                forceCaptionGeneration: false,
                enhanceAudio: true,
                recommendOverlays: true,
                systemTriggered,
                parentActionGroup,
            });
            call(fetchAndSetRecommendedSceneAttributesOperation, {
                sceneId: scene.id,
                systemTriggered,
                parentActionGroup,
                autoScale: true,
            });
        } else {
            const sceneDesignId = getTemplateDefaultSceneDesign(getState(), templateId);
            if (!sceneDesignId) throw new Error('default scene design not found');
            sceneGroup = luminary.addSceneGroup({ sceneDesignId }).sceneGroup;
            scene = sceneGroup.scenes[0];
            // Clear media of any source
            const defaultMediaAsset = getLargestMediaAsset(scene, true);
            if (defaultMediaAsset !== null) {
                clearSourceData(defaultMediaAsset);
            }
            call(setMediaAssetForSceneOperation, {
                sceneId: scene.id,
                mediaSourceData: mediaData,
                targetAssetId: null,
                method: SetMediaAssetForSceneOperationMethod.DRAG,
                isRecommendedMedia: false,
                systemTriggered,
                parentActionGroup,
                autoScale: true,
            });

            // For AI charts: clear all text assets on the new scene
            const mediaSource = mediaData.getIn([mediaData.get('type'), 'source']);
            if (mediaSource === SourceEnum.SOURCE_AI_CHART) {
                scene.getAssets().forEach((asset) => {
                    if (VideoData.isTextAsset(asset)) {
                        asset.text = '';
                    }
                });
            }
        }
        targetIndex = getTargetIndex(luminary, targetIndex);
        luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        let mediaSource;
        switch (mediaData.getIn([mediaData.get('type'), 'source'])) {
            case SourceEnum.SOURCE_USER:
            case SourceEnum.SOURCE_USER_WATERMARK:
            case SourceEnum.SOURCE_USER_OUTRO:
                mediaSource = 'Uploads';
                break;
            case SourceEnum.SOURCE_ARTICLE:
                mediaSource = 'Article';
                break;
            default:
                mediaSource = 'Library';
                break;
        }
        analytics.track('New Scene Added', {
            method: 'Media Drag and Drop',
            mediaSource: mediaSource,
            sceneId: scene.id,
            sceneGroupId: sceneGroup.id,
            sceneDesignId: scene.sceneDesign,
            sceneCategory: scene.sceneCategory,
            templateId: luminary.template.id,
            hasIcon: sceneContainsIcon(scene),
            numNonOutroNonBrandSceneGroups,
        });

        dispatch(trackChangedTextAssetSlide({ sceneId: scene.id }));
        return { sceneId: scene.id, sceneGroupId: sceneGroup.id };
    },
);

/**
 * Applies an uploaded media to a scene group. If the recording is detected to have voiceover, a talking head scene is created.
 * @param {Promise} uploadingPromise - promise for upload process
 * @param uploadingContext - whether we are uploading a video or audio file or recording, for copy and analytics
 * @param sceneGroupId - which scene group to apply the media to
 * @returns {void} - results in a new scene created with the webcam recording as a background media.
 */
export const applyUploadingMediaToSceneGroupOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            uploadingPromise,
            uploadingContext,
            sceneGroupId,
            recommendOverlays = true,
            resetSceneGroupData = false,
            selectedBlueprintId = null,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            uploadingPromise: Promise<string[]>;
            uploadingContext: UploadingContext;
            sceneGroupId: string;
            recommendOverlays?: boolean;
            resetSceneGroupData?: boolean;
            selectedBlueprintId?: number | null;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): Promise<void> => {
        let sceneGroup = luminary.getSceneGroupById(sceneGroupId);

        const LoadingMsg: Record<UploadingContext, string> = {
            [UploadingContext.VideoRecording]: `We're uploading your recording!\n\nTrim and crop your clip using the scene controls.\n\nYou can find it again later in the ${RECORDING_FOLDER_NAME} folder in your media uploads.`,
            [UploadingContext.ScreenRecording]: `We're uploading your recording!\n\nTrim and crop your clip using the scene controls.\n\nYou can find it again later in the ${SCREEN_RECORDING_FOLDER_NAME} folder in your media uploads.`,
            [UploadingContext.VoiceRecording]: 'Uploading voiceover recording',
            [UploadingContext.VideoFileUpload]: 'Uploading video file',
            [UploadingContext.VoiceFileUpload]: 'Uploading audio file',
        };
        const SuccessMsg: Record<UploadingContext, string> = {
            [UploadingContext.VideoRecording]: `Your clip has been added to the ${RECORDING_FOLDER_NAME} folder in your media uploads.`,
            [UploadingContext.ScreenRecording]: `Your clip has been added to the ${SCREEN_RECORDING_FOLDER_NAME} folder in your media uploads.`,
            [UploadingContext.VoiceRecording]:
                'Your clip has been added to the Voice tab in uploads',
            [UploadingContext.VideoFileUpload]: 'Video file uploaded',
            [UploadingContext.VoiceFileUpload]: 'Audio file uploaded',
        };
        const trackingTitle: Record<UploadingContext, string> = {
            [UploadingContext.VideoRecording]: 'Video recording',
            [UploadingContext.ScreenRecording]: 'Screen recording',
            [UploadingContext.VoiceRecording]: 'Voice recording',
            [UploadingContext.VideoFileUpload]: 'Video upload',
            [UploadingContext.VoiceFileUpload]: 'Audio upload',
        };
        const errorMessage: Record<UploadingContext, string> = {
            [UploadingContext.VideoRecording]:
                'Sorry! There was a problem uploading your recording to Lumen5, please try recording your clip again.',
            [UploadingContext.ScreenRecording]:
                'Sorry! There was a problem uploading your recording to Lumen5, please try recording your clip again.',
            [UploadingContext.VoiceRecording]:
                'Sorry! There was a problem uploading your recording to Lumen5, please try recording again.',
            [UploadingContext.VideoFileUpload]:
                'Sorry! There was a problem uploading your video to Lumen5, please try uploading again.',
            [UploadingContext.VoiceFileUpload]:
                'Sorry! There was a problem uploading your audio to Lumen5, please try uploading again.',
        };

        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                context: { loadingMsg: LoadingMsg[uploadingContext] },
            }),
        );

        let uploadedAsset:
            | ImmutableMap<SlideBackgroundModel>
            | ImmutableMap<AudioAssetModel>
            | null = null;
        const cleanupOnError = () => {
            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
            if (sceneGroup) {
                // default scene group is a new scene group, so we want to keep track of it for tracking
                const asset =
                    uploadedAsset && !isImmutableAudioAsset(uploadedAsset) ? uploadedAsset : null;
                const newSceneGroup = call(
                    replaceSceneGroupWithDefaultAndAssignMediaOperation,
                    sceneGroupId,
                    asset,
                    false,
                    uuid(),
                );

                const scene = newSceneGroup.scenes[0];
                if (scene) {
                    const numNonOutroNonBrandSceneGroups =
                        getNumNonOutroNonBrandSceneGroups(luminary);

                    analytics.track('New Scene Added', {
                        method: trackingTitle[uploadingContext],
                        mediaSource: 'Uploads',
                        sceneId: scene.id,
                        sceneGroupId: sceneGroup.id,
                        sceneDesignId: scene.sceneDesign,
                        sceneCategory: scene.sceneCategory,
                        templateId: luminary.template.id,
                        hasIcon: sceneContainsIcon(scene),
                        numNonOutroNonBrandSceneGroups,
                    });
                }
            }

            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        };
        let assetUuid;
        let error = null;
        try {
            [assetUuid] = await uploadingPromise;
        } catch (err) {
            error = err;
        } finally {
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
        }

        if (error || !assetUuid) {
            // if assetUuid is false-y, then the upload failed (can happen if uploading a pdf, for example)
            Toast.error(errorMessage[uploadingContext]);
            cleanupOnError();
            return;
        }

        Toast.success(SuccessMsg[uploadingContext], 'Uploaded successfully');

        // re-retrieve the redux state and the scene group, after having waited for the uploadingPromise
        const newState = getState();
        sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            cleanupOnError();
            return; // must've been deleted while we were await'ing
        }

        uploadedAsset = getUploadedAssetByUuid(newState, assetUuid);
        if (!uploadedAsset) {
            cleanupOnError();
            return; // maybe the upload failed or something
        }

        if (resetSceneGroupData) {
            call(
                deleteAllOverlaysFromSceneGroupOperation,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
            if (sceneGroup.voiceoverAsset?.sourceData) {
                luminary.batchRun(() => {
                    sceneGroup.voiceoverAsset.sourceData = VOICEOVER_PLACEHOLDER_ASSET_SOURCE;
                });
            } else if (sceneGroup.mediaAsset?.sourceData) {
                luminary.batchRun(() => {
                    sceneGroup.mediaAsset.sourceData =
                        TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA;
                });
            }
        }

        // Recommend a talking head scene if media is a video with voiceover
        if (isImmutableAudioAsset(uploadedAsset)) {
            if (uploadedAsset.get('has_voiceover')) {
                await call(setAndApplyVoiceoverAssetForSceneGroupOperation, {
                    sceneGroupId: sceneGroup.id,
                    audioAssetData: uploadedAsset,
                    avatarAssetData: null,
                    captionsLanguage: null,
                    forceCaptionGeneration: false,
                    selectedBlueprintId,
                    generateRecommendedVideoResponse: null,
                    sceneRecommendations: null,
                    isGlobalVideo: true,
                    recommendOverlays,
                    systemTriggered,
                    parentActionGroup,
                });
            } else {
                cleanupOnError();
                return;
            }
        } else if (
            immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset) &&
            uploadedAsset.get(SlideBackgroundModelTypeEnum.VIDEO).get('has_voiceover') === true
        ) {
            const languageCode: string | null = null;
            const forceCaptionGeneration = false;
            const enhanceAudio = true;
            await call(applyTalkingHeadMediaOperation, {
                sceneGroupId,
                media: uploadedAsset,
                language_code: languageCode,
                forceCaptionGeneration,
                enhanceAudio,
                recommendOverlays,
                blueprintId: selectedBlueprintId,
                systemTriggered: true,
            });
        } else if (
            immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset) &&
            uploadedAsset.get(SlideBackgroundModelTypeEnum.VIDEO).get('has_voiceover') !== true
        ) {
            call(
                replaceSceneGroupWithDefaultAndAssignMediaOperation,
                sceneGroupId,
                uploadedAsset,
                true,
                parentActionGroup,
            );
        } else {
            cleanupOnError();
            return;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        const scene = sceneGroup.scenes[0];
        if (scene) {
            const numNonOutroNonBrandSceneGroups = getNumNonOutroNonBrandSceneGroups(luminary);
            analytics.track('New Scene Added', {
                method: trackingTitle[uploadingContext],
                mediaSource: 'Uploads',
                sceneId: scene.id,
                sceneGroupId: sceneGroup.id,
                sceneDesignId: scene.sceneDesign,
                sceneCategory: scene.sceneCategory,
                templateId: luminary.template.id,
                hasIcon: sceneContainsIcon(scene),
                numNonOutroNonBrandSceneGroups,
            });
        }
    },
);

/**
 * Creates a new text-on-media scene from text after a target scene group
 */
export const recommendNewSceneFromTextOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        text: string,
        language: LanguageEnum | null,
        creationMethod: string,
        targetSceneGroupId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const targetSceneGroup = luminary.getSceneGroupById(targetSceneGroupId);
        let newSceneGroupIndex: number | null = null;
        if (targetSceneGroup) {
            newSceneGroupIndex = targetSceneGroup.index + 1;
        }
        // Recommend new text on media scene after the target scene's scene group
        await call(fetchAndSetRecommendedTextOnMediaVideoOperation, {
            scriptSegments: [text],
            language,
            creationMethod,
            deleteOtherScenes: false,
            targetIndex: newSceneGroupIndex,
            blueprintId: getSelectedBlueprintId(getState()),
            systemTriggered,
            parentActionGroup,
        });

        // Get newly created scene, make it active and scroll to it
        if (newSceneGroupIndex === null) {
            newSceneGroupIndex = luminary.sceneGroups.length - 1; // added to the end
        }
        const newSceneGroup = luminary.sceneGroups[newSceneGroupIndex];
        if (newSceneGroup && newSceneGroup.scenes[0]) {
            dispatch(setFocusedSceneAndScroll(newSceneGroup.scenes[0].id, newSceneGroup.id, 0));
        }
    },
);

/**
 * This method is used to create a recommended "text-on-media video". It fetches our recommendation endpoint for a
 * provided script (set of sentences). It then creates scenes for each segment of the script using
 * the server-recommended designs, asset contents, and attributes.
 * NOTE: the endpoint is async and requires polling so this operation can take up to a minute.
 */
export const fetchAndSetRecommendedTextOnMediaVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            scriptSegments,
            language,
            creationMethod,
            deleteOtherScenes = false,
            targetIndex = null,
            blueprintId = null,
            aspectRatioOverride = null,
            mediaCandidates = null,
            mediaOptions = null,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            scriptSegments: string[];
            language: LanguageEnum | null;
            creationMethod: string; // `method` value to be passed to the "New Slide Added" tracking event
            deleteOtherScenes?: boolean;
            targetIndex?: number | null;
            blueprintId?: number | null;
            aspectRatioOverride?: AspectRatio | null;
            mediaCandidates?: MediaCandidates | null;
            mediaOptions?: MediaOptions | null;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        dispatch(setIsStoryboardGenerating({ isGenerating: true }));
        if (deleteOtherScenes) {
            dispatch(setTextOnMediaGenerating({ isGenerating: true }));
        }

        // Reset skip flag at the start of the operation
        dispatch(setSkipMediaOptimization({ skip: false }));

        await waitUntilTemplateBundleLoaded(luminary);
        const templateId = luminary.template.id;
        const defaultSceneDesignId = getTemplateDefaultSceneDesign(getState(), templateId);
        if (!defaultSceneDesignId) throw new Error('default scene design not found');

        updateLuminaryAspectRatio(luminary, aspectRatioOverride);

        targetIndex = getTargetIndex(luminary, targetIndex);

        const placeholderSceneGroupIdPerScriptSegment = createTextOnMediaPlaceholderScenes(
            luminary,
            scriptSegments,
            defaultSceneDesignId,
            targetIndex,
        );

        // mark all placeholder scenes as loading
        // iterate with index to ensure order is maintained
        const sceneGroupIds = Object.values(placeholderSceneGroupIdPerScriptSegment);
        for (const placeholderSceneGroupId of Object.values(
            placeholderSceneGroupIdPerScriptSegment,
        )) {
            if (placeholderSceneGroupId) {
                dispatch(
                    markSceneGroupAsLoading({
                        sceneGroupId: placeholderSceneGroupId,
                        type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                        context: { loadingMsg: deleteOtherScenes ? '' : 'Creating scene...' },
                    }),
                );
            }
        }

        // delete all other scenes if requested
        if (deleteOtherScenes) {
            clearScenesExceptCategories({
                luminary,
                // preserve the placeholder scenes we just created
                sceneGroupIdsToPreserve: Object.values(
                    placeholderSceneGroupIdPerScriptSegment,
                ).filter((id) => id !== null),
                preserveOutroScenes: true,
            });
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        const musicRecommendationMode = selectMusicRecommendationMode(getState());
        void fetchAndSetRecommendMusic(luminary, musicRecommendationMode);

        // check that the blueprint (if set) matches the narrative thread
        if (blueprintId !== null) {
            blueprintId = await dispatch(
                blueprintIdIfItMatchesNarrativeThread(blueprintId, NarrativeThreadEnum.TEXT),
            );
        }

        // fetch recommendations
        const videoId = getVideoIdOrThrow(getState());
        let tasksResultResponse;
        let generateRecommendedVideoResponse: GenerateRecommendedVideoResponse;
        try {
            const body: GenerateRecommendedVideoRequest = {
                video_id: videoId,
                template_id: luminary.template.id,
                language: language,
                narrative_thread: NarrativeThreadEnum.TEXT,
                script_segments: scriptSegments,
                transcript_words: null, // not used for text on media recommendations,
                aspect_ratio: luminary.aspectRatio,
                blueprint_id: blueprintId !== null ? blueprintId : undefined,
                media_candidates: mediaCandidates !== null ? mediaCandidates : undefined,
                media_options: mediaOptions !== null ? mediaOptions : undefined,
                reference_material: null, // only necessary when creating multiple videos in same session
            };
            if (blueprintId) {
                analytics.setUsingBlueprintsContext(true);
            }
            const optimizing =
                deleteOtherScenes &&
                Flags.canUseImproveMediaAgent() &&
                !shouldSkipAgentPipeline(getState());
            tasksResultResponse = await ApiService.generateVideoRecommendationCreate({ body });
            const ws = WebSocketClient.getInstance();
            generateRecommendedVideoResponse =
                await ws.waitForMessage<GenerateRecommendedVideoResponse>(
                    tasksResultResponse.data.task_id,
                    TaskResultMessageTypeEnum.TASK_RESULT,
                    'generate_recommended_video',
                    {
                        partialResponseCallback: (
                            partialResult: GenerateRecommendedVideoResponse,
                        ) => {
                            const newIds = addScenesFromRecommendations(
                                partialResult.scenes,
                                luminary,
                                dispatch,
                                placeholderSceneGroupIdPerScriptSegment,
                                defaultSceneDesignId,
                                creationMethod,
                                call,
                                optimizing,
                                true,
                                systemTriggered,
                                parentActionGroup,
                            );
                            sceneGroupIds.push(...newIds);
                        },
                    },
                );
            const finalNewIds = addScenesFromRecommendations(
                generateRecommendedVideoResponse.scenes,
                luminary,
                dispatch,
                placeholderSceneGroupIdPerScriptSegment,
                defaultSceneDesignId,
                creationMethod,
                call,
                optimizing,
                true,
                systemTriggered,
                parentActionGroup,
            );
            sceneGroupIds.push(...finalNewIds);
            dispatch(setRecommendedVideoResponse({ response: generateRecommendedVideoResponse }));
            dispatch(refreshRecommendedAssetsAfterLoadingRecommendedVideo());
        } catch (pollError: unknown) {
            ErrorReporter.capture(pollError);
            Toast.error('Sorry, something went wrong while generating scene recommendations.');
            analytics.track('Error generating scenes', {
                error: pollError,
                videoId: getVideoId(getState()),
                task_id: tasksResultResponse?.data?.task_id,
            });
        } finally {
            // Always set recommendations completed flag
            dispatch(setRecommendationsCompleted({ completed: true }));

            // If user hasn't skipped, run agent pipeline
            if (
                deleteOtherScenes &&
                Flags.canUseAgentFamily() &&
                !shouldSkipAgentPipeline(getState())
            ) {
                const { createBlueprintAgentOperation, createImproveMediaAgentOperation } =
                    await import('js/src/agent/agentOperations'); // dynamic import to avoid circular dependency
                const agentPipeline: AgentTaskPipeline = new AgentTaskPipeline([
                    ...(Flags.canUseImproveMediaAgent()
                        ? [
                              createImproveMediaAgentOperation(NarrativeThreadEnum.TEXT)(
                                  luminary,
                                  dispatch,
                                  getState,
                              ),
                          ]
                        : []),
                    ...(Flags.canUseBlueprintAgent()
                        ? [createBlueprintAgentOperation()(luminary, dispatch, getState)]
                        : []),
                ]);
                try {
                    setAgentPipeline(agentPipeline);
                    await agentPipeline.run();
                } catch (error) {
                    agentPipeline.cancel();
                    ErrorReporter.capture(error);
                } finally {
                    setAgentPipeline(null);
                }
            }

            // Only clear loading states if media optimizer is not triggered
            dispatch(setIsStoryboardGenerating({ isGenerating: false }));
            dispatch(setTextOnMediaGenerating({ isGenerating: false }));

            if (scriptSegments.length > 1 && !Cookies.get(COOKIE_KEYS.FEEDBACK_TEXT_AI_SHOWN)) {
                dispatch(
                    setVideoFeedbackFormState({
                        isVisible: true,
                        source: `Creator (${creationMethod})`,
                    }),
                );
                Cookies.set(COOKIE_KEYS.FEEDBACK_TEXT_AI_SHOWN, 'true', { expires: 365 });
            } else if (blueprintId) {
                dispatch(
                    setVideoFeedbackFormState({
                        isVisible: true,
                        source: `Creator (${NarrativeThreadEnum.TEXT} Blueprints)`,
                    }),
                );
            }

            const script = scriptSegments.join('\n');
            dispatch(fetchRecommendedSearchTerms(script, false));

            // clear any remaining loading states
            for (const placeholderSceneGroupId of sceneGroupIds) {
                if (placeholderSceneGroupId) {
                    dispatch(
                        markSceneGroupAsFinishedLoading({
                            sceneGroupId: placeholderSceneGroupId,
                        }),
                    );
                }
            }
            setSkipMediaOptimization({ skip: false });
        }
    },
);

export const fetchAsyncRecommendedTextOnMediaVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        taskId: string,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        try {
            // must use polling for this since the video is generated asynchronously and not pushed to socket
            const generateRecommendedVideoResponse =
                await PollAsyncTask.poll_for_result<GenerateRecommendedVideoResponse>(
                    taskId,
                    'generate_recommended_video',
                );
            call(
                setRecommendedTextOnMediaVideoOperation,
                generateRecommendedVideoResponse.scenes,
                'async',
                systemTriggered,
                parentActionGroup,
            );
            dispatch(setRecommendedVideoResponse({ response: generateRecommendedVideoResponse }));
            dispatch(refreshRecommendedAssetsAfterLoadingRecommendedVideo());
            Toast.removeAll();
        } catch (pollError: unknown) {
            ErrorReporter.capture(pollError);
            Toast.error('Sorry, something went wrong while generating scene recommendations.');
            analytics.track('Error generating scenes', {
                error: pollError,
                videoId: getVideoId(getState()),
                task_id: taskId,
            });
        }
    },
);

export const applyAgentBlueprintBasedImprovementsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        if (!Flags.canUseBlueprintAgent()) {
            return;
        }
        const blueprintId = getSelectedBlueprintId(getState());
        if (!blueprintId) {
            return;
        }
        const sceneGroupIds = luminary.sceneGroups.map((sceneGroup) => sceneGroup.id);
        sceneGroupIds.forEach((sceneGroupId) => {
            if (sceneGroupId) {
                dispatch(
                    markSceneGroupAsLoading({
                        sceneGroupId,
                        type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                        context: { loadingMsg: LOADING_SCENE_MSG.APPLYING_BLUEPRINT },
                    }),
                );
            }
        });
        try {
            const blueprint = await dispatch(getOrFetchBlueprint(blueprintId));
            if (!blueprint || !blueprint.video_ids || blueprint.video_ids.length === 0) {
                throw new Error('No video IDs found for blueprint');
            }
            const blueprintVideoId = blueprint.video_ids[0].toString();
            await call(
                applyAgentVideoBasedImprovementsOperation,
                blueprintVideoId,
                systemTriggered,
                parentActionGroup,
            );
        } finally {
            sceneGroupIds.forEach((sceneGroupId) => {
                if (sceneGroupId) {
                    dispatch(
                        markSceneGroupAsFinishedLoading({
                            sceneGroupId,
                        }),
                    );
                }
            });
        }
    },
);

export const applyAgentVideoBasedImprovementsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState },
        referenceVideoId = null,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!referenceVideoId) {
            return;
        }
        //  Get description of reference video
        const referenceVideoResponse = await ApiService.videoRetrieve({
            path: { video_id: referenceVideoId },
        });
        const referenceVideoData = referenceVideoResponse.data.video_data;
        const videoDescription = await generateVideoDescriptionFromVideoData(referenceVideoData);

        //  Create agent with necessary tools for pacing improvements
        const model = Model.GPT_4_1;
        const { BlueprintRootAgent } = await import('js/src/agent/root-agents/BlueprintRootAgent');
        const agent = new BlueprintRootAgent({ luminary, dispatch, model, getState });
        const improvementPrompt = `Make the necessary changes to match the pacing of the following video:
${videoDescription}

Match pacing style—apply rapid narrative where reference builds energy, add slower reflection where reference slows, distribute transitions and emotional arcs across all available scenes as best fits the new structure.

When reducing scene duration, prioritize keeping text legible and impactful. Avoid cutting scenes too short for viewers to read and absorb the message.`;

        const tracer = trace.getTracer('lumen5-luminary-operations');
        await new Promise<void>((resolve, reject) => {
            tracer.startActiveSpan('send-improvement-message', (span) => {
                const spanCtx = trace.setSpan(context.active(), span);
                context.with(spanCtx, async () => {
                    try {
                        const result = await agent.sendMessage(improvementPrompt);
                        if (!result.isSuccess) {
                            const error = new Error(
                                `Reference video-based improvement failed: ${result.result}`,
                            );
                            span.recordException(error);
                            reject(error);
                        } else {
                            resolve();
                        }
                    } catch (e) {
                        if (e instanceof Error) {
                            span.recordException(e);
                        }
                        reject(e);
                    } finally {
                        span.end();
                    }
                });
            });
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const fetchAsyncRecommendedVoiceDrivenVideoOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        taskId: string,
        sceneGroupId: string,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        try {
            // must use polling for this since the video is generated asynchronously and not pushed to socket
            const generateRecommendedVideoResponse =
                await PollAsyncTask.poll_for_result<GenerateRecommendedVideoResponse>(
                    taskId,
                    'generate_recommended_video',
                );
            await call(
                addOverlaysFromRecommendationsOperation,
                generateRecommendedVideoResponse,
                sceneGroupId,
                true,
                systemTriggered,
                parentActionGroup,
            );
            dispatch(setRecommendedVideoResponse({ response: generateRecommendedVideoResponse }));
            dispatch(refreshRecommendedAssetsAfterLoadingRecommendedVideo());
            Toast.removeAll();
        } catch (pollError: unknown) {
            ErrorReporter.capture(pollError);
            Toast.error('Sorry, something went wrong while generating scene recommendations.');
            analytics.track('Error generating scenes', {
                error: pollError,
                videoId: getVideoId(getState()),
                task_id: taskId,
            });
        }
    },
);

export const setRecommendedTextOnMediaVideoOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneRecommendations: SceneRecommendation[],
        creationMethod: string, // `method` value to be passed to the "New Slide Added" tracking event
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const defaultSceneDesign = getTemplateDefaultSceneDesign(getState(), luminary.template.id);
        if (!defaultSceneDesign) throw new Error('default scene design not found');

        const sceneGroupIdsToDestroy = luminary.sceneGroups.map((sceneGroup) => sceneGroup.id);

        try {
            addScenesFromRecommendations(
                sceneRecommendations,
                luminary,
                dispatch,
                {},
                defaultSceneDesign,
                creationMethod,
                call,
                false, // not optimizing
                true,
                systemTriggered,
                parentActionGroup,
            );
        } finally {
            // remove any placeholder scenes
            sceneGroupIdsToDestroy.forEach((sceneGroupId) => {
                luminary.removeSceneGroup(sceneGroupId);
            });

            const scriptSegments = sceneRecommendations.map((scene) => scene.scene_data.segment);
            const script = scriptSegments.join('\n');
            dispatch(fetchRecommendedSearchTerms(script, false));

            // If no music has been set, recommend music
            if (luminary.music === null) {
                const musicRecommendationMode = selectMusicRecommendationMode(getState());
                void fetchAndSetRecommendMusic(luminary, musicRecommendationMode);
            }
        }
    },
);

export const resetTemplateColorShuffleData = createLuminaryOperation(
    ({ luminary, dispatch }, systemTriggered = false, parentActionGroup = uuid()) => {
        // Reset template color shuffle data
        const colorShufflePalettes = generateBestColorPalettePermutationsFromLuminary({ luminary });
        dispatch(
            setTemplateColorShufflePalettes({
                palettes: colorShufflePalettes,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTemplateColorShuffleIndex({
                colorShuffleIndex: 0,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        color: number,
        index: number,
        alpha: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        // send analytics event
        analytics.track('Changed color', { index: index });
        // update the template color
        luminary.template.setColorAtIndex(color, index, alpha);

        // Reset/update template color shuffle data since colors changed
        call(resetTemplateColorShuffleData, systemTriggered, parentActionGroup);

        // checkpoint video
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const addNewSubsceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        index: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        scene.addSubscene(index);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const enableOutroSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Outro Enabled');
        const outroSceneDesignId = getTemplateDefaultOutroSceneDesign(
            getState(),
            luminary.template.id,
        );
        if (!outroSceneDesignId) throw new Error('default outro scene design not found');
        luminary.enableOutroSceneGroup({ sceneDesignId: outroSceneDesignId });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // ensure outro has no music if it has media with volume
        call(ensureOutroWithVolumeHasNoMusicOperation, systemTriggered, parentActionGroup);

        const outroSceneGroup = luminary.getOutroSceneGroup();
        const outroSlideId = outroSceneGroup?.scene?.id;
        if (outroSlideId) {
            window.setTimeout(() => {
                dispatch(
                    setFocusedSceneInfo({
                        sceneId: outroSlideId,
                        sceneGroupId: outroSceneGroup.sceneGroup.id,
                    }),
                );
                FocusAndScroll.scrollToSlide(outroSlideId);
            }, 100);
        }
    },
);

export const disableOutroSceneGroupOperation = createLuminaryOperation(
    ({ luminary, dispatch }, systemTriggered = false, parentActionGroup = uuid()) => {
        analytics.track('Media Step: Outro Disabled');
        luminary.disableOutroSceneGroup();
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const deleteSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const sceneGroupNumber = sceneGroup.index + 1;
        let nextSceneGroupId;
        if (sceneGroupNumber < luminary.sceneGroups.length) {
            nextSceneGroupId = luminary.sceneGroups[sceneGroupNumber].id;
        }
        const nextSceneGroup = nextSceneGroupId
            ? luminary.getSceneGroupById(nextSceneGroupId)
            : null;

        // check that there is at least one more "ordinary" or "talking_head" scene that will remain
        // after this scene is deleted, otherwise error!
        const sceneCategoriesMinOneOf: SceneCategory[] = [
            VideoData.CATEGORY_ORDINARY,
            VideoData.CATEGORY_TALKING_HEAD,
            VideoData.CATEGORY_TALKING_HEAD_INSERT,
            VideoData.CATEGORY_VOICEOVER,
        ];
        if (sceneCategoriesMinOneOf.includes(getSceneGroupCategory(sceneGroup))) {
            const firstRemainingScene = luminary.sceneGroups.find(
                (g) =>
                    sceneCategoriesMinOneOf.includes(getSceneGroupCategory(g)) &&
                    g.id !== sceneGroup.id,
            );
            if (firstRemainingScene === undefined) {
                throw new SlideDeleteError('not_enough_scenes');
            }
        }

        // get some data, for analytics purposes
        const deletedScenes = sceneGroup.scenes.map((scene) => {
            return {
                sceneId: scene.id,
                sceneDesignId: scene.sceneDesign,
                sceneCategory: scene.sceneCategory,
                hasIcon: sceneContainsIcon(scene),
            };
        });
        const sceneGroupAnalyticsData = {
            isVoicedriven: isVoiceDrivenSceneGroup(sceneGroup),
            isVoiceover: isVoiceoverSceneGroup(sceneGroup),
            isTalkingHead: isTalkingHeadSceneGroup(sceneGroup),
            isAIVoiceover: isAIVoiceoverSceneGroup(sceneGroup),
            sceneGroupId: sceneGroup.id,
            template: luminary.template.id,
        };

        // actually delete the scene group
        luminary.removeSceneGroup(sceneGroupId);
        deletedScenes.forEach(({ sceneId }) => {
            dispatch(clearTextAssetTrackedSlide({ sceneId }));
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(deleteTranscript({ sceneGroupId, systemTriggered, parentActionGroup }));
        dispatch(
            updateFocusedSceneInfoOnDelete(
                deletedScenes[0].sceneId,
                nextSceneGroup?.scenes?.[0]?.id,
                nextSceneGroup?.id,
            ),
        );

        // track analytics
        analytics.track('Scene Group Deleted', sceneGroupAnalyticsData);
        deletedScenes.forEach(({ sceneId, sceneDesignId, sceneCategory, hasIcon }) => {
            analytics.track('Slide Deleted', {
                sceneId,
                sceneGroupId,
                sceneDesignId,
                sceneCategory,
                templateId: luminary.template.id,
                hasIcon,
            });
        });
    },
);

export const setVideoHeightOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        height: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.videoHeight = height;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setVideoTransitionsEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        enabled: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.template.transitionsEnabled = enabled;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const toggleSceneTransitionEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }
        scene.transitionEnabled = !scene.transitionEnabled;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const toggleSceneMorphEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }
        scene.morphEnabled = !scene.morphEnabled;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setPaceMultiplierOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        multiplier: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.paceMultiplier = multiplier;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        luminary.scenes.forEach((scene) =>
            call(
                calculateAndSetRecommendedSceneDurationOperation,
                scene.id,
                systemTriggered,
                parentActionGroup,
            ),
        );
    },
);

export const moveSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        targetIndex: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const currentIndex = sceneGroup.index;
        const indexChange = targetIndex - currentIndex;
        if (indexChange < 0 || SharedLeftControlHandlers.canMoveDown({ sceneGroup })) {
            analytics.track('Media Step: Scene Groups Reordered', { sceneGroupId });
            luminary.moveSceneGroupToIndex(sceneGroupId, targetIndex);
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

export const setWatermarkOpacityOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        opacity: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Watermark Opacity Changed');
        luminary.template.watermark.opacity = opacity / 100;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setWatermarkPositionOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        position: WatermarkPositions,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Watermark Position Changed');
        luminary.template.watermark.position = position;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 *
 * Set the primary luminary template watermark.
 *
 */

export const setWatermarkOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: { luminary: Luminary; dispatch: AppDispatch },
        index: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Watermark Changed');
        luminary.template.watermark.sourceData = VideoData.templateWatermarkSourcesByIndex(index);
        luminary.template.watermark.visible = true;

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const addWatermarkOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        settings: MediaSourceData,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Watermark Changed');
        const newWatermarkIndex = luminary.template.watermarkSources.length;
        const watermarkSource = { sourceData: { ...settings } };

        luminary.template.addWatermarkSource(watermarkSource);
        call(setWatermarkOperation, newWatermarkIndex, systemTriggered, parentActionGroup);
        luminary.template.watermark.visible = true;

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const deleteWatermarkOperation = createLuminaryOperation(
    (
        { luminary, call },
        index: number,
        isDefaultWatermark = false,
        isBrandKitSelection = false,
    ) => {
        if (isBrandKitSelection === false) {
            analytics.track('Media Step: Watermark deleted');
        }
        // if we are deleting the default watermark in the style tab then we should swap to another watermark if one is available
        if (isDefaultWatermark && luminary.template.watermarkSources.length > 1) {
            // use next watermark in the watermarkSources, if it does not exist then use the previous one
            const newDefaultWatermarkIndex = luminary.template.watermarkSources[index + 1]
                ? index + 1
                : index - 1;
            call(setWatermarkOperation, newDefaultWatermarkIndex);
        }
        luminary.template.removeWatermarkSourceAtIndex(index);
    },
);

export const setWatermarkVisibilityOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        visible: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Watermark Visibility Changed');

        luminary.batchRun(() => {
            luminary.template.watermark.visible = visible;
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const removeWatermarkOperation = createLuminaryOperation(
    ({ luminary, dispatch }, systemTriggered = false, parentActionGroup = uuid()) => {
        analytics.track('Media Step: Watermark Removed');
        luminary.template.watermark.sourceData = VideoData.WATERMARK_NONE;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setColorPaletteReferenceOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        name: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Color Palette Selected', { theme: luminary.template.id, palette: name });

        const colorPalettes = getTemplateColorPalettes(getState(), luminary.template.id);
        const palette = colorPalettes.find((palette) => palette.get('name') === name);
        if (!palette) {
            throw new Error(`Palette with name ${name} not found`);
        }
        const paletteColors = ImmutableToJS(palette.get('colors'));
        const videoColors = luminary.template.colors;
        if (paletteColors.length < videoColors.length) {
            // The video could be made when the template had N colors and then
            // the template was changed to have N-1 colors.
            // If we just set the colors to the new palette, then there could
            // be references to the Nth color (non-existent), which would cause
            // an error.
            // Safest is to add more colors to the palette to make its length
            // equal to video colors.
            // However, the backend checks if all video colors exist in palette
            // for free users. So we are duplicating the last palette color to
            // fill in for the missing colors.
            const lastColor = paletteColors[paletteColors.length - 1];
            while (paletteColors.length < videoColors.length) {
                paletteColors.push(lastColor);
            }
        }
        luminary.template.colors = paletteColors;

        // Reset/update template color shuffle data since colors changed
        call(resetTemplateColorShuffleData, systemTriggered, parentActionGroup);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setColorPaletteOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        colors: TemplateColor[],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.template.colors = colors;

        // Reset/update template color shuffle data since colors changed
        call(resetTemplateColorShuffleData, systemTriggered, parentActionGroup);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setFontUrlOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        fontIndex: number,
        fontUrl: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Font Family Changed', { index: fontIndex });
        luminary.template.setFontAtIndex(fontUrl, fontIndex);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setTemplateFontsOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        fonts: string[],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        analytics.track('Media Step: Template Fonts Changed', { fontCount: fonts.length });
        // check that the number of fonts is at least the current template's font count
        if (luminary.template.fonts.length > fonts.length) {
            throw new Error('Cannot set fewer fonts than current template font count');
        }
        luminary.template.fonts = fonts;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const updateVideoTextDirectionOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }, systemTriggered = true, parentActionGroup = uuid()) => {
        // get the text direction
        const videoLanguage = getVideoLanguage(getState());
        const supportedLanguages = getSupportedLanguages(getState());
        const language = supportedLanguages.find((supportedLanguage) => {
            return supportedLanguage.get('code') === videoLanguage;
        });
        if (!language) {
            return;
        }
        const textDirection = language.get('text_direction');

        // set text direction
        if (textDirection !== luminary.textDirection) {
            luminary.textDirection = textDirection;
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

export const setVideoAspectRatioAndUpdateRecommendationsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        aspectRatio: AspectRatio,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        await waitUntilTemplateBundleLoaded(luminary);

        const oldAspectRatio = luminary.aspectRatio;
        if (oldAspectRatio === aspectRatio) {
            return;
        }

        // track how long it takes for all the scene design previews to be updated
        // when aspectRatio is changed
        const activityId = uuid();
        ActivityProfiler.start(activityId);

        const assetCustomTiming = luminary.scenes.flatMap((scene) => {
            const sceneId = scene.id;
            const assetsWithUserOverridenTimes = getAssetsWithTimesOverridden(getState(), sceneId);
            return assetsWithUserOverridenTimes
                .map((assetId) => {
                    const asset = scene.getAssetById(assetId);
                    if (!asset) {
                        return null;
                    }
                    return {
                        sceneId,
                        assetName: asset.name,
                        introAnimationOffset: asset.introAnimationOffset,
                        outroAnimationOffset: asset.outroAnimationOffset,
                    };
                })
                .filter(isNotNull);
        });

        // when the aspect ratio is changed, all the asset ids are also changed.
        // that means that the asset ids stored in the user overrides are no longer valid.
        dispatch(clearAssetTimingOverrides());

        // do not put aspectRatio update inside the batchRun. The asset ids are re-generated
        // after the batchRun ends, so we want them to be updated before we try to use them.
        luminary.aspectRatio = aspectRatio;

        luminary.batchRun(() => {
            luminary.scenes.forEach((scene) => {
                dispatch(
                    setTextPositionUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
                dispatch(
                    setTextSizeUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
                dispatch(
                    setTextAlignmentUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
                dispatch(
                    setCropUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered,
                        parentActionGroup,
                    }),
                );

                // resize text assets to fit all text
                scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((textAsset) => {
                    textAsset.setFontSizeToFitAllText();
                });

                // restore asset custom timing
                assetCustomTiming.forEach((assetCustomTiming) => {
                    if (assetCustomTiming.sceneId !== scene.id) {
                        return;
                    }
                    const asset = scene.getAssetByName(assetCustomTiming.assetName);
                    if (!asset) {
                        return;
                    }
                    asset.introAnimationOffset = assetCustomTiming.introAnimationOffset;
                    asset.outroAnimationOffset = assetCustomTiming.outroAnimationOffset;
                    dispatch(
                        setAssetTimingOverride({
                            sceneId: scene.id,
                            assetId: asset.id,
                            isOverridden: true,
                            systemTriggered,
                            parentActionGroup,
                        }),
                    );
                });

                luminary.sceneGroups.forEach((sceneGroup) => {
                    if (isVoiceDrivenSceneGroup(sceneGroup)) {
                        sceneGroup.scenes.forEach((scene) => {
                            makeVoicedrivenCompatibleScene(scene);
                        });
                    }
                });
            });
        });

        await call(fetchAndSetRecommendedSceneAttributesForMultipleScenesOperation, {
            sceneIds: luminary.scenes.map((scene) => scene.id),
            systemTriggered,
            parentActionGroup,
        });

        luminary
            .onAllSlideImagesRendered()
            .then(() => {
                // mark the tracking as complete when all scene images have finished rendering
                const metricLabels = METRIC_CHANGE_ASPECT_RATIO.getLabels(luminary.scenes.length);
                ActivityProfiler.end(activityId, METRIC_CHANGE_ASPECT_RATIO.name, metricLabels);
            })
            .catch();

        analytics.track('Aspect Ratio Selected', { aspectRatio: aspectRatio });
    },
);

export const deleteSubsceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        sceneId: string,
        index: number,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const scene = sceneGroup.scenes.find((scene) => scene.id === sceneId);
        if (!scene) {
            return;
        }
        if (index < 0 || index >= scene.subscenes.length) {
            return;
        }
        scene.removeSubslide(index);
        const nextActiveSubsceneIndex = Math.max(0, index - 1);
        dispatch(setFocusedSceneAndScroll(scene.id, sceneGroup.id, nextActiveSubsceneIndex));
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered: true,
                parentActionGroup: uuid(),
            }),
        );
    },
);

export const setMusicOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        music: ImmutableMap<AudioAssetModel | AudioSourceData>,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.music = music.toJS() as AudioSourceData;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // ensure outro has no music if it has media with volume
        call(ensureOutroWithVolumeHasNoMusicOperation, systemTriggered, parentActionGroup);
    },
);

export const clearMusicOperation = createLuminaryOperation(
    ({ luminary, dispatch }, systemTriggered = false, parentActionGroup = uuid()) => {
        if (systemTriggered === false) {
            analytics.track('Media Step: Audio Removed');
        }
        luminary.music = null;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setMusicStartTimeOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        startTime = 0,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const musicAudioAsset = luminary.audioAssets[luminary.musicId];
        if (musicAudioAsset) {
            musicAudioAsset.startTime = startTime;
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const clearVoiceoverOperation = createLuminaryOperation(
    ({ luminary, dispatch }, systemTriggered = false, parentActionGroup = uuid()) => {
        if (systemTriggered === false) {
            analytics.track('Media Step: Voiceover Removed');
        }
        luminary.voiceover = null;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setMusicVolumeOverrideOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        volume: number,
        systemTriggered = false,
        parentActionGroup: string | undefined = undefined,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        sceneGroup.musicVolumeOverride = volume;

        analytics.track('slide globalAudioVolumeOverride changed', {
            new_global_audio_volume: volume,
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setVideoMusicVolumeOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        volume: number,
        systemTriggered = false,
        parentActionGroup: string | undefined = undefined,
    ) => {
        if (luminary.musicAsset) {
            luminary.musicAsset.volume = volume;
        }

        analytics.track('video globalAudioVolumeOverride changed', {
            new_global_audio_volume: volume,
        });

        // checkpoint video
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const ensureOutroWithVolumeHasNoMusicOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        systemTriggered = false,
        parentActionGroup: string | undefined = undefined,
    ) => {
        const changed = muteOutroMusicIfHasMediaWithVolume(luminary);

        if (changed) {
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

export const setSceneWatermarkOverrideOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        // passing in null as index overrides the watermark to none
        index: number | null,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        if (index === null) {
            scene.watermark.sourceData = VideoData.WATERMARK_NONE;
        } else {
            scene.watermark.sourceData = VideoData.templateWatermarkSourcesByIndex(index);
        }
        // checkpoint video
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setSceneWatermarkPositionOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        position: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const validWatermarkPosition = scene.watermark.availablePositions.indexOf(position) !== -1;
        if (validWatermarkPosition) {
            setObjectPropertyValue(scene, 'watermark.position', position);
            // checkpoint video
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            analytics.track('Media Step: Scene Watermark Position Changed', { sceneId });
        }
    },
);

/**
 * Splits a scene group into two, at the given time. The time is in "media asset time" (aka "transcript time"),
 * Not relative to the scenegroup or video start time or anything like that.
 */
export const splitSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneGroupId: string,
        splitMillis: number,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const originalSceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!originalSceneGroup) {
            return;
        }

        luminary.batchRun(() => {
            const newSceneGroupId = call(duplicateSceneGroupOperation, {
                sceneGroupId,
                scrollToNewSceneGroup: false,
                systemTriggered,
                parentActionGroup,
            });
            if (!newSceneGroupId) {
                throw new Error('Could not duplicate scene group');
            }
            const newSceneGroup = luminary.getSceneGroupById(newSceneGroupId);
            if (!newSceneGroup) {
                return;
            }

            call(
                cutVoicedrivenSceneGroupOperation,
                sceneGroupId,
                splitMillis,
                getVoiceDrivenBaseAssetSourceDuration(originalSceneGroup),
                systemTriggered,
                parentActionGroup,
            );
            call(
                cutVoicedrivenSceneGroupOperation,
                newSceneGroupId,
                0,
                splitMillis,
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

/**
 * Duplicates the given scene group, and then sets the first scene as focused so that the user can start editing
 * The group is inserted after the group that is being duplicated.
 *
 * @return {string} id of the duplicated slide
 */
export const duplicateSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            scrollToNewSceneGroup = true,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            scrollToNewSceneGroup?: boolean;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const numNonOutroNonBrandSceneGroups = getNumNonOutroNonBrandSceneGroups(luminary);
        /**
         * When we click on words in transcript and scrub a video, luminary switch to drawToCanvas mode to render previews.
         * If we duplicate scene while we in "scrubbing" mode, we'll see black screen on duplicated scene.
         * This change stops playback and scrub mode before duplicating
         */
        SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

        const { sceneGroup: newSceneGroup } = luminary.addSceneGroup({
            sceneDesignId: sceneGroup.scenes[0].sceneDesign,
        });
        call(
            replaceSceneGroupOperation,
            newSceneGroup.id,
            sceneGroup,
            systemTriggered,
            parentActionGroup,
        );
        luminary.moveSceneGroupToIndex(newSceneGroup.id, sceneGroup.index + 1);

        const transcript = getTranscriptById(getState(), sceneGroup.id);

        // make a mapping of the old scene ids to the new scene ids
        const newSceneIdMap: Record<string, string> = {};
        sceneGroup.scenes.forEach((scene: Slide, index: number) => {
            newSceneIdMap[scene.id] = newSceneGroup.scenes[index].id;
        });

        if (transcript && transcript.get('editorState')) {
            duplicateTranscript(
                newSceneGroup.id,
                transcript.get('editorState') as unknown as SerializedEditorState,
                transcript.get('language'),
                newSceneIdMap,
                systemTriggered,
                parentActionGroup,
            )(dispatch);
        }

        dispatch(
            copyUserOverridesForDuplicatedSceneGroup(
                newSceneGroup,
                sceneGroup,
                systemTriggered,
                parentActionGroup,
            ),
        );

        const newScene = newSceneGroup.scenes[0];
        if (scrollToNewSceneGroup) {
            dispatch(setFocusedSceneAndScroll(newScene.id, newSceneGroup.id, 0));
        }

        analytics.track('New Scene Added', {
            method: 'Duplicate',
            sceneId: newScene.id,
            sceneGroupId: newSceneGroup.id,
            sceneDesignId: newScene.sceneDesign,
            sceneCategory: newScene.sceneCategory,
            templateId: luminary.template.id,
            hasIcon: sceneContainsIcon(newScene),
            numNonOutroNonBrandSceneGroups,
        });
        return newSceneGroup.id;
    },
);

/**
 * Serializes the given SceneGroup and all it's Scenes. Then `.load()`'s that data into the
 * targetSceneGroup (destroying anything that was previously in the targetSceneGroup).
 *
 * One example of when this is useful: when you want to copy a SceneGroup from one Luminary to another
 */
export const replaceSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        targetSceneGroupId: string, // id of the scene group whose data will be replaced
        sourceSceneGroup: SceneGroup, // luminary SceneGroup that will replace the target SceneGroup
        systemTriggered = false,
        parentActionGroup = uuid(),
        saveCheckpoint = true,
    ) => {
        luminary.batchRun(() => {
            const targetSceneGroup = luminary.getSceneGroupById(targetSceneGroupId);

            const sceneGroupData = sourceSceneGroup.save();

            targetSceneGroup.clear();
            targetSceneGroup.load(sceneGroupData);
            targetSceneGroup.generateNewAssetIds();

            // remove all the scenes
            targetSceneGroup.scenes
                .map((scene: Slide) => scene.id)
                .forEach((sceneId: string) => {
                    targetSceneGroup.removeScene(sceneId);
                });

            // now add the new scenes
            sourceSceneGroup.scenes.forEach((sourceScene: Slide) => {
                const targetScene = targetSceneGroup.addScene({
                    sceneDesignId: sourceScene.sceneDesign,
                });
                targetScene.clear();
                targetScene.load(sourceScene.save());
                targetScene.generateNewAssetIds();
            });

            targetSceneGroup.musicVolumeOverride = sourceSceneGroup.musicVolumeOverride;

            if (isVoiceDrivenSceneGroup(targetSceneGroup)) {
                void updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({
                    sceneGroup: targetSceneGroup,
                });
            }

            saveCheckpoint &&
                dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(luminary),
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
        });
    },
);

/**
 * Replaces the slide data for the given slide id, and then applies the user's overrides if there are any.
 */
export const replaceSlideOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        targetSceneId: string, // id of the slide whose data will be replaced
        sourceSlide: Slide, // luminary slide object that will replace the slides data
        sourceAssetsWithCustomTiming: string[],
        systemTriggered = false,
        parentActionGroup = uuid(),
        saveCheckpoint = true,
    ) => {
        const scene = luminary.getSceneById(targetSceneId);
        if (!scene) {
            return;
        }

        scene.clear();
        scene.load(sourceSlide.save());
        scene.generateNewAssetIds();
        saveCheckpoint &&
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );

        // set duplicated slide user overrides
        if (!getState().undoable) {
            return;
        }
        const originalSlideOverrides = getSlideOverrides(getState(), targetSceneId);

        if (!originalSlideOverrides) {
            return;
        }

        const assetTimingOverrides: string[] = [];
        sourceAssetsWithCustomTiming.forEach((assetId) => {
            const sourceAsset = sourceSlide.getAssetById(assetId);
            if (!sourceAsset) {
                return;
            }
            const targetAsset = scene.getAssetByName(sourceAsset.name);
            if (!targetAsset) {
                return;
            }
            assetTimingOverrides.push(targetAsset.id);
        });
        originalSlideOverrides.asset_timing_overrides = assetTimingOverrides;
        dispatch(
            setSceneOverrides({
                sceneId: targetSceneId,
                overrides: originalSlideOverrides,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const initializeSceneAttributesCacheOperation = createLuminaryOperation(({ luminary }) => {
    const sceneAttributesCache = SceneAttributesCache.getInstance();
    sceneAttributesCache.setLuminary(luminary);
});

export const getSceneAttributesForSwapSceneDesignTabOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return Promise.reject(
                new Error(`Could not find slide for scene attributes... ${sceneId}`),
            );
        }

        let recommendBrightness = true;
        if (isBrightnessUserOverridden(getState(), sceneId)) {
            recommendBrightness = false;
        }

        // In the layout selector want to autoCrop
        const cacheData = SceneAttributesCacheData.from(
            scene,
            luminary,
            AUTO_CROP,
            recommendBrightness,
        );

        const sceneAttributesCache = SceneAttributesCache.getInstance();

        return sceneAttributesCache.getSceneAttributes(cacheData);
    },
);

export const getFaceDetectionsOperation = createLuminaryOperation(
    async ({ luminary }, sceneId: string) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return [];
        }

        if (!isVoiceDrivenSceneGroup(scene.sceneGroup)) {
            return [];
        }

        const mediaAsset = scene.sceneGroup.mediaAsset;
        if (!mediaAsset) {
            return [];
        }

        const result = await ApiService.faceDetectionCreate({
            body: {
                asset_type: AssetTypes.VIDEO,
                asset_uuid: mediaAsset.sourceData.uuid,
                timestamp_ms: translateVideoTimeToTranscriptTime(
                    (scene.startTime + scene.endTime) / 2,
                    getVoiceDrivenAssetPlaybackRegions(scene.sceneGroup),
                    getVoiceDrivenBaseAssetSourceDuration(scene.sceneGroup),
                    scene.sceneGroup.startTime,
                ),
            },
        });
        const ws = WebSocketClient.getInstance();
        const faceDetections = await ws.waitForMessage<FaceDetectionResponse>(
            result.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'face detection',
        );

        return faceDetections.bounding_boxes;
    },
);

export function setLuminaryCapabilities(luminary: Luminary, capabilities: Capabilities) {
    luminary.capabilities.noBranding = capabilities.no_branding;
    luminary.capabilities.customWatermark = capabilities.custom_watermark;
    luminary.capabilities.render720p = capabilities.render_720p;
    luminary.capabilities.render1080p = capabilities.render_1080p;
}

export const setCapabilitiesOperation = createLuminaryOperation(({ luminary, call }) => {
    // ignore logged out users
    if (!SERVER.USER) {
        return;
    }

    setLuminaryCapabilities(luminary, SERVER.USER.capabilities);
});

export const setInitialSlideColorShuffleStateOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const slideArea = luminary.referenceResolution.height * luminary.referenceResolution.width;
        const invisibleAssetNames = getInvisibleAssetNames(luminary, scene.id);
        const colorShuffleInfo = getColorPermutations(
            scene,
            slideArea,
            invisibleAssetNames,
            luminary.template,
            MAX_COLOR_SHUFFLES,
        );
        dispatch(
            setColorShuffleDataForSlide({
                sceneId: scene.id,
                colorShuffleInfo,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setColorShuffleIndexForSlide({
                sceneId: scene.id,
                colorShuffleIndex: 0,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const shuffleColorsOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }, systemTriggered = false, parentActionGroup = uuid()) => {
        const initAutoDraw = luminary.autoDraw;
        luminary.autoDraw = false;

        let colorShuffleIndex = getTemplateColorShuffleIndex(getState());
        let colorShufflePalettes: number[][] = getTemplateColorShufflePalettes(getState());

        // Generate the color shuffle palettes if they don't exist already
        if (colorShufflePalettes.length === 0) {
            colorShufflePalettes = generateBestColorPalettePermutationsFromLuminary({ luminary });
            dispatch(
                setTemplateColorShufflePalettes({
                    palettes: colorShufflePalettes,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }

        colorShuffleIndex++;
        colorShuffleIndex = colorShuffleIndex % colorShufflePalettes.length;

        const newPalette = colorShufflePalettes[colorShuffleIndex];
        const newLuminaryColors: TemplateColor[] = structuredClone(luminary.template.colors);

        newLuminaryColors.forEach((color: TemplateColor, index) => {
            const newColor = newPalette[index];
            color.color = newColor;
        });

        luminary.template.colors = newLuminaryColors;

        dispatch(
            setTemplateColorShuffleIndex({ colorShuffleIndex, systemTriggered, parentActionGroup }),
        );
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Video Colors Shuffled');

        luminary.autoDraw = initAutoDraw;
    },
);

export const shuffleSceneColorsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        const initAutoDraw = scene.autoDraw;
        scene.autoDraw = false;

        const activeColorShuffleIndex = getSceneColorShuffleIndex(getState(), sceneId);
        const activeColorShuffleData = getSceneColorShuffleData(getState(), sceneId);
        if (
            activeColorShuffleData !== null &&
            Object.prototype.hasOwnProperty.call(activeColorShuffleData, 'colorShuffles')
        ) {
            const colorNames = [...Array(scene.template.colors.length).keys()].map(
                (i) => 'colors_' + i,
            );
            const colorShuffleIndex = activeColorShuffleIndex;
            // get the color permutation we need to apply (colorShuffleIndex is assumed to have already been advanced to the next
            // permutation by the time we get here)
            const newColorIndices = activeColorShuffleData.colorShuffles[colorShuffleIndex];

            // apply the new colors
            const childColorProps = activeColorShuffleData.childColorProps;
            scene
                .getAssets()
                .filter((c: Asset) => c.name in childColorProps)
                .forEach((c) => {
                    childColorProps[c.name].forEach((p) => {
                        const propertyValue = VideoData.getTemplateProperty(c, p);
                        if (VideoData.isTemplateReference(propertyValue)) {
                            // figure out which index in the original color palette this property is mapped to
                            const colorName =
                                activeColorShuffleData.templateReferenceMap[c.name][p];
                            const colorIndex = colorNames.indexOf(colorName);
                            // apply the color that has been moved into that location in this permutation
                            const newColorIndex = newColorIndices[colorIndex];
                            setObjectPropertyValue(
                                c,
                                p,
                                VideoData.templateColorByIndex(newColorIndex),
                            );
                        }
                    });
                });

            scene.autoDraw = initAutoDraw;

            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);

export const getMediaAssetCropInformationOperation = createLuminaryOperation(
    async ({ luminary }, sceneId: string, assetId: string) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene)
            throw new Error('The media cropper could not find the scene for the given scene ID.');
        const mediaAsset = scene.getAssetById(assetId);
        if (!mediaAsset || !mediaAsset.isEditable || !VideoData.isMediaAsset(mediaAsset)) {
            throw new Error(
                'The media cropper could not find the editable media asset for the given scene.',
            );
        }

        let assetPreviewUrl = mediaAsset.sourceData.preview_url;
        let assetSourceWidth = mediaAsset.sourceRenderWidth;
        let assetSourceHeight = mediaAsset.sourceRenderHeight;
        let assetIsVideo = mediaAsset.isVideo;
        let mediaTargetWidth = mediaAsset.mediaTargetResolution.width;
        let mediaTargetHeight = mediaAsset.mediaTargetResolution.height;

        if (isAIAvatarSceneGroup(scene.sceneGroup) && mediaAsset.isLinkedToCompositingAsset) {
            const imageData = await mediaAsset.getBase64Data();
            assetPreviewUrl = imageData.base64;
            assetSourceWidth = imageData.width;
            assetSourceHeight = imageData.height;
            assetIsVideo = false;
            mediaTargetWidth = imageData.width;
            mediaTargetHeight = imageData.height;
        }

        let assetStartTime = 0;
        let assetEndTime = 0;
        if (assetIsVideo) {
            if (mediaAsset.linkToSceneGroup && mediaAsset._linkedMediaAsset) {
                // the TH media asset is attached to the scene group,
                // but the crop is handled on a scene-by-scene basis.
                // We want to show the correct part of the video in the crop modal - for
                // example if the user clicks "crop" on overlay 2, we want to show the
                // part of the video that would be playing during overlay 2.
                const sceneGroup = mediaAsset.slide.sceneGroup;
                const voicedrivenAssetPlaybackRegions =
                    getVoiceDrivenAssetPlaybackRegions(sceneGroup);
                const voicedrivenAssetSourceDuration =
                    getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                assetStartTime =
                    translateVideoTimeToTranscriptTime(
                        mediaAsset.slide.startTime,
                        voicedrivenAssetPlaybackRegions,
                        voicedrivenAssetSourceDuration,
                        sceneGroup.startTime,
                    ) / 1000;
                assetEndTime =
                    translateVideoTimeToTranscriptTime(
                        mediaAsset.slide.endTime,
                        voicedrivenAssetPlaybackRegions,
                        voicedrivenAssetSourceDuration,
                        sceneGroup.startTime,
                        { includeTrailingCut: false },
                    ) / 1000;
            } else {
                assetStartTime = mediaAsset.startTime / 1000;
                assetEndTime = (mediaAsset.endTime as number) / 1000;
            }
        }

        return {
            assetIsVideo,
            assetSourceWidth,
            assetSourceHeight,
            assetPreviewUrl,
            assetStartTime,
            assetEndTime,
            mediaTargetWidth,
            mediaTargetHeight,
        };
    },
);

/**
 * Resize all text on all slides so it best fits within their available bounding box using the template's pre-defined
 * font size range.
 */
export const resizeTextToFitOnSceneForAllSlidesOperation = createLuminaryOperation(
    ({ luminary }) => {
        resizeTextToFitOnSceneForEntireVideo(luminary);
    },
);

/**
 * Retrieves the template bundle and calls setTemplate on luminary
 */
export const setLuminaryTemplateOperation = createLuminaryOperation(
    ({ luminary, getState, call }, templateId: string) => {
        const templateBundle = getTemplateBundle(getState(), templateId);
        luminary.setTemplate(ImmutableToJS(templateBundle));
    },
);

/**
 * Retrieves the current caption scene design selected by the user in the video
 */
export function getCurrentGlobalCaptionSceneDesignInUse(luminary: Luminary): string | null {
    const existingVoiceDrivenSceneGroup = getFirstVoiceDrivenSceneGroup(luminary);
    return existingVoiceDrivenSceneGroup?.captions?.sceneDesignId || null;
}

/**
 * Base method to build a scene with captions and set caption visibility
 * @returns
 */
const buildCaptionSceneOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            blueprintId = null,
        }: {
            sceneGroupId: string;
            blueprintId: number | null;
        },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const scene = sceneGroup.scenes[0];
        // Delete all scenes except first scene
        sceneGroup.scenes.forEach(
            (s: Slide) => s.id !== scene.id && sceneGroup.removeScene(scene.id),
        );

        // Set the duration of the one and only scene to be the duration of the captions
        scene.duration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        // Configure captions from blueprint/cookies
        const templateId = luminary.template.id;
        const defaultSceneDesignId = getTemplateDefaultCaptionSceneDesign(
            getState(),
            luminary.template.id,
        );
        await call(
            setCaptionsFromBlueprintOrCookiesOperation,
            sceneGroup,
            blueprintId,
            templateId,
            defaultSceneDesignId,
        );
    },
);

/**
 * Existing videos using the old talking head scenes may have been switched to the new talking head scenes.
 * This means we need to check the media of such scenes, and generate a transcript if the media has voiceover.
 */
export const checkNeedsTranscriptOperation = createLuminaryOperation(
    async ({ luminary, getState, call }) => {
        for (const sceneGroup of luminary.sceneGroups) {
            const scene = sceneGroup.scenes[0];
            if (!scene) {
                return;
            }
            const defaultMediaAsset = sceneGroup.mediaAsset;
            const transcriptIds = getTranscriptIds(getState());
            const hasAudio = defaultMediaAsset?.hasAudio || sceneGroup.mediaAsset?.hasAudio;
            if (
                getSceneGroupCategory(sceneGroup) === VideoData.CATEGORY_TALKING_HEAD &&
                hasAudio &&
                !transcriptIds.contains(sceneGroup.id)
            ) {
                const defaultMediaAssetId = defaultMediaAsset
                    ? defaultMediaAsset.sourceData?.uuid
                    : null;
                if (!defaultMediaAssetId) {
                    continue;
                }
                const response = await ApiService.assetTranscriptRetrieve({
                    path: { asset_id: defaultMediaAssetId },
                    query: { asset_type: TranscriptAssetType.VIDEO },
                });
                if (response.data.hasVoiceover && response.data.transcriptWords) {
                    call(loadTranscriptFromLuminary, sceneGroup.id);
                    await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
                    analytics.track('Transcript generated from captions', {
                        sceneGroupId: sceneGroup.id,
                    });
                }
            }
        }
    },
);

/**
 * Generates a transcript from the luminary transcript in a scene group.
 * This is particularly useful for making old talking head scenes compatible with
 * new talking head scenes which require a transcript.
 */
export const loadTranscriptFromLuminary = createLuminaryOperation(
    async ({ luminary, dispatch, getState, call }, sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !sceneGroup.scenes[0]) {
            return;
        }
        analytics.track('Loading the transcript from luminary - deprecated!');
        const transcriptWords: TranscriptWord[] =
            sceneGroup.captions.transcript.transcriptData.words.map((word) => {
                return {
                    ...word,
                    filler: false, // the old talking head scene groups did not have filler words.
                };
            });
        await call(
            loadNewTranscriptOperation,
            transcriptWords,
            undefined,
            sceneGroupId,
            sceneGroup.scenes[0].id,
            false,
            false,
            null,
            true,
        );
    },
);

export function makeVoicedrivenCompatibleScene(scene: Slide) {
    scene.customTransition.transitionUrl = VideoData.NONE_TRANSITION_URL;
}

/**
 * helper functions for createOverlay() - it checks whether the durations have been set properly. In each scenario the summation of the durations
 * of the newly created scenes should never exceed the original base scene's duration.
 * @param discardLeftBaseScene - when we create overlays that overlap the beginning of the base scene, we throw away the left/original base scene
 * @param discardRightBaseScene - when we create overlays that overlap the end of the base scene, we throw away the right base scene
 * @param originalBaseSceneDuration - the original base scene's duration - this is what we're checking against
 * @param originalBaseScene - left base scene
 * @param newOverlayScene - new overlay scene
 * @param rightBaseScene - right base scene
 */
const _validateCreateOverlayDurations = (
    discardLeftBaseScene: boolean,
    discardRightBaseScene: boolean,
    originalBaseSceneDuration: number,
    originalBaseScene: Slide,
    newOverlayScene: Slide,
    rightBaseScene: Slide,
) => {
    if (discardLeftBaseScene && discardRightBaseScene) {
        if (!floatingPointComparison(newOverlayScene.duration, originalBaseSceneDuration)) {
            ErrorReporter.setContext('overlay creation data', {
                newOverlaySceneDuration: newOverlayScene.duration,
                originalBaseSceneDuration,
            });
            throw new CreateOverlayError(
                'Invalid duration when creating overlay over entire base scene',
            );
        }
    } else if (discardLeftBaseScene) {
        if (
            !floatingPointComparison(
                newOverlayScene.duration + rightBaseScene.duration,
                originalBaseSceneDuration,
            )
        ) {
            ErrorReporter.setContext('overlay creation data', {
                newOverlaySceneDuration: newOverlayScene.duration,
                rightBaseSceneDuration: rightBaseScene.duration,
                originalBaseSceneDuration,
            });
            throw new CreateOverlayError(
                'Invalid duration when creating overlay over the beginning of the base scene',
            );
        }
    } else if (discardRightBaseScene) {
        if (
            !floatingPointComparison(
                originalBaseScene.duration + newOverlayScene.duration,
                originalBaseSceneDuration,
            )
        ) {
            ErrorReporter.setContext('overlay creation data', {
                newOverlaySceneDuration: newOverlayScene.duration,
                originalBaseSceneDotDuration: originalBaseScene.duration,
                originalBaseSceneDuration,
            });
            throw new CreateOverlayError(
                'Invalid duration when creating overlay over the end of the base scene',
            );
        }
    } else {
        if (
            !floatingPointComparison(
                originalBaseScene.duration + newOverlayScene.duration + rightBaseScene.duration,
                originalBaseSceneDuration,
            )
        ) {
            ErrorReporter.setContext('overlay creation data', {
                newOverlaySceneDuration: newOverlayScene.duration,
                originalBaseSceneDotDuration: originalBaseScene.duration,
                rightBaseSceneDuration: rightBaseScene.duration,
                originalBaseSceneDuration,
            });
            throw new CreateOverlayError(
                'Invalid duration when creating overlay in the middle of the base scene',
            );
        }
    }
};

/*
 * After performing multiple operations like createOverlay and translateOverlay{Start,End}
 * on a scene group, we will end up with adjacent scenes in the scene group having
 * the same scene design - unnecessary fragmentation.
 *
 * This function does a single pass defragmentation by merging the first pair of adjacent
 * scenes with a common scene design that it finds. It is intended to be called at the
 * end of other operations.
 */
export const defragSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneGroupId: string,
        baseSceneDesignId: string,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new TranslateOverlayError('Scene Group missing');
        }
        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new TranslateOverlayError('Voice Driven asset missing');
        }

        for (let i = 1; i < sceneGroup.scenes.length; i++) {
            const scene = sceneGroup.scenes[i];
            const prevScene = sceneGroup.scenes[i - 1];
            if (
                prevScene.sceneDesign === scene.sceneDesign &&
                scene.sceneDesign === baseSceneDesignId
            ) {
                call(
                    translateOverlayStartOperation,
                    scene.id,
                    sceneGroupId,
                    baseSceneDesignId,
                    translateVideoTimeToTranscriptTime(
                        scene.endTime,
                        getVoiceDrivenAssetPlaybackRegions(sceneGroup),
                        getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
                        sceneGroup.startTime,
                        { includeTrailingCut: false },
                    ),
                    systemTriggered,
                    parentActionGroup,
                );
                return;
            }
        }
    },
);

function _createBaseScene(sceneGroup: SceneGroup, baseSceneDesignId: string, scene: Slide) {
    // create a new scene with the baseSceneDesignId and copy current scene elements to it
    const baseScene = sceneGroup.addScene({ sceneDesignId: baseSceneDesignId });
    const otherBaseScene = sceneGroup.scenes.find((scene) => {
        return scene.sceneDesign === baseSceneDesignId;
    });
    standardizeTalkingHeadMediaAssetProperties(baseScene, otherBaseScene);
    makeVoicedrivenCompatibleScene(baseScene);

    baseScene.duration = 1;
    scene.duration -= 1;

    return baseScene;
}

/**
 * Translates the beginning of an overlay earlier or later. This allows for fine grained
 * control over the duration of the overlay.
 *
 * There is also a baseSceneDesignId parameter. This is used for the case where an
 * overlay is being shortened, but it is also the first scene in the scene group.
 * In this case, we create a new first scene with the scene design baseSceneDesignId.
 *
 * If the overlay is shortened to a length of zero, the overlay will be removed entirely.
 * The captions will be moved to the prior scene.
 *
 * Finally, a defragmentation pass is run on the scene group to merge adjacent scenes
 * that have the same scene design.
 *
 * @param sceneId - id of the Overlay
 * @param sceneGroupId - id of the scene group containing the Overlay
 * @param baseSceneDesignId - id of the scene design to use for the new base scene
 * @param startTimeMillis - the new start time for the Overlay, in the transcript "timezone"
 * @param systemTriggered
 * @param parentActionGroup
 */
export const translateOverlayStartOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneId: string,
        sceneGroupId: string,
        baseSceneDesignId: string,
        startTimeMillis: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new TranslateOverlayError('Scene Group missing');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new TranslateOverlayError('Scene missing');
        }
        if (scene.sceneGroup !== sceneGroup) {
            throw new TranslateOverlayError('Scene does not belong to Scene Group');
        }

        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new TranslateOverlayError('Voice Driven asset missing');
        }

        // Calculate interest points before transformation
        const interestPoints: Array<InterestPointTime> = [];
        // Get interest points for the overlay scene
        const overlayInterestPoints = call(calculateInterestPointTimesOperation, sceneId);
        interestPoints.push(...overlayInterestPoints);
        // Get interest points for the left neighbouring scene (if it exists)
        const leftSceneIndex = scene.localIndex - 1;
        if (leftSceneIndex >= 0) {
            const leftScene = sceneGroup.scenes[leftSceneIndex];
            const leftInterestPoints = call(calculateInterestPointTimesOperation, leftScene.id);
            interestPoints.push(...leftInterestPoints);
        }

        // let's work in video time
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        startTimeMillis = translateTranscriptTimeToVideoTime(
            startTimeMillis,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenBaseAssetSourceDuration,
            sceneGroup.startTime,
        );
        let sceneStartTime = scene.startTime;
        let sceneEndTime = scene.endTime;

        if (startTimeMillis > sceneEndTime) {
            if (equalWithinEpsilon(startTimeMillis, sceneEndTime)) {
                // startTimeMillis is juuuuust after the end of the scene.
                // we will remove it entirely, so set the startTime to be the sceneEndTime
                startTimeMillis = sceneEndTime;
            } else {
                throw new TranslateOverlayError('Invalid translation for overlay startTime');
            }
        }

        luminary.batchRun(() => {
            let missingLeft = false;
            // Find the scene before the overlay (the left sibling). If there is no left sibling
            // then a left sibling with the baseSceneDesignId will have to be created
            let leftSceneIndex = scene.localIndex - 1;
            if (leftSceneIndex < 0) {
                // this happens when you delete an overlay that starts at the beginning of a scene group
                const newScene = _createBaseScene(sceneGroup, baseSceneDesignId, scene);
                // Offset the original scene by 1 millisecond
                sceneStartTime += 1;
                sceneEndTime -= 1;
                sceneGroup.moveSceneToIndex(newScene.id, 0);
                leftSceneIndex = 0;
                missingLeft = true;
            }

            let leftScene = sceneGroup.scenes[leftSceneIndex];
            if (
                leftScene.sceneDesign !== baseSceneDesignId &&
                scene.sceneDesign !== baseSceneDesignId
            ) {
                // we are moving the overlay's start to the right. If the left scene is also an overlay, we need to
                // create a new base scene in-between the two overlays
                const newScene = _createBaseScene(sceneGroup, baseSceneDesignId, scene);
                // Offset the original scene by 1 millisecond
                sceneStartTime += 1;
                sceneEndTime -= 1;
                sceneGroup.moveSceneToIndex(newScene.id, leftSceneIndex + 1);
                missingLeft = true;
                leftScene = newScene;
            }

            // earlier denotes whether we are shifting the beginning of the Overlay to be earlier or later
            const earlier = startTimeMillis <= sceneStartTime;

            // The offset is the number of milliseconds to translate the beginning of the Overlay
            let offset = Math.abs(sceneStartTime - startTimeMillis);
            let removed = false;

            if (earlier) {
                const duration = leftScene.duration;
                if (duration - offset > 1) {
                    leftScene.duration -= offset;
                    scene.duration += offset;
                    offset = 0;
                } else {
                    // If the beginning of the Overlay has been translated all the way to the beginning
                    // the left scene will now have zero length and needs to be removed entirely.
                    sceneGroup.removeScene(leftScene.id);
                    removed = true;
                    scene.duration += duration;
                    offset -= duration;
                }
            } else {
                const duration = scene.duration;
                if (duration - offset > 1) {
                    // If the current scene is longer than the required offset, simply alter the duration of the scene
                    scene.duration -= offset;
                    leftScene.duration += offset;
                    offset = 0;
                } else {
                    // If the current scene is shorter or has the same duration as the required offset
                    // remove the current scene and add the removed scene's duration to the left sibling
                    sceneGroup.removeScene(scene.id);
                    removed = true;
                    leftScene.duration += duration;
                    offset -= duration;
                }
            }
            if (leftScene.duration > 1 && missingLeft) {
                // If we had to create a new leftScene it originally has a duration of 1 millisecond
                // which was offset from the overlay duration. As long as this leftScene is now
                // longer than 1 millisecond we can add back the offset we subtracted.
                sceneEndTime += 1;
            }
            if (equalWithinEpsilon(startTimeMillis, sceneEndTime)) {
                // If the beginning of the overlay has been translated all the way to the end
                // the scene will now have zero length and needs to be removed entirely.
                sceneGroup.removeScene(scene.id);
                removed = true;
            }

            if (removed) {
                // The sceneGroup will likely be fragmented now - there will be adjacent
                // scenes in the group that have the same sceneDesign. Here we do a
                // defragment pass and "stitch" those scenes together.
                call(
                    defragSceneGroupOperation,
                    sceneGroupId,
                    baseSceneDesignId,
                    systemTriggered,
                    parentActionGroup,
                );
            }
        });

        // Reapply interest points after transformation
        if (interestPoints.length > 0) {
            call(
                reapplyInterestPointsOperation,
                interestPoints,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
        }
    },
);

/**
 * Translates the end of an overlay earlier or later. This allows for fine grained
 * control over the duration of the overlay.
 *
 * There is also a baseSceneDesignId parameter. This is used for the case where an
 * overlay is being shortened, but it is also the last scene in the scene group.
 * In this case, we create a new final scene with the scene design  baseSceneDesignId.
 *
 * If the overlay is shortened to a length of zero, the overlay will be removed entirely.
 *
 * Finally, a defragmentation pass is run on the scene group to merge adjacent scenes
 * that have the same scene design.
 *
 * @param sceneId - id of the Overlay
 * @param sceneGroupId - id of the scene group containing the Overlay
 * @param baseSceneDesignId - id of the scene design to use for the new base scene
 * @param endTimeMillis - the new end time for the Overlay, in the transcript "timezone"
 * @param systemTriggered
 * @param parentActionGroup
 */
export const translateOverlayEndOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        sceneId: string,
        sceneGroupId: string,
        baseSceneDesignId: string,
        endTimeMillis: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new TranslateOverlayError('Scene Group missing');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new TranslateOverlayError('Scene missing');
        }
        if (scene.sceneGroup !== sceneGroup) {
            throw new TranslateOverlayError('Scene does not belong to Scene Group');
        }

        luminary.batchRun(() => {
            // Find the scene after the overlay (the right sibling). If there is no right sibling
            // then a right sibling with the baseSceneDesignId will have to be created at the end
            const rightSceneIndex = scene.localIndex + 1;
            if (rightSceneIndex > sceneGroup.scenes.length - 1) {
                const newScene = _createBaseScene(sceneGroup, baseSceneDesignId, scene);
                sceneGroup.moveSceneToIndex(newScene.id, rightSceneIndex);
            }
            let rightScene = sceneGroup.scenes[rightSceneIndex];
            if (rightScene.sceneDesign !== baseSceneDesignId) {
                const newScene = _createBaseScene(sceneGroup, baseSceneDesignId, scene);
                sceneGroup.moveSceneToIndex(newScene.id, rightSceneIndex);
                rightScene = newScene;
            }

            // Implement translateOverlayEnd by calling translateOverlayStart on the right sibling scene
            call(
                translateOverlayStartOperation,
                rightScene.id,
                sceneGroupId,
                baseSceneDesignId,
                endTimeMillis,
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

export const deleteLuminaryOverlayOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call, getState },
        sceneId: string,
        sceneGroupId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        if (scene.sceneGroup !== sceneGroup) {
            throw new TranslateOverlayError('Scene does not belong to Scene Group');
        }

        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new TranslateOverlayError('Voice Driven asset missing');
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) throw new Error('Base scene design id not found');

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        const leftScene = scene.localIndex === 0 ? null : sceneGroup.scenes[scene.localIndex - 1];
        const rightScene =
            scene.localIndex === sceneGroup.scenes.length - 1
                ? null
                : sceneGroup.scenes[scene.localIndex + 1];

        // note: these are left/right scenes before the delete happens
        // ie. if the last overlay is deleted, we can have no left/right scene, but after the delete happens, there
        // will be a base scene.
        const leftSceneExists = !!leftScene;
        const rightSceneExists = !!rightScene;

        const leftSceneIsOverlay = leftSceneExists && leftScene.sceneDesign !== baseSceneDesignId;
        const rightSceneIsOverlay =
            rightSceneExists && rightScene.sceneDesign !== baseSceneDesignId;

        luminary.batchRun(() => {
            // remove the overlay by translating the start of the overlay all the way to its own end time
            call(
                translateOverlayStartOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                translateVideoTimeToTranscriptTime(
                    scene.endTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                    sceneGroup.startTime,
                    { includeTrailingCut: false },
                ),
                systemTriggered,
                parentActionGroup,
            );

            // in voiceover scene groups we try to fill the gap of deleted overlay with surrounding overlays
            const shouldFillGap = !(
                isAIAvatarSceneGroup(sceneGroup) || isTalkingHeadSceneGroup(sceneGroup)
            );
            if (shouldFillGap) {
                // we want to prioritize maintaining overlay boundaries when filling in the gap of the deleted overlay
                // we only "fill the gap" when deleted overlay is between two overlays, or
                // we're deleting the first/last overlay and the neighbouring scene is an overlay
                if (leftSceneIsOverlay && rightSceneIsOverlay) {
                    call(
                        translateOverlayStartOperation,
                        rightScene.id,
                        sceneGroupId,
                        baseSceneDesignId,
                        translateVideoTimeToTranscriptTime(
                            leftScene.endTime,
                            voiceDrivenAssetPlaybackRegions,
                            voiceDrivenBaseAssetSourceDuration,
                            sceneGroup.startTime,
                        ),
                        systemTriggered,
                        parentActionGroup,
                    );
                } else if (!leftSceneExists && rightSceneIsOverlay) {
                    call(
                        translateOverlayStartOperation,
                        rightScene.id,
                        sceneGroupId,
                        baseSceneDesignId,
                        voiceDrivenAssetPlaybackRegions[0][0],
                        systemTriggered,
                        parentActionGroup,
                    );
                } else if (!rightSceneExists && leftSceneIsOverlay) {
                    call(
                        translateOverlayEndOperation,
                        leftScene.id,
                        sceneGroupId,
                        baseSceneDesignId,
                        voiceDrivenAssetPlaybackRegions[
                            voiceDrivenAssetPlaybackRegions.length - 1
                        ][1],
                        systemTriggered,
                        parentActionGroup,
                    );
                }
            }
        });

        let nextSceneGroup = sceneGroup;
        if (!rightScene && sceneGroup.index + 1 < luminary.sceneGroups.length) {
            nextSceneGroup = luminary.sceneGroups[sceneGroup.index + 1];
        }
        dispatch(updateFocusedSceneInfoOnDelete(sceneId, rightScene?.id, nextSceneGroup?.id));
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const deleteAllOverlaysFromSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new RemoveOverlayError('Scene Group missing');
        }

        const state = getState();
        const allOverlays = getOverlayScenesInSceneGroup(state, sceneGroup);
        if (allOverlays.length === 0) {
            // nothin' to clear if there are no overlays in the first place!
            return;
        }

        const baseSceneDesign = getBaseSceneDesignId(state, sceneGroup);
        if (!baseSceneDesign) throw new Error('Base scene design not found');

        const allOverlayDuration = allOverlays.reduce((acc, overlay) => acc + overlay.duration, 0);

        // create a new base scene
        const sceneWithBaseMediaAsset = getSceneWithBaseMediaAsset(sceneGroup);
        luminary.batchRun(() => {
            const newBaseScene = sceneGroup.addScene({ sceneDesignId: baseSceneDesign });
            standardizeTalkingHeadMediaAssetProperties(newBaseScene, sceneWithBaseMediaAsset);
            makeVoicedrivenCompatibleScene(newBaseScene);

            // remove all the overlays
            allOverlays.forEach((scene: Slide) => sceneGroup.removeScene(scene.id));
            newBaseScene.duration = allOverlayDuration;
        });

        call(
            defragSceneGroupOperation,
            sceneGroup.id,
            baseSceneDesign,
            systemTriggered,
            parentActionGroup,
        );
        call(
            checkpointVideoAndTranscriptOperation,
            sceneGroupId,
            {},
            systemTriggered,
            parentActionGroup,
        );
    },
);

export const $deleteAllScenesWithoutWordsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) {
            throw new Error('Base scene design id not found');
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        const _getContainsWordBetween = (
            nodeList: TranscriptWordNode[],
            startTime: number,
            endTime: number,
        ) => {
            for (const node of nodeList) {
                if (node.startTime >= startTime && node.endTime <= endTime) {
                    return true;
                }
                if (node.startTime > endTime) {
                    return false;
                }
            }
            return false;
        };

        const nonEmptyWordNodes = $dfs()
            .map(({ node }) => {
                return node;
            })
            .filter((node): node is TranscriptWordNode => {
                return $isTranscriptWordNode(node) && !$isTrimmedOut(node) && !node.isEmptyText();
            });

        luminary.batchRun(() => {
            for (let i = sceneGroup.scenes.length - 1; i >= 0; i--) {
                const scene = sceneGroup.scenes[i];
                const startTime = translateVideoTimeToTranscriptTime(
                    scene.startTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                    sceneGroup.startTime,
                );
                const endTime = translateVideoTimeToTranscriptTime(
                    scene.endTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                    sceneGroup.startTime,
                    { includeTrailingCut: false },
                );
                const containsWord = _getContainsWordBetween(nonEmptyWordNodes, startTime, endTime);

                if (containsWord) {
                    continue;
                }

                // Scene has no words, check if its an overlay or a base scene
                const isOverlay = scene.sceneDesign !== baseSceneDesignId;
                if (isOverlay) {
                    call(
                        deleteLuminaryOverlayOperation,
                        scene.id,
                        sceneGroupId,
                        systemTriggered,
                        parentActionGroup,
                    );
                    continue;
                }
                // In a base scene
                const leftScene =
                    scene.localIndex === 0 ? null : sceneGroup.scenes[scene.localIndex - 1];
                const rightScene =
                    scene.localIndex === sceneGroup.scenes.length - 1
                        ? null
                        : sceneGroup.scenes[scene.localIndex + 1];

                const leftSceneExists = !!leftScene;
                const rightSceneExists = !!rightScene;

                const leftSceneIsOverlay =
                    leftSceneExists && leftScene.sceneDesign !== baseSceneDesignId;
                const rightSceneIsOverlay =
                    rightSceneExists && rightScene.sceneDesign !== baseSceneDesignId;

                if (leftSceneExists && leftSceneIsOverlay) {
                    // Adjust the left overlay's end time to cover the base scene
                    const newEndTime = translateVideoTimeToTranscriptTime(
                        scene.endTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                        { includeTrailingCut: false },
                    );
                    call(
                        translateOverlayEndOperation,
                        leftScene.id,
                        sceneGroupId,
                        baseSceneDesignId,
                        newEndTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                } else if (rightSceneExists && rightSceneIsOverlay) {
                    // The base scene must be the first scene, adjust the right overlay's start time to cover the base scene
                    const newStartTime = translateVideoTimeToTranscriptTime(
                        scene.startTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                    call(
                        translateOverlayStartOperation,
                        rightScene.id,
                        sceneGroupId,
                        baseSceneDesignId,
                        newStartTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                }
            }
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Scenario:
 *   ------------------------------------------------------
 * | [here is the first overlay] | this will be a base scene |
 * 0             1.2             5                           10 (seconds)
 *   ------------------------------------------------------
 * imagine the word "first" has { startTime: 1100, endTime: 1300 } and "overlay" has { startTime: 1300, endTime: 1600 }
 * let's say we trim 1.2s from the beginning, resulting in:
 *   ------------------------------------------------------
 * | [overlay] | this will be a base scene |
 * 1.2              5                           10 (seconds)
 *   ------------------------------------------------------
 * we actually cut out "first" from the transcript because the startTime is outside of the trim's start time, see LuminarySyncPlugin.ts for more info.
 * The overlay and scene group now starts at 1.2s (actual startTime == 0) - for trimming we want users to be able to set EXACT start/end times
 *
 * However, imagine the user now wants to "un-trim" the overlay
 *   ------------------------------------------------------
 * | here is the first [overlay] | this will be a base scene |
 * 0                 1.2         5                           10 (seconds)
 *   ------------------------------------------------------
 * the user will eventually be selecting words and creating overlays from the transcript,
 * so we need to "fix" our previous trimmed start time according to the transcript. The LuminarySyncPlugin handles that, moving
 * the overlay boundary to no longer occur in the middle of a word shown on the transcript
 *
 * This example is for extending scene group in the start position, but the same is done for the end of the SceneGroup
 */
export const applySceneGroupTrimOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        newPlaybackRegions: [number, number][],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) throw new Error('Editor is not set');

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);

        type TimingMetadata = {
            templateId: string | null;
            sceneGroupId: string | null;
            mediaDuration: number | null;
            totalScenesInSceneGroup: number;
        };

        type TimingData = {
            timingMetadata: TimingMetadata;
            startTrimming: number;
            endTrimming: number;
            startRender: number;
            endRender: number;
            renderedIds: Set<string>;
        };
        type TrimAnalyticsPayload = TimingMetadata & {
            totalTime: number;
            trimTime: number;
            timeToRenderAllScenes: number;
            numberOfRenderedScenes: number;
            isTalkingHead: boolean;
            isVoiceover: boolean;
            isAIAvatar: boolean;
            isAIVoiceover: boolean;
        };

        function defaultTimingData(): TimingData {
            return {
                timingMetadata: {
                    templateId: null,
                    sceneGroupId: null,
                    mediaDuration: null,
                    totalScenesInSceneGroup: 0,
                },
                startTrimming: 0,
                endTrimming: 0,
                startRender: 0,
                endRender: 0,
                renderedIds: new Set<string>(),
            };
        }
        const td = defaultTimingData();
        const renderTimingCallback = (
            sceneId: string,
            subsceneIndex: number,
            canvasElement: HTMLCanvasElement,
        ) => {
            td.renderedIds.add(sceneId);

            if (luminary.isRenderQueueDoneProcessing()) {
                luminary.off('onRenderedSlideImage', renderTimingCallback);
                td.endRender = performance.now();
                const analyticsPayload: TrimAnalyticsPayload = {
                    ...td.timingMetadata,
                    totalTime: td.endRender - td.startTrimming,
                    trimTime: td.endTrimming - td.startTrimming,
                    timeToRenderAllScenes: td.endRender - td.startRender,
                    numberOfRenderedScenes: td.renderedIds.size,
                    isTalkingHead: isTalkingHeadSceneGroup(sceneGroup),
                    isVoiceover: isVoiceoverSceneGroup(sceneGroup),
                    isAIAvatar: isAIAvatarSceneGroup(sceneGroup),
                    isAIVoiceover: isAIVoiceoverSceneGroup(sceneGroup),
                };
                analytics.track('Voice Driven Media Resized', analyticsPayload);
            }
        };
        luminary.on('onRenderedSlideImage', renderTimingCallback);

        td.timingMetadata.templateId = luminary.template.id;
        td.timingMetadata.sceneGroupId = sceneGroupId;
        td.timingMetadata.totalScenesInSceneGroup = sceneGroup.scenes.length;
        td.timingMetadata.mediaDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        td.startTrimming = performance.now();

        luminary.batchRun(() => {
            // totally untrim the entire SceneGroup
            const cutRegions = getVoiceDrivenBaseAssetCutRegions(sceneGroup);
            for (const cutRegion of cutRegions) {
                call(
                    uncutVoicedrivenSceneGroupOperation,
                    sceneGroupId,
                    cutRegion,
                    systemTriggered,
                    parentActionGroup,
                );
            }
            const voiceDrivenAssetSourceDuration =
                getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

            // validate/correct the new playback regions so that:
            // 1. they all hold at least 1 word
            // 2. any overlay that starts or ends on a playback region boundary holds
            // at least 1 word
            const updatedPlaybackRegions = editor.getEditorState().read(() => {
                const playbackRegionsWithAtLeastOneWord: [number, number][] = [];
                for (const [start, end] of newPlaybackRegions) {
                    const nextWord = $getNextWordAtTime(start);
                    if (!nextWord) {
                        // there is no next word, we cannot include this as a playback region
                        continue;
                    }
                    const endTime = Math.max(end, nextWord.endTime);
                    playbackRegionsWithAtLeastOneWord.push([start, endTime]);
                }

                const playbackRegionsWithOverlaysWithAtLeastOneWord: [number, number][] = [];
                for (let [start, end] of defragPlaybackRegions(playbackRegionsWithAtLeastOneWord)) {
                    // now, the scenegroup has become totally uncut, so sceneGroupTime === transcriptTime
                    const leftScenes = sceneGroup.getActiveScenesAt(start);
                    if (leftScenes.length !== 1) {
                        throw new Error('No scene found at playback start time');
                    }
                    const leftScene = leftScenes[0];
                    if (isSceneAnOverlay(getState(), leftScene)) {
                        // if the left scene is an overlay, we need to ensure that it contains at least one word
                        const firstWord = $getNextWordAtTime(start);
                        if (!firstWord) {
                            throw new Error('No word found in playback region');
                        }
                        if (leftScene.endTime < firstWord.endTime) {
                            // the left scene ends before the first word ends. Move the start of the playback
                            // region to include the previous word
                            const previousWord = $getPreviousTranscriptWordNode(firstWord);
                            if (!previousWord) {
                                // there is no previous word, we cannot include this playback region
                                continue;
                            }
                            start = Math.min(previousWord.startTime, start);
                        }
                    }

                    let rightScenes = sceneGroup.getActiveScenesAt(end);
                    if (rightScenes.length !== 1 || rightScenes[0].startTime === end) {
                        // if sceneGroup.getActiveScenesAt(time) is called with a scene boundary time,
                        // it will return the scene that STARTS at that time. That could be the
                        // next scene, or [] if there is no next sceneGroup.
                        // In this case, we should use the last scene in the sceneGroup as the rightScene
                        rightScenes = [sceneGroup.scenes[sceneGroup.scenes.length - 1]];
                    }
                    const rightScene = rightScenes[0];
                    if (isSceneAnOverlay(getState(), rightScene)) {
                        // if the right scene is an overlay, we need to ensure that it contains at least one word
                        const lastWord = $getPreviousWordAtTime(end);
                        if (!lastWord) {
                            throw new Error('No word found in playback region');
                        }
                        if (rightScene.startTime > lastWord.startTime) {
                            // the right scene starts after the last word starts. Move the end of the playback
                            // region to include the next word
                            const nextWord = $getNextTranscriptWordNode(lastWord);
                            if (!nextWord) {
                                // there is no next word, we cannot include this playback region
                                continue;
                            }
                            end = Math.max(nextWord.endTime, end);
                        }
                    }

                    playbackRegionsWithOverlaysWithAtLeastOneWord.push([start, end]);
                }

                return defragPlaybackRegions(playbackRegionsWithOverlaysWithAtLeastOneWord);
            });

            // cut the scene group to the updatedPlaybackRegions
            const updatedCutRegions = VideoData.getCutRegions(
                updatedPlaybackRegions,
                voiceDrivenAssetSourceDuration,
            );
            for (const cutRegion of updatedCutRegions) {
                call(
                    cutVoicedrivenSceneGroupOperation,
                    sceneGroupId,
                    cutRegion[0],
                    cutRegion[1],
                    systemTriggered,
                    parentActionGroup,
                );
            }
        });

        const sceneId = luminary.getSceneGroupById(sceneGroupId)?.scenes[0]?.id;
        if (sceneId) {
            dispatch(
                updateTranscriptCurrentTimeAndPreview({
                    sceneGroupId,
                    sceneId: sceneId,
                    renderPreviewAtTime: 0,
                }),
            );
        }

        td.endTrimming = performance.now();
        td.startRender = performance.now();
    },
);

/**
 * Different operations in the transcript can create scenes. For example, creating an overlay
 * will obviously create a scene (or two, if you are splitting a base scene). Likewise, exposing a gap
 * in the transcript will create a scene with a base design.
 *
 * Whenever we create a scene, we want to make sure that the talking head MediaAsset (with linkToSceneGroup=true)
 * looks the same as in other scenes where the MediaAsset is visible.
 *
 * This utility function can be called anywhere that a scene is created on a talking head scene group, and it will
 * make sure that that is the case.
 *
 * @param scene the newly created scene
 * @param sourceScene a scene that we can read properties from (the base media asset should be present on this scene!)
 */
export function standardizeTalkingHeadMediaAssetProperties(
    targetScene: Slide,
    sourceScene: Slide | undefined,
    copyZoom: boolean = true,
): void {
    if (!sourceScene) {
        // there's no scene with the base media asset, so we can't do anything.
        // This could be true for talking head scene groups where the entire transcript is covered by a cutaway,
        // and it could be true for voiceover scene groups.
        return;
    }
    const targetTalkingHeadAssets = getLinkedMediaAssets(targetScene);
    const sourceTalkingHeadAssets = getLinkedMediaAssets(sourceScene);

    if (targetTalkingHeadAssets.length === 0 || sourceTalkingHeadAssets.length === 0) {
        return;
    }

    // there are lists of assets that are linked to the scene group media asset. We want to pair
    // them up so that, for example, if there are 2 assets in the source scene with specific crop settings,
    // then we can reproduce those settings on 2 assets in the target scene.
    type AssetStats = {
        asset: TimeAwareMediaAsset;
        aspectRatio: number;
        size: number;
    };

    const getStats = (asset: TimeAwareMediaAsset) => {
        return {
            asset,
            aspectRatio: asset.mediaTargetResolution.width / asset.mediaTargetResolution.height,
            size: asset.mediaTargetResolution.width * asset.mediaTargetResolution.height,
        };
    };

    const computeSimilarity = (asset1: AssetStats, asset2: AssetStats) => {
        const areaDiff = Math.abs(asset1.size - asset2.size) / Math.max(asset1.size, asset2.size);
        const aspectRatioDiff =
            Math.abs(asset1.aspectRatio - asset2.aspectRatio) /
            Math.max(asset1.aspectRatio, asset2.aspectRatio);
        const AREA_WEIGHT = 0.5;
        const ASPECT_RATIO_WEIGHT = 0.5;

        return AREA_WEIGHT * areaDiff + ASPECT_RATIO_WEIGHT * aspectRatioDiff;
    };
    const targetAssetStats = targetTalkingHeadAssets.map(getStats);
    const sourceAssetStats = sourceTalkingHeadAssets.map(getStats);

    // compare every target asset with every source asset and compute the similarity score for each pair
    const pairings: { targetAsset: AssetStats; sourceAsset: AssetStats }[] = [];
    const matchedTargetAssets = new Set<AssetStats>();

    for (const sourceAsset of sourceAssetStats) {
        let bestMatch: AssetStats | undefined;
        let bestScore = Infinity;
        for (const targetAsset of targetAssetStats) {
            if (matchedTargetAssets.has(targetAsset)) {
                continue;
            }
            const similarityScore = computeSimilarity(sourceAsset, targetAsset);
            if (similarityScore < bestScore) {
                bestScore = similarityScore;
                bestMatch = targetAsset;
            }
        }
        if (bestMatch) {
            pairings.push({ targetAsset: bestMatch, sourceAsset });
            matchedTargetAssets.add(bestMatch);
        }
    }

    // crop the target assets to the source assets
    for (const pairing of pairings) {
        adjustAssetCropByResolution(pairing.targetAsset.asset, pairing.sourceAsset.asset, copyZoom);
    }

    // note: brightness is set on the `sceneGroup.mediaAsset` directly, so no need to sync it here.
    // But if we have other things to sync, we can do so here.
}

export const srtDownloadOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState }, videoName: string) => {
        const srtText = await luminary.generateSRTText();
        const fileData = 'data:text/plain;charset=utf-8,' + encodeURIComponent(srtText);
        const fileName = videoName.replace(/\s+/g, '-').toLowerCase() + '-captions.srt';
        downloadStringAsFile(fileData, fileName);
    },
);

export const getTransitionedMediaErrorsOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }) => {
        const assets = getBackgroundMediaAssetSourceData(luminary);
        return ApiService.transitionedAssetErrorCreate({ body: { assets } });
    },
);

export const createNewVoicedrivenSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupCategory: SceneCategory,
        sceneGroupIndex: number,
        {
            deleteOtherScenes = false,
            setPlaceholderAsset = true,
            hasAvatar = false,
            isScreenRecording = false,
        },
        parentActionGroup = uuid(),
        systemTriggered = false,
    ): { sceneGroupId: string; sceneId: string; sceneDesign: string; hasIcon: boolean } => {
        let sceneDesignId: SceneDesignId | null;
        let sceneTypeCopy = '';
        if (sceneGroupCategory === VideoData.CATEGORY_VOICEOVER) {
            if (hasAvatar) {
                sceneTypeCopy = 'talking head';
                sceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(
                    getState(),
                    luminary.template.id,
                );
            } else {
                sceneTypeCopy = 'voiceover';
                sceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
                    getState(),
                    luminary.template.id,
                );
            }
        } else if (sceneGroupCategory === VideoData.CATEGORY_TALKING_HEAD) {
            sceneTypeCopy = 'talking head';
            sceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(
                getState(),
                luminary.template.id,
            );
        } else {
            throw new Error(`Invalid scene group category: ${sceneGroupCategory}`);
        }

        if (!sceneDesignId) {
            Toast.error(`This template does not support ${sceneTypeCopy} scenes`);
            throw new Error(`No default ${sceneTypeCopy} scene design found`);
        }

        const { sceneGroup, scene } = luminary.addSceneGroup({ sceneDesignId: sceneDesignId });
        makeVoicedrivenCompatibleScene(scene);
        const sceneGroupId = sceneGroup.id;

        luminary.moveSceneGroupToIndex(sceneGroupId, sceneGroupIndex);

        call(addVoiceDrivenAssetsToSceneGroupOperation, sceneGroupCategory, sceneGroupId, {
            setPlaceholderAsset,
            hasAvatar,
            isScreenRecording,
        });

        if (!setPlaceholderAsset) {
            call(applyGlobalCaptionStylesToSceneGroupOperation, sceneGroup);
        }

        // delete all other scenes if requested
        if (deleteOtherScenes) {
            // preserve any brand scenes (since we'd just re-create them)
            const sceneCategoriesToPreserve: SceneCategory[] = [VideoData.CATEGORY_BRAND];
            const sceneGroupIdsToKeep = [sceneGroupId];
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (sceneCategoriesToPreserve.includes(getSceneGroupCategory(sceneGroup))) {
                    sceneGroupIdsToKeep.push(sceneGroup.id);
                }
            });
            clearScenesExceptCategories({
                luminary,
                sceneGroupIdsToPreserve: sceneGroupIdsToKeep,
                preserveOutroScenes: true,
            });
        }

        return {
            sceneGroupId,
            sceneId: scene.id,
            sceneDesign: scene.sceneDesign,
            hasIcon: sceneContainsIcon(scene),
        };
    },
);

export const exportSceneAnnotationOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string | undefined,
        renderCaptions = false,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = sceneGroupId ? luminary.getSceneGroupById(sceneGroupId) : null;
        let textContent = '';
        const renderText = true;
        if (sceneGroup) {
            for (const scene of sceneGroup.scenes) {
                textContent += `${new SceneAnnotation({ scene }).getText({ renderText, renderCaptions })}\n`;
            }
        } else {
            for (const scene of luminary.scenes) {
                textContent += `${new SceneAnnotation({ scene }).getText({ renderText, renderCaptions })}\n`;
            }
        }
        return textContent;
    },
);

export const downloadLuminaryFrameAsImgOperation = createLuminaryOperation(
    async (_, timeMillis: number, videoName: string, videoData: OpaqueVideoData) => {
        let imgRendererLuminary: Luminary | undefined;
        try {
            imgRendererLuminary = new Luminary({
                mediaSizeToUse: VideoData.RENDER_MEDIA_SIZE,
                name: 'luminaryFrameDownloader',
                resolutionValue: 1080,
                fetchResources: {
                    audio: false,
                    // onBuffer makes it so that luminary will only fetch the media assets that are needed.
                    media: 'onBuffer',
                    transitions: true,
                    fonts: true,
                },
            });

            imgRendererLuminary.load(videoData);

            await imgRendererLuminary.seek(timeMillis, VideoData.SeekMode.ResolveWhenSingleFrame);

            imgRendererLuminary.render();

            if (!imgRendererLuminary.canvas) {
                throw new Error(
                    'Luminary.canvas is null -- this should only happen if Luminary is constructed with disableRenderer=true',
                );
            }
            const imageData = imgRendererLuminary.canvas.toDataURL('image/png');
            downloadStringAsFile(
                imageData,
                `${videoName} - ${getTimeStringFromSeconds(timeMillis / 1000)}.png`,
            );
        } catch (err) {
            Toast.error('Error downloading frame, please try again.');
        } finally {
            if (imgRendererLuminary) {
                await imgRendererLuminary.destroy();
            }
        }
    },
);

/**
 * The advanced media controls update luminary immediately because that gives the user immediate visual feedback on
 * their change. However, for each minute change we don't want to send analytics and checkpoint the video. Instead, by
 * debouncing, we can wait until the user has settled on a value.
 */
const debounceOneSecond = debounce((callback: () => void) => {
    callback();
}, 1000);

export const setSceneGroupAvatarBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !sceneGroup.avatar) {
            return;
        }
        sceneGroup.avatar.brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Avatar Brightness Updated', {
                isSceneGroupAvatar: true,
                isFromAdvancedControls,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setSceneGroupMediaBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !sceneGroup.mediaAsset) {
            return;
        }

        sceneGroup.mediaAsset.brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Media Asset Brightness Updated', {
                isSceneGroupMedia: true,
                isFromAdvancedControls,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setSceneMediaPlaybackRateOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        playbackRate: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const mediaAsset = scene.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
        if (!mediaAsset) throw new Error('Media asset not found in scene');
        mediaAsset.playbackRate = playbackRate;

        debounceOneSecond(() => {
            analytics.track('Media Asset Playback Rate Updated', {
                isSceneGroupMedia: false,
                isFromAdvancedControls,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setSceneMediaBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const mediaAsset = scene.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
        if (!mediaAsset) throw new Error('Media asset not found in scene');
        mediaAsset.brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Media Asset Brightness Updated', {
                isSceneGroupMedia: false,
                isFromAdvancedControls,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            dispatch(
                setBrightnessUserOverride({
                    sceneId: scene.id,
                    isAutomatic: false,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsContrastOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        contrast: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.contrast = contrast;

        debounceOneSecond(() => {
            analytics.track('Media Asset Contrast Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsHueOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        hue: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.hue = hue;

        debounceOneSecond(() => {
            analytics.track('Media Asset Hue Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsSaturationOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        saturation: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.saturation = saturation;

        debounceOneSecond(() => {
            analytics.track('Media Asset Saturation Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsLightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        lightness: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.lightness = lightness;

        debounceOneSecond(() => {
            analytics.track('Media Asset Lightness Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsBlurOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        blur: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.blur = blur;

        debounceOneSecond(() => {
            analytics.track('Media Asset Blur Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsVignettingOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        vignetting: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.vignetting = vignetting;
        // these options are hard coded for now and not exposed to the user
        mediaAsset.vignettingBlur = DEFAULT_VIGNETTING_BLUR;
        mediaAsset.vignettingAlpha = DEFAULT_VIGNETTING_ALPHA;

        debounceOneSecond(() => {
            analytics.track('Media Asset Vignetting Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsNoiseOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        noise: number,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        mediaAsset.noise = noise;
        // hard coded for now and not exposed to the user
        mediaAsset.noiseSize = DEFAULT_NOISE_SIZE;

        debounceOneSecond(() => {
            analytics.track('Media Asset Noise Updated', {
                isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const resetAdvancedMediaControlsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        _event: React.MouseEvent<HTMLElement>,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const state = getState();
        const sceneId = advancedMediaControlsSceneId(state);
        const assetId = advancedMediaControlsAssetId(state);
        const { mediaAsset, mediaContainer } = getAdvancedMediaControlsAsset(
            luminary,
            sceneId,
            assetId,
        );

        if (!mediaAsset) {
            return;
        }

        // colour correction
        mediaAsset.brightness = DEFAULT_BRIGHTNESS;
        mediaAsset.contrast = DEFAULT_CONTRAST;
        mediaAsset.hue = DEFAULT_HUE;
        mediaAsset.saturation = DEFAULT_SATURATION;
        mediaAsset.lightness = DEFAULT_LIGHTNESS;
        // effects
        mediaAsset.blur = DEFAULT_BLUR;
        mediaAsset.vignetting = DEFAULT_VIGNETTING;
        mediaAsset.vignettingBlur = DEFAULT_VIGNETTING_BLUR;
        mediaAsset.vignettingAlpha = DEFAULT_VIGNETTING_ALPHA;
        mediaAsset.noise = DEFAULT_NOISE;
        mediaAsset.noiseSize = DEFAULT_NOISE_SIZE;
        mediaAsset.enableGreyscale = DEFAULT_ENABLE_GREYSCALE;
        mediaAsset.playbackRate = DEFAULT_PLAYBACK_RATE;

        analytics.track('Advanced Media Controls Reset', {
            isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const updateVideoPosterUrlOperation = createLuminaryOperation(
    async (
        { luminary, dispatch },
        videoId: number,
        videoData: OpaqueVideoData,
        systemTriggered = true,
        parentActionGroup?: string,
    ) => {
        const firstScene = luminary.scenes[0];
        if (!firstScene) {
            return;
        }
        const timeMillis = firstScene.getPreviewTime();
        let imgRendererLuminary: Luminary | undefined;
        try {
            imgRendererLuminary = new Luminary({
                mediaSizeToUse: VideoData.THUMBNAIL_MEDIA_SIZE,
                name: 'luminaryFrameDownloader',
                resolutionValue: 215,
                fetchResources: {
                    audio: false,
                    // onBuffer makes it so that luminary will only fetch the media assets that are needed.
                    media: 'onBuffer',
                    transitions: false,
                    fonts: true,
                },
            });

            imgRendererLuminary.load(videoData);

            await imgRendererLuminary.seek(timeMillis, VideoData.SeekMode.ResolveWhenSingleFrame);

            imgRendererLuminary.render();

            const fileName = `${uuid()}.jpg`;
            const fileType = 'image/jpeg';
            const quality = 0.95;

            if (!imgRendererLuminary.canvas) {
                throw new Error(
                    'Luminary.canvas is null -- this should only happen if Luminary is constructed with disableRenderer=true',
                );
            }
            imgRendererLuminary.canvas.toBlob(
                (blob) => uploadVideoPosterBlob(blob, fileName, fileType, videoId),
                fileType,
                quality,
            );
        } catch (err) {
            ErrorReporter.capture(err);
        } finally {
            if (imgRendererLuminary) {
                await imgRendererLuminary.destroy();
            }
        }
    },
);

export const setLuminaryDebugVariablesOperation = createLuminaryOperation(
    ({ luminary, dispatch }, activeSceneId: string, activeAssetId?: string) => {
        const scene = luminary.getSceneById(activeSceneId);
        if (!scene) {
            return;
        }
        window.$s = window.$scene = scene;

        const asset = activeAssetId ? scene.getAssetById(activeAssetId) : undefined;
        if (asset) {
            window.$a = window.$asset = asset;
        }
    },
);

export enum NewOverlayPosition {
    Before = 'before',
    After = 'after',
}

/**
 * This function uses luminary and the lexical editor to find good start/end times for a new overlay.
 * If there is no space in the transcript for a new overlay, it will throw an error
 *
 * It will fit in the new overlay following some rules like:
 * - if one of the neighbouring scenes is a base scene, put the new overlay on top of that base scene
 * - if both scenes are overlays, favour putting the new overlay overtop of the left overlay. But if there's no space there, put it over the right overlay.
 * - if the neighbouring scenes are overlays, make sure that the transcript has at least two words in the new overlay. An overlay with
 *   a single word cannot be split.
 * @returns { start: number, end: number } - start and end times for where the new overlay should be placed, in video time
 */
export const getNewOverlayTimeBoundsOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }, sceneGroupId, sceneId, position) => {
        const TARGET_OVERLAY_DURATION = 3000;

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const scene = luminary.getSceneById(sceneId);
        if (!sceneGroup || !scene) {
            throw new Error('getNewOverlayTimeBounds missing scene or scene group');
        }

        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            throw new Error('getNewOverlayTimeBounds only works for voice-driven scene groups');
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('getNewOverlayTimeBounds missing editor');
        }

        const sceneIndex = scene.localIndex;

        function getSceneData(scene: Slide) {
            const sceneExists = !!scene;
            return {
                sceneExists,
                isOverlay: sceneExists && isSceneAnOverlay(getState(), scene),
                duration: sceneExists ? scene.duration : 0,
            };
        }
        const leftScene =
            position === NewOverlayPosition.After ? scene : sceneGroup.scenes[sceneIndex - 1];
        const leftSceneData = getSceneData(leftScene);

        const rightScene =
            position === NewOverlayPosition.After ? sceneGroup.scenes[sceneIndex + 1] : scene;
        const rightSceneData = getSceneData(rightScene);

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const translateToTranscriptTime = (time: number, { includeTrailingCut = true } = {}) => {
            return translateVideoTimeToTranscriptTime(
                time,
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenBaseAssetSourceDuration,
                sceneGroup.startTime,
                { includeTrailingCut },
            );
        };

        return editor.getEditorState().read(() => {
            let start, end;
            // first, check for base scenes on the left and right:
            if (leftSceneData.sceneExists && !leftSceneData.isOverlay) {
                // The left scene is a base scene.
                // We should take all the time from the left scene, no need to take any time from the right scene.
                const timeToTakeFromLeftScene = Math.min(
                    leftSceneData.duration, // since we are going on-top of a base scene, we could take all it's time
                    TARGET_OVERLAY_DURATION,
                );
                let targetStartTime = translateToTranscriptTime(
                    leftScene.endTime - timeToTakeFromLeftScene,
                );
                end = leftScene.endTime;

                const baseSceneWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(leftScene.startTime),
                    translateToTranscriptTime(leftScene.endTime, { includeTrailingCut: false }),
                );
                if (baseSceneWords.length < 1) {
                    // the base scene has no words in the transcript, so it's impossible to add an overlay here
                    throw new Error(
                        'getNewOverlayTimeBounds failed to add overlay: left base scene has no words',
                    );
                } else {
                    // the base scene has words in the transcript. Let's ensure that we are choosing at least one:
                    const lastWord = baseSceneWords[baseSceneWords.length - 1];
                    targetStartTime = Math.min(targetStartTime, lastWord.startTime);

                    // now find a good boundary:
                    start = $getFineOverlayBoundaryTimeCloseTo(
                        targetStartTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    start = translateTranscriptTimeToVideoTime(
                        start,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
            } else if (rightSceneData.sceneExists && !rightSceneData.isOverlay) {
                // The right scene is a base scene.
                // We should take all the time from the right scene, no need to take any time from the left scene.
                const timeToTakeFromRightScene = Math.min(
                    rightSceneData.duration, // since we are going on-top of a base scene, we could take all it's time
                    TARGET_OVERLAY_DURATION,
                );
                start = rightScene.startTime;
                let targetEndTime = translateToTranscriptTime(
                    rightScene.startTime + timeToTakeFromRightScene,
                    { includeTrailingCut: false },
                );

                const baseSceneWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(rightScene.startTime),
                    translateToTranscriptTime(rightScene.endTime, { includeTrailingCut: false }),
                );
                if (baseSceneWords.length < 1) {
                    // the base scene has no words in the transcript, so it's impossible to add an overlay here
                    throw new Error(
                        'getNewOverlayTimeBounds failed to add overlay: right base scene has no words',
                    );
                } else {
                    // the base scene has words in the transcript. Let's ensure that we are choosing at least one:
                    const firstWord = baseSceneWords[0];
                    targetEndTime = Math.max(targetEndTime, firstWord.endTime);

                    // now find a good boundary:
                    end = $getFineOverlayBoundaryTimeCloseTo(
                        targetEndTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    end = translateTranscriptTimeToVideoTime(
                        end,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
            }

            // next, check for missing scenes on the left and right:
            else if (!rightSceneData.sceneExists) {
                // the right scene does not exist, but the left scene is an overlay.
                // We should take all the time from the left scene.
                const timeToTakeFromLeftScene = Math.min(
                    leftSceneData.duration / 2, // since we are going to shift the left overlay, we need to leave it some time. Target half the duration.
                    TARGET_OVERLAY_DURATION,
                );
                const targetStartTime = translateToTranscriptTime(
                    leftScene.endTime - timeToTakeFromLeftScene,
                );
                end = leftScene.endTime;

                const overlayWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(leftScene.startTime),
                    translateToTranscriptTime(leftScene.endTime, { includeTrailingCut: false }),
                );
                if (overlayWords.length < 2) {
                    // the overlay has 0 or 1 words in the transcript. You can't split 0 or 1 words, so it's impossible
                    // to add an overlay here.
                    throw new Error(
                        'getNewOverlayTimeBounds failed to add overlay: left overlay cannot be split',
                    );
                } else {
                    start = $getFineOverlayBoundaryTimeCloseTo(
                        targetStartTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    if (start <= overlayWords[0].startTime) {
                        // the new overlay would start before the first word in the left overlay. That would leave 0 words
                        // inside the left overlay. We need to fix this by leaving at least one word in the left overlay.
                        start = (overlayWords[0].endTime + overlayWords[1].startTime) / 2;
                    }
                    const lastWord = overlayWords[overlayWords.length - 1];
                    const secondLastWord = overlayWords[overlayWords.length - 2];
                    if (start >= lastWord.endTime) {
                        // the new overlay would start after the last word in the left overlay. That would leave 0 words
                        // in the new overlay. We need to fix this by leaving at least one word in the new overlay.
                        start = (lastWord.startTime + secondLastWord.endTime) / 2;
                    }
                    start = translateTranscriptTimeToVideoTime(
                        start,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
            } else if (!leftSceneData.sceneExists) {
                // the left scene does not exist, but the right scene is an overlay.
                // We should take all the time from the right scene.
                const timeToTakeFromRightScene = Math.min(
                    rightSceneData.duration / 2, // since we are going to shift the right overlay, we need to leave it some time. Target half the duration.
                    TARGET_OVERLAY_DURATION,
                );
                start = rightScene.startTime;
                const targetEndTime = translateToTranscriptTime(
                    rightScene.startTime + timeToTakeFromRightScene,
                    { includeTrailingCut: false },
                );

                const overlayWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(rightScene.startTime),
                    translateToTranscriptTime(rightScene.endTime, { includeTrailingCut: false }),
                );
                if (overlayWords.length < 2) {
                    // the overlay has 0 or 1 words in the transcript. You can't split 0 or 1 words, so it's impossible
                    // to add an overlay here.
                    throw new Error(
                        'getNewOverlayTimeBounds failed to add overlay: right overlay cannot be split',
                    );
                } else {
                    end = $getFineOverlayBoundaryTimeCloseTo(
                        targetEndTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    const lastOverlayWord = overlayWords[overlayWords.length - 1];
                    const secondLastOverlayWord = overlayWords[overlayWords.length - 2];
                    if (end <= overlayWords[0].startTime) {
                        // the new overlay would end before the first word in the right overlay. That would leave 0 words
                        // in the new overlay. We need to fix this by leaving at least one word in the new overlay.
                        end = (overlayWords[0].endTime + overlayWords[1].startTime) / 2;
                    }
                    if (end >= lastOverlayWord.endTime) {
                        // the new overlay would end after the last word in the right overlay. That would leave 0 words
                        // in the right overlay. We need to fix this by leaving at least one word in the right overlay.
                        end = (lastOverlayWord.startTime + secondLastOverlayWord.endTime) / 2;
                    }
                    end = translateTranscriptTimeToVideoTime(
                        end,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
            }

            // finally, we know both scenes exist and are overlays:
            else {
                // Both the left and right scenes are overlays.
                const leftOverlayWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(leftScene.startTime),
                    translateToTranscriptTime(leftScene.endTime, { includeTrailingCut: false }),
                );
                const rightOverlayWords = $getWordNodesBetweenTimes(
                    translateToTranscriptTime(rightScene.startTime),
                    translateToTranscriptTime(rightScene.endTime, { includeTrailingCut: false }),
                );
                const takeFromLeftScene = leftOverlayWords.length >= 2;
                const takeFromRightScene = rightOverlayWords.length >= 2;
                if (!takeFromLeftScene && !takeFromRightScene) {
                    // there are not enough words in the transcript to split the left and right scene in half
                    throw new Error(
                        'getNewOverlayTimeBounds failed to add overlay: left and right overlay cannot be split',
                    );
                } else if (takeFromLeftScene) {
                    // first, we try to take 1/2 of the left scene, where the end time of the new scene is where the left scene used to end
                    // this way at least one end of the scene time won't change when adding a new overlay, which is what some users have complained about
                    end = leftScene.endTime;
                    const timeToTakeFromLeftScene = Math.min(
                        // if we take time from the left scene, the start time is roughly halfway of the left scene
                        leftSceneData.duration / 2,
                        // or the target overlay duration
                        TARGET_OVERLAY_DURATION,
                    );
                    const targetStartTime = translateToTranscriptTime(
                        leftScene.endTime - timeToTakeFromLeftScene,
                    );
                    // there is space on either the left overlay, or the right overlay, or both
                    start = $getFineOverlayBoundaryTimeCloseTo(
                        targetStartTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    const lastLeftOverlayWord = leftOverlayWords[leftOverlayWords.length - 1];
                    const secondLastLeftOverlayWord = leftOverlayWords[leftOverlayWords.length - 2];
                    // There are multiple words in the transcript during the left overlay.
                    if (start <= leftOverlayWords[0].startTime) {
                        // The place where we want the new overlay to start is going to overwrite the entire left overlay.
                        // We need to fix this by leaving at least one word in the left overlay.
                        // Set the start of the new overlay to be after the left overlay's first word.
                        start = (leftOverlayWords[0].endTime + leftOverlayWords[1].startTime) / 2;
                    } else if (start >= lastLeftOverlayWord.endTime) {
                        // the place where we want the new overlay to start is not going to touch the left overlay at all.
                        // take the last word from the left overlay and include it in the new overlay that we are creating.
                        // This guarantees that the new overlay will have at least one word in it.
                        start =
                            (secondLastLeftOverlayWord.endTime + lastLeftOverlayWord.startTime) / 2;
                    }
                    start = translateTranscriptTimeToVideoTime(
                        start,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
                // we couldn't split the left scene in half, so we try splitting the right scene in half
                else {
                    // when taking time from the right scene, we start where the right scene used to start, and end halfway of the right scene
                    start = rightScene.startTime;
                    const timeToTakeFromRightScene = Math.min(
                        rightSceneData.duration / 2,
                        TARGET_OVERLAY_DURATION,
                    );
                    const targetEndTime = translateToTranscriptTime(
                        rightScene.startTime + timeToTakeFromRightScene,
                        { includeTrailingCut: false },
                    );
                    // there is space on either the left overlay, or the right overlay, or both
                    end = $getFineOverlayBoundaryTimeCloseTo(
                        targetEndTime,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        { getIdealOverlayBoundaryTime: true },
                    );
                    const lastRightOverlayWord = rightOverlayWords[rightOverlayWords.length - 1];
                    const secondLastRightOverlayWord =
                        rightOverlayWords[rightOverlayWords.length - 2];
                    // There are multiple words in the transcript during the right overlay.
                    if (end <= rightOverlayWords[0].startTime) {
                        // the end of the new overlay is not going to touch the right overlay at all.
                        // take the first word from the right overlay and include it in the new overlay that we are creating.
                        // This guarantees that the new overlay will have at least one word in it.
                        end = (rightOverlayWords[0].endTime + rightOverlayWords[1].startTime) / 2;
                    } else if (end >= lastRightOverlayWord.endTime) {
                        // the place where we want the new overlay to end is going to overwrite the entire right overlay.
                        // We need to fix this by leaving at least one word in the right overlay.
                        // Set the end of the new overlay to be before the right overlay's last word.
                        end =
                            (secondLastRightOverlayWord.endTime + lastRightOverlayWord.startTime) /
                            2;
                    }
                    end = translateTranscriptTimeToVideoTime(
                        end,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenBaseAssetSourceDuration,
                        sceneGroup.startTime,
                    );
                }
            }

            return { start, end };
        });
    },
);

/**
 * Update all color properties for specific assetId for scenes in sceneGroup with color
 */
export const applySceneGroupAssetColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneGroupId: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        luminary.batchRun(() => {
            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
            if (!sceneGroup) {
                return;
            }

            sceneGroup.scenes.forEach((scene: Slide) => {
                if (isBackgroundColorUserOverridden(getState(), scene.id)) {
                    return;
                }

                const bgAssetProperties = getBackgroundColorAssetProperties(scene);
                if (!bgAssetProperties) {
                    return;
                }
                const asset = scene.getAssetById(bgAssetProperties.assetId);
                if (!asset) {
                    return;
                }

                setObjectPropertyValue(asset, bgAssetProperties.path, newColor);
            });
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * @deprecated - this function does not support multiple playback regions, and so
 * should not be used in any user-facing features. Currently, it is only used in
 * some staff-only, hacked-in features
 */
export const setSceneGroupVoiceDrivenSourceDataOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        voiceoverSourceData: AudioSourceData | null,
        mediaSourceData: MediaSourceData | null,
        avatarSourceData: MediaSourceData | null,
        volume: number,
        playbackRegions: number[][],
    ) => {
        if (playbackRegions.length !== 1) {
            throw new Error(
                'setSceneGroupVoiceDrivenSourceDataOperation only supports a single playback region',
            );
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        if (!voiceoverSourceData && !mediaSourceData) {
            return;
        }
        luminary.batchRun(() => {
            // load the voiceoverAsset into luminary
            if (voiceoverSourceData) {
                sceneGroup.voiceoverAsset.sourceData = voiceoverSourceData;
                sceneGroup.voiceoverAsset.volume = volume;
                sceneGroup.voiceoverAsset.startTime = playbackRegions[0][0];
                if (playbackRegions[0][1]) {
                    sceneGroup.voiceoverAsset.endTime = playbackRegions[0][1];
                }
                if (avatarSourceData) {
                    sceneGroup.avatar.addForeground();
                    if (sceneGroup.avatar.foreground) {
                        sceneGroup.avatar.foreground.sourceData = avatarSourceData;
                    }
                    sceneGroup.avatar.addBackground();
                    if (sceneGroup.avatar.background) {
                        sceneGroup.avatar.background.sourceData =
                            AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA;
                    }
                }
            }
            // load the mediaAsset into luminary
            else if (mediaSourceData) {
                sceneGroup.mediaAsset.sourceData = mediaSourceData;
                sceneGroup.mediaAsset.volume = volume;
                sceneGroup.mediaAsset.startTime = playbackRegions[0][0];
                if (playbackRegions[0][1]) {
                    sceneGroup.mediaAsset.endTime = playbackRegions[0][1];
                }
            }
        });
        await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
    },
);

export const cutVoicedrivenSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        cutStartTime: number, // in transcript time
        cutEndTime: number, // in transcript time
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const voicedrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voicedrivenAsset) {
            throw new Error('You cannot cut a scenegroup that does not have a voicedriven asset');
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const cutStartVideoTime = translateTranscriptTimeToVideoTime(
            cutStartTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const cutEndVideoTime = translateTranscriptTimeToVideoTime(
            cutEndTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );

        if (
            lessThanOrEqualWithinEpsilon(cutStartVideoTime, sceneGroup.startTime) &&
            greaterThanOrEqualWithinEpsilon(cutEndVideoTime, sceneGroup.endTime)
        ) {
            // then entire transcript was cut. We need to just delete the whole scenegroup
            // remember - there needs to be at least 1 scene group in the video, so this call may end up doing
            // nothing if this scenegroup is the only one.
            call(
                SharedLeftControlHandlers.handleDeleteSceneGroup,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
            return;
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) {
            throw new Error('Template does not have a base scene design, cannot cut!');
        }

        luminary.batchRun(() => {
            const interestPoints = call(
                calculateInterestPointTimesForEntireSceneGroupOperation,
                sceneGroupId,
            );
            /*
             * Assume this is a media asset, with a cut happening in the middle:
             *
             *         cutStartTime           cutEndTime
             *    [--------|----------------------|--------]  Media Asset being cut
             *
             * There can be different places where scenes can exist, relative to this cut:
             *
             *      [--] -> scene is before cut -> CASE A
             *          [-------] -> scene is overlapping part of the start of the cut -> CASE B
             *                      [------] -> scene is entirely within the cut -> CASE C
             *                               [---------] -> scene is overlapping part of the end of the cut -> CASE D
             *                                         [--] -> scene is after the cut -> CASE E
             *         [--------------------------------] -> cut is entirely within the scene -> CASE F
             */
            for (let i = sceneGroup.scenes.length - 1; i >= 0; i--) {
                // we are iterating the scenes backwards, so that edits in the loop don't affect other loop runs
                const scene = sceneGroup.scenes[i];
                if (
                    lessThanOrEqualWithinEpsilon(scene.endTime, cutStartVideoTime) ||
                    greaterThanOrEqualWithinEpsilon(scene.startTime, cutEndVideoTime)
                ) {
                    // scene is either before or after the cut
                    // CASE A or CASE E above
                    // no need to do anything
                } else if (
                    greaterThanOrEqualWithinEpsilon(scene.startTime, cutStartVideoTime) &&
                    lessThanOrEqualWithinEpsilon(scene.endTime, cutEndVideoTime)
                ) {
                    // scene is entirely within the cut (or the two overlap perfectly)
                    // CASE C above
                    sceneGroup.removeScene(scene.id);
                } else if (scene.startTime < cutStartVideoTime && scene.endTime > cutEndVideoTime) {
                    // cut is entirely with the scene
                    // CASE F above
                    scene.duration = scene.duration - (cutEndVideoTime - cutStartVideoTime);
                } else if (scene.startTime < cutStartVideoTime) {
                    // cut starts in the middle of the scene
                    // CASE B above
                    scene.duration = scene.duration - (scene.endTime - cutStartVideoTime);
                } else if (scene.endTime > cutEndVideoTime) {
                    // cut ends in the middle of the scene
                    // CASE D above
                    scene.duration = scene.duration - (cutEndVideoTime - scene.startTime);
                } else {
                    throw new Error('this is an impossible case.');
                }
            }

            call(
                defragSceneGroupOperation,
                sceneGroupId,
                baseSceneDesignId,
                systemTriggered,
                parentActionGroup,
            );
            // write to the voicedrivenAsset playback regions
            voicedrivenAsset.cut(cutStartTime, cutEndTime);

            call(
                reapplyInterestPointsOperation,
                interestPoints,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const uncutVoicedrivenSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, getState, call },
        sceneGroupId: string,
        [uncutStart, uncutEnd]: [number, number],
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const voicedrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voicedrivenAsset) {
            throw new Error('You cannot uncut a scenegroup that does not have a voicedriven asset');
        }

        const baseSceneDesign = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesign) {
            throw new Error('Template does not have a base scene design, cannot uncut!');
        }

        const isVoiceover = isVoiceoverSceneGroup(sceneGroup);

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            uncutStart,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
        );
        const leftSceneOptions = sceneGroup.getActiveScenesAt(Math.max(sceneGroupTime - 0.1, 0));
        const leftScene = leftSceneOptions.length === 1 ? leftSceneOptions[0] : null;
        const rightSceneOptions = sceneGroup.getActiveScenesAt(
            Math.min(sceneGroupTime + 0.1, sceneGroup.duration),
        );
        const rightScene = rightSceneOptions.length === 1 ? rightSceneOptions[0] : null;

        let sceneToExtend: Slide | null = null;
        let createNewBaseScene = false;
        const leftIsOverlay = !!leftScene && isSceneAnOverlay(getState(), leftScene);
        const rightIsOverlay = !!rightScene && isSceneAnOverlay(getState(), rightScene);

        // For Voiceover, we prefer extending overlays to keep the transcript covered.
        // For Talking Head, we prefer extending a base scene (or, we can create a new base scene)
        if (isVoiceover) {
            sceneToExtend = leftIsOverlay ? leftScene : rightScene;
        } else {
            if (leftScene && !leftIsOverlay) {
                sceneToExtend = leftScene;
            } else if (rightScene && !rightIsOverlay) {
                sceneToExtend = rightScene;
            } else {
                createNewBaseScene = true;
                sceneToExtend = leftScene || rightScene;
            }
        }

        if (!sceneToExtend) {
            throw new Error('Could not find a scene to extend for uncut operation.');
        }

        luminary.batchRun(() => {
            voicedrivenAsset.uncut(uncutStart, uncutEnd);
            sceneToExtend.duration += uncutEnd - uncutStart;
            if (createNewBaseScene) {
                call(
                    createOverlayOperation,
                    sceneGroupId,
                    uncutStart,
                    uncutEnd,
                    baseSceneDesign,
                    systemTriggered,
                    parentActionGroup,
                );
            }
        });
    },
);

export const setAssetPositionTransformationWithinBoundsOperation = createLuminaryOperation(
    (
        { luminary },
        { sceneId, assetId, x, y }: { sceneId: string; assetId: string; x: number; y: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setAssetPositionTransformationWithinBoundsOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('setAssetPositionTransformationWithinBoundsOperation: asset not found');
        }
        asset.setTransformationPositionWithinSceneBounds(x, y);
    },
);

export const setAssetPositionTransformationOperation = createLuminaryOperation(
    (
        { luminary },
        {
            sceneId,
            sceneGroupId,
            assetId,
            x,
            y,
        }: { sceneId?: string; sceneGroupId?: string; assetId: string; x: number; y: number },
    ) => {
        let sceneOrGroup = null;

        if (sceneGroupId) {
            sceneOrGroup = luminary.getSceneGroupById(sceneGroupId);
        } else if (sceneId) {
            sceneOrGroup = luminary.getSceneById(sceneId);
        }

        if (!sceneOrGroup) {
            throw new Error(
                `Could not find ${sceneId ? 'scene' : 'scene group'} with id ${sceneId || sceneGroupId}`,
            );
        }

        const asset = sceneGroupId
            ? sceneOrGroup.getAssetById(assetId, { deep: true })
            : sceneOrGroup.getAssetById(assetId);

        if (!asset) {
            throw new Error('setAssetPositionTransformationOperation: asset not found');
        }

        asset.transformation.position.x = x;
        asset.transformation.position.y = y;
    },
);

export const setHasUserChangedAssetPositionOperation = createLuminaryOperation(
    (
        { luminary },
        { sceneGroupId, assetId, value }: { sceneGroupId: string; assetId: string; value: boolean },
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const asset = sceneGroup.getAssetById(assetId, { deep: true });
        if (!asset) {
            return;
        }
        if (VideoData.isAnyTextAsset(asset)) {
            asset.hasUserChangedPosition = value;
        }
    },
);

/**
 * This operation can by used to set the maxWidth of an asset. It will also set the maxHeight in a way that preserves
 * the aspect ratio of the asset (prevent squishing or stretching).
 *
 * So far, this operation is only used by IconAssets (which have the maxWidth/maxHeight properties). We can probably
 * also use this for other types of assets, but those have not been tried yet.
 */
export const setAssetDimensionsProportionallyOperation = createLuminaryOperation(
    (
        { luminary },
        { sceneId, assetId, maxWidth }: { sceneId: string; assetId: string; maxWidth: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setHasUserChangedAssetPositionOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('setHasUserChangedAssetPositionOperation: asset not found');
        }

        if (
            !VideoData.isIconAsset(asset) &&
            !(VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset))
        ) {
            throw new Error(
                `asset type ${asset.classType} not supported by setAssetDimensionsProportionallyOperation`,
            );
        }

        luminary.batchRun(() => {
            const assetAspectRatio = asset.maxWidth / asset.maxHeight;

            const newWidth = maxWidth;
            const newHeight = maxWidth / assetAspectRatio;

            asset.maxWidth = newWidth;
            asset.maxHeight = newHeight;
        });
    },
);

export const setGlobalColorGradingFilterOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        textureUrl: string | null,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): void => {
        let filter;
        if (!textureUrl) {
            // If textureUrl is null, remove the color grading
            if (luminary.template.grading) {
                luminary.template.removeGrading();
            }
        } else {
            let isGradingNewlyAdded = false;
            if (!luminary.template.grading) {
                luminary.template.addGrading();
                isGradingNewlyAdded = true;
            }
            if (!luminary.template.grading) {
                throw new Error('Failed to add color grading property');
            }
            filter = getColorGradingFilterFromUrl(getState(), textureUrl);
            if (!filter) {
                throw new Error(`Color grading filter with url ${textureUrl} not found`);
            }
            luminary.template.grading.source = filter.texture_url;
            if (isGradingNewlyAdded) {
                luminary.template.grading.intensity = 1; // Set default intensity
            }
        }
        analytics.track('global_color_grading_filter_set', {
            filter: filter?.name || 'No filter',
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setGlobalColorGradingFilterIntensityOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        intensity: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): void => {
        if (!luminary.template.grading) {
            throw new Error('Cannot set color grading intensity: Color grading property not found');
        }
        luminary.template.grading.intensity = intensity;
        debounceOneSecond(() => {
            analytics.track('global_color_grading_filter_intensity_set', {
                value: intensity,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setColorGradingFilterOverrideOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        textureUrl: string | null,
        assetId: string,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): void => {
        const { mediaAsset: asset } = getAdvancedMediaControlsAsset(luminary, sceneId, assetId);
        if (!asset) {
            throw new Error('setColorGradingFilterOverrideOperation: asset not found');
        }
        if (!VideoData.isMediaAsset(asset)) {
            throw new Error('setColorGradingFilterOverrideOperation: asset is not a media asset');
        }
        let isGradingNewlyAdded = false;
        if (!asset.gradingOverride) {
            asset.addGradingOverride();
            isGradingNewlyAdded = true;
        }
        if (!asset.gradingOverride) {
            throw new Error('Failed to add color grading override property for asset');
        }
        if (asset.gradingOverride.source === '') {
            isGradingNewlyAdded = true; // If the override was previously set to the default (empty) filter, treat it as a new addition
        }

        const setGreyscale = (asset: TimeAwareMediaAsset, enable: boolean): void => {
            const updateAnalytics = () => {
                analytics.track('Media Asset Greyscale Updated', {
                    isSceneGroupMedia: asset.linkToSceneGroup,
                });
            };
            if (enable && !asset.enableGreyscale) {
                asset.enableGreyscale = true;
                updateAnalytics();
            } else if (!enable && asset.enableGreyscale) {
                asset.enableGreyscale = false;
                updateAnalytics();
            }
        };

        let filter;
        if (!textureUrl) {
            // Since this is still an override setting, we want to remove the color grading filter without reverting to the global filter setting
            // We do this by making the filter override to be a transparent filter rather than calling removeGradingOverride which would cause the asset to revert back to the global filter
            asset.gradingOverride.source = '';
            asset.gradingOverride.intensity = 0; // Reset intensity
            setGreyscale(asset, false);
        } else if (textureUrl === MONO_FILTER_TEXTURE_URL) {
            asset.gradingOverride.source = '';
            asset.gradingOverride.intensity = 0;
            setGreyscale(asset, true);
        } else {
            filter = getColorGradingFilterFromUrl(getState(), textureUrl);
            if (!filter) {
                throw new Error(`Color grading filter with url ${textureUrl} not found`);
            }
            asset.gradingOverride.source = filter.texture_url;
            if (isGradingNewlyAdded) {
                asset.gradingOverride.intensity = 1; // Set default intensity
            }
            setGreyscale(asset, false);
        }
        analytics.track('color_grading_filter_override_set', {
            filter: filter?.name || 'No filter',
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setColorGradingFilterOverrideIntensityOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        intensity: number,
        assetId: string,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): void => {
        const { mediaAsset: asset } = getAdvancedMediaControlsAsset(luminary, sceneId, assetId);
        if (!asset) {
            throw new Error('setColorGradingFilterOverrideIntensityOperation: asset not found');
        }
        if (!VideoData.isMediaAsset(asset)) {
            throw new Error('Cannot apply color grading override: Asset is not a MediaAsset');
        }
        let isGradingNewlyAdded = false;
        if (!asset.gradingOverride) {
            // User wants to only set the intensity so we create an override that extends the global grading and then set the intensity
            asset.addGradingOverride();
            isGradingNewlyAdded = true;
        }
        if (!asset.gradingOverride) {
            throw new Error('Failed to add color grading override property for asset');
        }
        if (isGradingNewlyAdded) {
            if (!luminary.template.grading) {
                throw new Error(
                    'Cannot set color grading override intensity: Global color grading property not found',
                );
            }
            asset.gradingOverride.source = luminary.template.grading.source;
        }
        asset.gradingOverride.intensity = intensity;
        debounceOneSecond(() => {
            analytics.track('color_grading_filter_intensity_override_set', {
                value: intensity,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const resizeLeftAssetOperation = createLuminaryOperation(
    (
        { luminary, call },
        { sceneId, assetId, width }: { sceneId: string; assetId: string; width: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeLeftAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeLeftAssetOperation: asset not found');
        }

        if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
            const diff = asset.maxWidth - width;
            asset.maxWidth = width;

            if (asset.textAlign === TextAlignment.LEFT) {
                asset.transformation.position.x += diff;
            } else if (asset.textAlign === TextAlignment.CENTER) {
                asset.transformation.position.x += diff / 2;
            }

            if (VideoData.isTextAsset(asset)) {
                if (!asset.canFitText(asset.text)) {
                    asset.setFontSizeToFitAllText();
                }
            }
        } else if (VideoData.isAssetGroup(asset)) {
            const diff = asset.approximateMaxBounds.width - width;

            const originalMaxWidths = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .map((childAsset) => childAsset.maxWidth);

            asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                childAsset.maxWidth = width;
            });

            if (asset.approximateMaxBounds.width > width) {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset, i) => {
                    childAsset.maxWidth = originalMaxWidths[i];
                });

                return;
            }

            if (asset.alignChildren === TextAlignment.LEFT) {
                call(setAssetPositionTransformationOperation, {
                    sceneId,
                    assetId: asset.id,
                    x: asset.transformation.position.x + diff,
                    y: asset.transformation.position.y,
                });
            } else if (asset.alignChildren === TextAlignment.CENTER) {
                call(setAssetPositionTransformationOperation, {
                    sceneId,
                    assetId: asset.id,
                    x: asset.transformation.position.x + diff / 2,
                    y: asset.transformation.position.y,
                });
            }
        }
    },
);

export const resizeRightAssetOperation = createLuminaryOperation(
    (
        { luminary, call },
        { sceneId, assetId, width }: { sceneId: string; assetId: string; width: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeRightAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeRightAssetOperation: asset not found');
        }

        if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
            const diff = asset.maxWidth - width;
            asset.maxWidth = width;

            if (asset.textAlign === TextAlignment.RIGHT) {
                asset.transformation.position.x -= diff;
            } else if (asset.textAlign === TextAlignment.CENTER) {
                asset.transformation.position.x -= diff / 2;
            }

            if (VideoData.isTextAsset(asset)) {
                if (!asset.canFitText(asset.text)) {
                    asset.setFontSizeToFitAllText();
                }
            }

            if (asset.transformation.position.y < 0) {
                asset.transformation.position.y = 0;
            }
        }
        if (VideoData.isAssetGroup(asset)) {
            const diff = asset.approximateMaxBounds.width - width;

            const originalMaxWidths = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .map((childAsset) => childAsset.maxWidth);

            asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                childAsset.maxWidth = width;
            });

            if (asset.approximateMaxBounds.width > width) {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset, i) => {
                    childAsset.maxWidth = originalMaxWidths[i];
                });

                return;
            }

            if (asset.alignChildren === TextAlignment.RIGHT) {
                call(setAssetPositionTransformationOperation, {
                    sceneId,
                    assetId: asset.id,
                    x: asset.transformation.position.x - diff,
                    y: asset.transformation.position.y,
                });
            } else if (asset.alignChildren === TextAlignment.CENTER) {
                call(setAssetPositionTransformationOperation, {
                    sceneId,
                    assetId: asset.id,
                    x: asset.transformation.position.x - diff / 2,
                    y: asset.transformation.position.y,
                });
            }
        }
    },
);

export const resizeAssetInitOperation = createLuminaryOperation(
    ({ luminary }, { sceneId, assetId }: { sceneId: string; assetId: string }) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeAssetInitOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeAssetInitOperation: asset not found');
        }

        const sceneHeight = luminary.referenceResolution.height;

        if (VideoData.isTextAsset(asset)) {
            asset.maxWidth = asset.getNTEContainerBounds().width;
            asset.hasUserChangedMaxWidth = true;
            updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.textAlign);
            asset.maxHeight = sceneHeight;
        } else if (VideoData.isFancyCaptionAsset(asset)) {
            asset.maxWidth = asset.width;
            asset.hasUserChangedMaxWidth = true;
            updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.textAlign);
        } else if (VideoData.isAssetGroup(asset)) {
            asset.hasUserChangedMaxWidth = true;

            const maxChildRealWidth = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .reduce((maxWidth, childAsset) => {
                    return childAsset.hasText
                        ? Math.max(maxWidth, childAsset.getNTEContainerBounds().width)
                        : maxWidth;
                }, 0);

            updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.alignChildren);

            luminary.batchRun(() => {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                    childAsset.maxWidth = maxChildRealWidth;
                    childAsset.maxHeight = sceneHeight;
                });
            });
        }
    },
);

export const applySplitAndStackOnSceneGroupOperation = createLuminaryOperation(
    (
        { call, luminary, dispatch, getState },
        sceneGroupId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        if (!isTalkingHeadSceneGroup(sceneGroup)) {
            return;
        }
        return sceneGroup.scenes
            .map((scene) => scene.id)
            .map(async (sceneId) => {
                const faceDetections = await call(getFaceDetectionsOperation, sceneId);

                const scene = luminary.getSceneById(sceneId);
                if (!scene) {
                    // scene was deleted or something
                    return;
                }

                const sceneGroup = scene.sceneGroup;
                const voicedrivenAsset = sceneGroup.mediaAsset;
                if (!voicedrivenAsset) {
                    return;
                }

                if (faceDetections.length === 0) {
                    // no faces, just leave the scene as is
                    return;
                }

                if (faceDetections.length === 1) {
                    // Single face detected - just crop to the face
                    const linkedMediaAssets = scene
                        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                        .filter((asset) => {
                            return asset.linkToSceneGroup;
                        });
                    if (linkedMediaAssets.length === 0) {
                        return;
                    }

                    const faceDetection = faceDetections[0];
                    const asset = linkedMediaAssets[0];

                    luminary.batchRun(() => {
                        cropToBoundingBox(faceDetection, asset, voicedrivenAsset.sourceData, 0.3);
                    });

                    dispatch(
                        setCropUserOverride({
                            sceneId: scene.id,
                            isAutomatic: false,
                            systemTriggered,
                            parentActionGroup,
                        }),
                    );
                } else {
                    // Multiple faces detected - use multiple speakers scene design
                    const availableSceneDesignIds = getTemplateSceneIds(
                        getState(),
                        luminary.template.id,
                    );
                    const recommendedSceneDesignIds = call(
                        getMultipleSpeakersSceneDesignIdsOperation,
                        availableSceneDesignIds,
                        faceDetections,
                    );
                    if (recommendedSceneDesignIds.size === 0) {
                        return;
                    }

                    const newSceneId = luminary.batchRun(() => {
                        const newScene = sceneGroup.addScene({
                            sceneDesignId: recommendedSceneDesignIds.get(0),
                        });

                        makeVoicedrivenCompatibleScene(newScene);
                        newScene.duration = scene.duration;

                        call(setUpMultipleSpeakersSceneOperation, newScene.id, faceDetections);

                        sceneGroup.removeScene(scene.id);
                        return newScene.id;
                    });
                    dispatch(
                        setCropUserOverride({
                            sceneId: newSceneId,
                            isAutomatic: false,
                            systemTriggered,
                            parentActionGroup,
                        }),
                    );
                }
            });
    },
);

export const setUpMultipleSpeakersOnAllScenesOperation = createLuminaryOperation(
    (
        { luminary, call, getState, dispatch },
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        return luminary.sceneGroups.map((sceneGroup) => {
            call(
                applySplitAndStackOnSceneGroupOperation,
                sceneGroup.id,
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

export const loadMultiVideosOperation = createLuminaryOperation(
    async ({ dispatch }, { videoIds }: { videoIds: number[] }) => {
        const videoPromises = videoIds.map(async (videoId: number) => {
            try {
                const response = await ApiService.videoRetrieve({
                    path: { video_id: videoId.toString() },
                    query: {
                        require_editable_version: true,
                    },
                });
                return response.data;
            } catch (error) {
                ErrorReporter.capture(error);
                return null;
            }
        });

        const videoResponses = await Promise.all(videoPromises);
        const validVideos = videoResponses.filter(isNotNull);

        dispatch(setMultiVideos({ videos: validVideos }));

        return {
            videos: validVideos,
            error: validVideos.length === 0 ? 'No valid videos found to edit.' : null,
        };
    },
);

/**
 * Applies template settings from a blueprint's video_data to the current video.
 * This includes the color palette and any other template-level settings.
 */
export const applyBlueprintTemplateSettingsOperation = createLuminaryOperation(
    async ({ luminary, getState }, blueprintId: number) => {
        const blueprint = getBlueprint(getState(), blueprintId);
        if (!blueprint) {
            return;
        }

        let blueprintVideoData: OpaqueVideoData;

        // Fetch video_data from the API
        try {
            const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
                path: {
                    id: blueprintId,
                },
                query: {
                    template: luminary.template.id,
                },
            });
            blueprintVideoData = JSON.parse(response.data.video_data);
        } catch (error) {
            ErrorReporter.capture(error);
            return;
        }

        // Create a data luminary instance to extract template settings
        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });
        dataLuminary.load(blueprintVideoData);
        const templateData = dataLuminary.template.save();
        await dataLuminary.destroy();

        // Apply the template settings from the blueprint to the current video
        luminary.template.load(templateData);
    },
);

export const setMediaColorGraphicColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        colorPropertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return;
        const asset = scene.getAssetById(assetId);
        if (!asset || !VideoData.isMediaAsset(asset) || !VideoData.isStickerAsset(asset)) return;
        const colorGraphic = asset.colorGraphics[0];
        if (!colorGraphic) return;

        setObjectPropertyValue(colorGraphic, colorPropertyName, newColor);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
